<script lang="ts">
  /**
   * Flight planning (§8.4): a full-bleed "Google Maps"-style map that always shows the SHGM drone
   * zones + NOTAMs in view (fetched per viewport, refreshed periodically), with hover tooltips and
   * click popups. Click empty ground → a go/no-go verdict for that launch point (SHGM zones + NOTAM +
   * live weather). Advisory only — SHGM is the legal authority (deep-linked). Logic is the shared,
   * fixture-verified engine (lib/planning/engine.ts).
   */
  import { onMount, onDestroy, tick } from 'svelte';
  import { MapLibreEngine } from '../lib/planning/map/maplibreEngine.ts';
  /**
   * Which FEATURES this deployment has, as opposed to `caps`, which is what the RENDERER can do.
   * Both feed the same dead-control rule — a control whose backing data cannot exist here is not
   * dimmed, it is absent — but they answer different questions, so they stay separate:
   *
   *   caps      "this browser fell back to Leaflet, so there is no 3D"     — runtime
   *   features  "this deployment has no local archive and no weather"      — build
   *
   * Defaults are all true, so drone-player renders exactly as before and this prop is invisible
   * there. The public site turns off what it genuinely does not have; leaving the controls in
   * place would give a visitor a Media-filters card that narrows an empty set, a My-flights
   * checkbox that toggles nothing, and a verdict card that cannot reach a weather API.
   */
  interface Features {
    /** The local flight/photo archive: layers, media filters, the media table, search scopes. */
    archive: boolean;
    /** Live weather: the wind grid, and the weather half of the launch verdict. */
    weather: boolean;
    /** The click-a-point launch assessment card. Depends on weather for the wind verdict. */
    assessment: boolean;
    /**
     * There is a HOST application around this map with its own settings pane and source switcher.
     * The ⚙ and ✕ buttons hand off to it (`app.floatSourceAt` / `app.hideSource`), so where the map
     * IS the whole application they lead nowhere. With this off, ⚙ is replaced by the one setting
     * that genuinely belongs to the map — the interface language.
     */
    host: boolean;
  }
  /**
   * Reported whenever the dataset's freshness changes, for a HOST that frames this map with its own
   * chrome. The public site's advisory bar is outside this component and above the fold, which is
   * where a visitor on a phone actually looks — and it had no way to learn any of this, so its
   * freshness stamp was dead markup and the only real one lived in a panel that starts collapsed
   * on a phone. Age is a safety fact on this map; it cannot live only where the filters live.
   */
  let { features = {}, onDataStatus }: { features?: Partial<Features>; onDataStatus?: (s: PlanningDataStatus) => void } = $props();
  const feat: Features = $derived({ archive: true, weather: true, assessment: true, host: true, ...features });

  // Pushed out on every change, so a host renders the same facts this component's own footer does.
  $effect(() => {
    onDataStatus?.({
      zonesAt,
      zonesAgeLabel: zonesAt == null ? null : ageLabel(zonesAt),
      zonesOk,
      notamsOk,
      liveNotams,
      notamsWithheld,
      loading: indexing || (!updatedAt && !dataError),
      failed: !!dataError && !updatedAt,
    });
  });
  import type { MapInteractionHost, PlanningMapEngine, MapCapabilities, FlightPath } from '../lib/planning/map/engine.ts';
  import 'maplibre-gl/dist/maplibre-gl.css';
  import { app } from '../lib/state.svelte.ts';
  import { t, i18n } from '../lib/i18n.svelte.ts';
  import { fetchWeather } from '../lib/planning/fetch.ts';
  import { LutEngine, LUTS, detectColorMode } from '../lib/lut.ts';
  import { readCache, revalidate, refreshDataset, isStale, zonesFresh, readMerge, writeMerge, drawableNotams, type CachedDataset } from '../lib/planning/zoneStore.ts';
  import { assessLaunch, isActive, isStanding, STANDING_END_MS, nextNotamChange, RingGrid, type Zone, type Notam, type Weather, type LaunchAssessment } from '../lib/planning/engine.ts';
  import type { PlanningDataStatus } from '../lib/planning/status.ts';
  import { notamsShown, notamVisible, zoneVisible, zoneRecordVisible, notamRecordVisible, type FilterState } from '../lib/planning/visibility.ts';
  import { geocode, placeAttribution, SEARCH_DEBOUNCE_MS, type GeoResult } from '../lib/planning/geocode.ts';
  import { zoneAreaLabel, zoneAuthorityLabel, notamTypeLabel, notamTypeDesc, notamFamily, zoneCategory, categoryLabel, categoryIcon, categoryHasMapSymbol } from '../lib/planning/legend.ts';
  import { effClassFC, effComponentAt, type EffectiveGeom, type EffFootprint } from '../lib/planning/merge.ts';
  import MergeWorker from '../lib/planning/merge.worker.ts?worker';
  import { wxCacheGet, wxCacheSet, wxBudgetSpent, wxBudgetAdd, WX_DAILY_WEIGHT_CAP } from '../lib/planning/wxCache.ts';
  import { sunPhase, SUN_PHASES, sunPhaseIcon, sunPhaseLabel, type SunPhase } from '../lib/planning/solar.ts';
  import { gpsValid } from '../lib/geo/geo.ts';
  import { plog, ptime } from '../lib/perf.ts';

  // Weather loads down to a country-wide view; only VERY low zoom (whole-world) is gated, to avoid a
  // meaningless global request. Interpolation keeps the field readable even when the grid is coarse.
  const WIND_MIN_ZOOM = 4;

  let container: HTMLDivElement;
  let engine: PlanningMapEngine | undefined; // the renderer seam — the panel talks ONLY to this
  let resizeObs: ResizeObserver | undefined;
  let refreshTimer: ReturnType<typeof setInterval> | undefined;
  // Flips true once the style has loaded and all layers exist. The toggle effects track it so a control
  // flipped in the brief window before 'load' (glyphs/sprite/tiles fetching) isn't silently dropped.
  let mapReady = $state(false);

  type Point = { lat: number; lon: number };
  let zones: Zone[] = [];
  let notams: Notam[] = [];
  // id → item, so per-item lookups (hover highlight, list click) are O(1) instead of scanning the full
  // ~11k-zone array on every mousemove. Rebuilt in pushLayers whenever the dataset changes.
  let zoneById = new Map<string, Zone>();
  let notamById = new Map<string, Notam>();
  // Spatial index for point lookups (hover info-card, path assessment) — avoids scanning every zone/NOTAM
  // per mousemove. Rebuilt alongside the id-maps in pushLayers.
  let zoneGrid = new RingGrid<Zone>([]);
  let notamGrid = new RingGrid<Notam>([]);
  let updatedAt = $state<number | null>(null); // dataset identity for cache keying — NOT a user-facing time
  /**
   * When the DATA was produced (epoch ms), or null when unknown. This is what the user is shown.
   * `updatedAt` is the download time and answers a different question; showing it would tell a
   * visitor the airspace data is current whenever their browser happened to fetch it.
   */
  let zonesAt = $state<number | null>(null);
  let zonesOk = $state(true);
  /** The NOTAM layer arrived. What of it may be drawn is decided per record (`drawableNotams`). */
  let notamsOk = $state(true);
  let liveNotams = $state(0);
  let notamsWithheld = $state(0);
  // The name follows the data: today the layer holds structures only, and it says so until real
  // NOTAMs are in it again.
  const notamLayerLabel = $derived(liveNotams > 0 ? 'Airspace structures and NOTAMs' : 'Airspace structures');
  let indexing = $state(false); // first-ever full fetch (no cache yet)
  let indexProgress = $state(0);
  let dataError = $state<string | null>(null);
  // Whether a zone dataset is actually ON the map — not merely that the fetch finished. The
  // always-on map key gates on this, for two reasons. notamsOk starts true and only turns false
  // once the data proves stale, so a key rendered before the fetch returned listed NOTAM on a
  // dataset that was about to withdraw it. And when the fetch FAILS there is nothing drawn at all:
  // an earlier version treated "settled either way" as ready and put a three-row key over an empty
  // map, directly beneath a banner saying the zone data could not be loaded.
  const dataReady = $derived(!indexing && updatedAt != null);
  // Renderer capabilities drive which WebGL-only controls the panel shows (dead-control rule).
  let caps = $state<MapCapabilities>({ threeD: true, terrain: true, weather: true, effectiveZones: true, vectorLabels: true });
  let reducedMap = $state(false); // on the WebGL-less Leaflet fallback
  let destroyed = false; // set in onDestroy so an in-flight async engine mount can't leak a live map + timers
  let dataAbort: AbortController | null = null;

  // Assessment card (click a launch point).
  let assessPoint = $state<Point | null>(null);
  let assessErr = $state<string | null>(null);
  let assessment = $state<LaunchAssessment | null>(null);
  let weather = $state<Weather | null>(null);
  let elevation = $state<number | null>(null);
  let pointZones = $state<Zone[]>([]); // ALL zones containing the assessed point (red + green)
  let pointNotams = $state<Notam[]>([]);
  let assessAbort: AbortController | null = null;

  // Place search (Nominatim).
  let searchQuery = $state('');
  let searchResults = $state<GeoResult[]>([]);
  let searchBusy = $state(false);
  let searchError = $state(false); // geocoder unreachable — distinct from "no places found"
  let placeCredit = $state(''); // licence credit for whichever place source answered
  let searchAbort: AbortController | null = null;
  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  let paletteBlurTimer: ReturnType<typeof setTimeout> | undefined;

  function cancelSearch(): void {
    clearTimeout(searchTimer);
    searchAbort?.abort();
  }
  function onSearchInput(): void {
    clearTimeout(searchTimer);
    activeIdx = 0; // a new query re-orders the list → highlight the top match
    const q = searchQuery;
    if (!searchScope.place || q.trim().length < 2) {
      // Abort any in-flight fetch too, or its late result would repopulate the just-cleared dropdown.
      searchAbort?.abort();
      searchResults = [];
      searchError = false;
      return;
    }
    // Debounce is set by the search SOURCE, not by taste — a remote geocoder is rate-limited for
    // the whole application, a baked index is not. See SEARCH_DEBOUNCE_MS.
    searchTimer = setTimeout(async () => {
      searchAbort?.abort();
      const ac = new AbortController();
      searchAbort = ac;
      searchBusy = true;
      searchError = false;
      try {
        const rs = await geocode(q, ac.signal);
        if (!ac.signal.aborted) {
          searchResults = rs;
          placeCredit ||= (await placeAttribution()) ?? '';
        }
      } catch {
        if (!ac.signal.aborted) { searchResults = []; searchError = true; }
      } finally {
        if (searchAbort === ac) searchBusy = false;
      }
    }, SEARCH_DEBOUNCE_MS);
  }
  function pickResult(r: GeoResult): void {
    cancelSearch(); // a debounce/fetch scheduled just before the click would otherwise reopen the list
    searchResults = [];
    searchQuery = r.name.split(',')[0];
    closePalette();
    // Google-Earth-style gradual flight: a longer, arcing zoom-out-then-in instead of a hard jump.
    engine?.flyTo([r.lon, r.lat], Math.max(engine.getZoom(), 13), { duration: 2600, curve: 1.6, speed: 0.9 });
  }

  // ---- persist panel settings + last view across reloads (localStorage) --------------------------
  // The map remembers how you left it: which layers/overlays were on, units, AND the exact camera. Guarded
  // so private-mode / quota / stale-JSON never throw — a bad blob just falls back to defaults. Bumping the
  // key version retires an incompatible saved shape.
  const LS_SETTINGS = 'fp:settings:v1';
  const LS_VIEW = 'fp:view:v1';
  /**
   * Load persisted settings, merging saved values over the defaults ONE LEVEL DEEP.
   *
   * A shallow spread is not enough: the settings blob holds nested records (`searchScope`, `layers`,
   * `wxShow`, `terrain`, `placeLabels`, `flightViz`), and a shallow merge replaces each whole object
   * with the saved one. So the moment a new key is added to any of them, everyone with an existing blob
   * gets `undefined` for it — silently OFF for a boolean read as truthy. Adding `searchScope.photo` hit
   * exactly this: photos would never have appeared in search for anyone who had used the panel before.
   *
   * Arrays are replaced wholesale, never merged — spreading an array into an object turns `[35, 39]`
   * into `{0: 35, 1: 39}` and would corrupt the saved camera centre.
   */
  function lsLoad<T>(key: string, fallback: T): T {
    const isPlainObject = (v: unknown): v is Record<string, unknown> =>
      typeof v === 'object' && v !== null && !Array.isArray(v);
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return fallback;
      const saved = JSON.parse(raw) as Record<string, unknown>;
      const out: Record<string, unknown> = { ...(fallback as Record<string, unknown>) };
      for (const [k, v] of Object.entries(saved)) {
        const base = out[k];
        out[k] = isPlainObject(base) && isPlainObject(v) ? { ...base, ...v } : v;
      }
      return out as T;
    } catch { /* private mode / malformed JSON → defaults */ }
    return fallback;
  }
  function lsSave(key: string, val: unknown): void {
    try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* quota / private mode → skip */ }
  }
  const S0 = lsLoad(LS_SETTINGS, {
    basemap: 'satellite' as 'streets' | 'satellite',
    showFilters: true, showFlightsTable: false,
    searchScope: { flight: true, photo: true, nofly: true, ok: true, notam: true, place: true },
    layers: { nofly: true, allowed: true, notam: true, weather: false },
    wxShow: { temp: false, wind: true, rain: false }, wxHourOffset: 0,
    effective: true,
    terrain: { hillshade: false, elevation: false, contours: false },
    poi: true, poiSymbols: true, poiNames: true,
    placeLabels: { il: true, ilce: true, koy: true },
    showFlights: true, showPhotos: true, flightViz: { air: true, ground: true, curtain: true },
    windUnit: 'kmh' as 'kmh' | 'ms',
    // Flight filters. The facets follow the same "absent or true = selected" convention as the zone
    // facets, so an unfiltered map persists as `{}` instead of a wall of `true`s.
    typeFacet: {} as Record<string, boolean>,
    fmtFacet: {} as Record<string, boolean>,
    phaseFacet: {} as Record<string, boolean>,
    dowFacet: {} as Record<string, boolean>,
    monthFacet: {} as Record<string, boolean>,
    hourFrom: 0, hourTo: 24,
    numRange: { durMin: null, durMax: null, distMin: null, distMax: null, altMin: null, altMax: null } as Record<string, number | null>,
  });
  /**
   * Türkiye's extent, used to frame the FIRST visit. A fixed zoom cannot do this: 5.5 fills a laptop
   * and leaves a 4K monitor showing Italy and Egypt, while a phone gets a slice of Anatolia. Fitting
   * the bounds derives the zoom from the viewport, so the country fills whatever screen arrives.
   * Only used when there is no stored view — a returning visitor keeps where they were.
   */
  const TR_BOUNDS: [[number, number], [number, number]] = [
    [25.6, 35.8],
    [44.9, 42.2],
  ];
  const hasStoredView = (() => {
    try { return localStorage.getItem(LS_VIEW) !== null; } catch { return false; }
  })();
  const V0 = lsLoad(LS_VIEW, { center: [35.0, 39.0] as [number, number], zoom: 5.5, pitch: 0, bearing: 0 });

  // Layer filters + weather wind grid.
  /**
   * The panel opens by default because it doubles as the legend — correct on a desktop, wrong on a
   * phone, where it was measured covering 97% of the width and 81% of the height of a 390x844
   * screen, hiding the map the visitor came for. A narrow viewport therefore starts collapsed, and
   * the toggle is one tap away. A STORED preference always wins: this only picks the first-run
   * default, so someone who opened the panel on their phone keeps it open.
   */
  const NARROW_PX = 700;
  const storedFilters = (() => {
    try { return localStorage.getItem(LS_SETTINGS) !== null; } catch { return false; }
  })();
  const wideEnoughForPanel = typeof window === 'undefined' || window.innerWidth >= NARROW_PX;
  let showFilters = $state(storedFilters ? S0.showFilters : wideEnoughForPanel);
  let refreshing = $state(false);
  let searchFocused = $state(false); // the unified search palette is open (omnibox: on-map data + places)
  let searchInputEl = $state<HTMLInputElement | undefined>(undefined);
  let shortcutsOpen = $state(false); // the ? keyboard-shortcuts overlay
  // The all-flights reference table (a map-embedded panel: filter/sort every flight, click a row to fly).
  let showFlightsTable = $state(S0.showFlightsTable);
  let flightsTableQuery = $state('');
  type FlightSortKey = 'title' | 'date' | 'tod' | 'durationS' | 'distanceKm' | 'altMax';
  let flightsSortKey = $state<FlightSortKey>('date');
  let flightsSortDir = $state<'asc' | 'desc'>('desc');
  let filtersEl = $state<HTMLDivElement>(); // the Layers & filters panel — measured to place the facet popup
  let facetFlip = $state(false); // true → open the facet sub-popup to the LEFT (panel is near the right edge)
  let facetShift = $state<number | null>(null); // px from the panel's left edge, when NEITHER side fits
  let winW = $state(0); // viewport width, bound below — re-places the popup when the window resizes
  const FACET_POP_W = 248; // the popup's own width (see .fp-facet-pop)
  const FACET_GAP = 8; // breathing room between the panel and the popup
  const FACET_EDGE = 8; // never let the popup touch the viewport edge
  /**
   * Place the facet sub-popup so it always lands fully on screen.
   *
   * It normally opens to the panel's RIGHT, right beside the facet buttons, and flips LEFT when the
   * panel has been dragged near the right edge. The flip alone was not enough: it asked only "is there
   * room on the right?", so in a narrow window — where there is room on NEITHER side — it flipped left
   * and put the popup at x ≈ −245, completely off screen and unreachable. That hit all eight facets.
   *
   * Third case: when neither side fits, overlay the panel and clamp inside the viewport. Covering the
   * panel is the right trade at that width — an unreachable popup is strictly worse.
   *
   * Offsets stay relative to the panel so the popup keeps travelling with it while it is dragged.
   */
  $effect(() => {
    if (!facetPop || !filtersEl) { facetShift = null; return; }
    void winW; // track the viewport so a resize re-places an open popup
    const r = filtersEl.getBoundingClientRect();
    const vw = window.innerWidth;
    if (vw - r.right - FACET_GAP >= FACET_POP_W) { facetFlip = false; facetShift = null; return; }
    if (r.left - FACET_GAP >= FACET_POP_W) { facetFlip = true; facetShift = null; return; }
    facetFlip = false;
    const left = Math.max(FACET_EDGE, Math.min(vw - FACET_POP_W - FACET_EDGE, r.left));
    facetShift = left - r.left;
  });
  // Type filters that scope WHAT the search looks through (like filtering a DB), independent of what the
  // map shows.
  let searchScope = $state(S0.searchScope);
  // Granular sub-filter: selected category / NOTAM-type keys (empty = all). Narrows zone/NOTAM results.
  let catFilter = $state<string[]>([]);
  let facetPop = $state<string | null>(null); // which zone sub-filter popup is open (opens left of the panel)
  const FACET_TITLES: Record<string, string> = {
    'nofly-cat': 'Why restricted', 'ok-cat': 'Category', 'notam-type': 'Structure types', 'site-cat': 'Site symbols',
    tod: 'Time of day', dow: 'Day of week', month: 'Month', type: 'Type',
  };
  const facetTitle = (key: string): string => t(FACET_TITLES[key] ?? 'Authority');
  let layers = $state(S0.layers); // weather off by default
  // Faceted sub-filters (item 6): filter zones by authority + NOTAMs by type. Populated from the data.
  let notamFacet = $state<Record<string, boolean>>({});
  let authFacet = $state<Record<string, boolean>>({});
  let availNotamTypes = $state<string[]>([]);
  let availAuthorities = $state<number[]>([]);
  // "Why restricted" category sub-breakdown, per zone kind (red / green). Derived from zone names.
  let catFacet = $state<{ nofly: Record<string, boolean>; ok: Record<string, boolean> }>({ nofly: {}, ok: {} });
  /**
   * Every filter as one value, for the shared predicates in `visibility.ts`.
   *
   * They live outside this component because the same question — may this be drawn? — is asked from
   * the visibility effect, the merged-footprint filter and the effective-mode tooltip, and those
   * three had drifted apart. The direct NOTAM layer was withheld on `notamsOk` while the EFFECTIVE
   * layer, which is the default view, was not: the panel told the visitor temporary restrictions
   * were being withheld as unreliable, and the map went on drawing them.
   */
  const filterState = $derived<FilterState>({ layers, notamsOk, authFacet, catFacet, notamFacet });
  const notamsVisible = $derived(notamsShown(filterState));
  let availCats = $state<{ nofly: string[]; ok: string[] }>({ nofly: [], ok: [] });
  // Which site-symbol categories (military/dam/SİT/…) are shown — filterable like the zone facets.
  let siteCatFacet = $state<Record<string, boolean>>({});
  let availSiteCats = $state<string[]>([]);
  // Independent weather layers — temp / wind / rain can all show at once (not a single-select).
  let wxShow = $state(S0.wxShow);
  let wxHourOffset = $state(S0.wxHourOffset); // 0 = now; otherwise hours into the future (flight planning is forward-looking)
  let effective = $state(S0.effective); // default ON: merge overlaps so a no-fly pocket inside an allowed area shows red (safety)
  let effBusy = $state(false); // the geometric union is running
  let effFailed = $state(false); // the merge worker died; effective mode falls back to the raw zones
  let mapFailed = $state(false); // neither renderer started — there is no map on this page at all
  let effStamp = -1; // updatedAt value the cached merge was built from (recompute when data changes)
  let effGeom: EffectiveGeom | null = null; // cached merged geometry (for hover component lookup)
  let effReady = $state(false); // the merge for the CURRENT dataset is done → safe to show effective (else raw)
  let terrain = $state(S0.terrain); // DEM relief + colours + contour lines
  let poi = $state(S0.poi); // drone-relevant places (airports / heliports / military airfields) — default on
  let poiSymbols = $state(S0.poiSymbols); // the icons on the site-symbol markers (independent of the names)
  let poiNames = $state(S0.poiNames); // the text labels on the site-symbol markers
  // Basemap place-name detail level — İl (province) / İlçe (district) / Köy (village), each toggleable so
  // the map can be de-cluttered (thousands of village names) or shown in full. Default all on.
  let placeLabels = $state(S0.placeLabels);
  const placeAll = $derived(placeLabels.il && placeLabels.ilce && placeLabels.koy);
  const placeSome = $derived(placeLabels.il || placeLabels.ilce || placeLabels.koy);
  const togglePlaceAll = (on: boolean): void => { placeLabels.il = on; placeLabels.ilce = on; placeLabels.koy = on; };
  let showFlights = $state(S0.showFlights); // the user's own flight tracks (scanned from the archive)
  let showPhotos = $state(S0.showPhotos); // the user's own located photos — previously always-on with no toggle
  // Flight visualization sub-layers: airborne route (the path at real altitude) + ground projection
  // (2D shadow line) + 3D vertical curtain connecting them. Air on by default — that's the real route.
  let flightViz = $state(S0.flightViz); // deck.gl 3D line + ground projection + altitude curtain
  // One flight = one clip. `samples` = [lon, lat, tSec (from clip start), relAlt] for path-hover detail.
  type FlightSample = [number, number, number, number];
  type VideoMeta = { width: number | null; height: number | null; fps: number | null; codec: string | null; durationS: number | null; bitrateMbps: number | null; created: string | null };
  type MyFlight = {
    id: string;
    folder: string;
    name: string;
    clipName: string;
    date: string;
    segments: [number, number][][];
    samples: FlightSample[];
    startTime: string;
    endTime: string;
    durationS: number;
    altMin: number;
    altMax: number;
    distanceKm: number;
    video: string | null;
    videoDesc: string;
    meta: VideoMeta | null;
  };
  // A photo is a single located capture (§2.5) — mirrors the server's PhotoPoint.
  type MyPhoto = {
    id: string; name: string; folder: string; path: string;
    lat: number; lon: number; absAlt?: number; takenAt?: string;
    make?: string; model?: string; lens?: string;
    iso?: number; shutter?: number; fnum?: number; focalLen?: number; ev?: number;
    width?: number; height?: number; thumb?: string; raw: boolean; present: boolean;
  };
  // Title = the clip's own (video-filename) description when it has one, else the session/folder name.
  const flightTitle = (f: { videoDesc?: string; name: string }): string => (f.videoDesc?.trim() || f.name);
  // DJI encodes the colour profile in the clip filename suffix: `_D` = D-Log(-M), `_N` = Normal. This is
  // the same signal the player uses to auto-apply the D-Log→709 LUT (see Player colorMode detection).
  function colorProfile(clip: string): string {
    if (/_dlog|_D$/i.test(clip)) return 'D-Log';
    if (/_N$/i.test(clip)) return 'Normal';
    return '';
  }
  // Basename of the video path (the actual file on disk) — the raw filename source, distinct from clipName.
  const videoFileName = (path: string | null): string => (path ? (path.split('/').pop() ?? path) : '');
  let flightDetail = $state<MyFlight | null>(null); // the clip whose rich detail card is open
  let myFlights = $state<MyFlight[]>([]);
  let myPhotos = $state<MyPhoto[]>([]);
  let photoView = $state<MyPhoto | null>(null); // the photo whose full-size lightbox is open
  // Live progress of the background archive scan (console + bar); null when idle/dismissed.
  type ScanStatus = { scanning: boolean; done: number; total: number; phase: string; recent: string[] };
  let scanStatus = $state<ScanStatus | null>(null);
  let scanPollTimer: ReturnType<typeof setTimeout> | undefined;
  // Storyboard background-fill progress (server pre-generates every clip's frames; see /api/thumb-status).
  type ThumbStatus = { total: number; done: number; pending: number; active: number };
  let thumbStatus = $state<ThumbStatus | null>(null);
  let thumbPolling = false;
  let thumbPollTimer: ReturnType<typeof setTimeout> | undefined;
  // Background-activity console (bottom-left): every async op the map runs in the background — loading
  // zones, weather, flights, merging effective geometry — shows up here as a live line (spinner → ✓/✕), so
  // the map never looks stuck when work is happening off the main thread. The archive scan streams its own
  // fine-grained progress into the same panel. All of it is advisory; the UI never blocks on any of it.
  type ActItem = { id: number; label: string; state: 'run' | 'ok' | 'err'; at: number };
  let activity = $state<ActItem[]>([]);
  let activityDismissed = $state(false); // user closed the panel → stay closed until new work arrives
  let actHideTimer: ReturnType<typeof setTimeout> | undefined;
  let actSeq = 0;
  const anyRunning = $derived(activity.some((a) => a.state === 'run') || !!scanStatus?.scanning);
  const consoleOpen = $derived(!activityDismissed && (activity.length > 0 || !!(scanStatus && (scanStatus.scanning || scanStatus.total > 0)) || !!(thumbStatus && thumbStatus.total > 0 && (thumbStatus.done < thumbStatus.total || thumbStatus.active > 0))));
  // Wrap a background promise so its lifecycle is visible in the console. Returns the SAME promise, so callers
  // still handle success/failure exactly as before — this only observes. A settled line lingers a few seconds
  // then the panel auto-hides once nothing is running.
  function track<T>(label: string, p: Promise<T>): Promise<T> {
    const id = ++actSeq;
    const now = Date.now();
    activity = [...activity.filter((a) => a.state === 'run' || now - a.at < 6000), { id, label, state: 'run' as const, at: now }].slice(-6);
    activityDismissed = false;
    clearTimeout(actHideTimer);
    const settle = (state: 'ok' | 'err'): void => {
      activity = activity.map((a) => (a.id === id ? { ...a, state, at: Date.now() } : a));
      // Hide the panel a few seconds after the last op settles (unless a scan is still streaming).
      clearTimeout(actHideTimer);
      actHideTimer = setTimeout(() => { if (!anyRunning) activity = []; }, 4500);
    };
    void p.then(() => settle('ok'), () => settle('err'));
    return p;
  }
  let windUnit = $state<'kmh' | 'ms'>(S0.windUnit); // display unit for wind (logic stays m/s)

  // ---- flight filters: time of day, weekday, month, and numeric ranges ---------------------------
  // "Show me the sunset flights" is not a clock question: sunset swings from ~16:40 to ~20:45 across
  // the year here, so any fixed hour bucket labelled "sunset" is wrong for most of it. The time-of-day
  // axis is therefore the sun's real elevation at the takeoff point (lib/planning/solar.ts), while the
  // clock window, weekday and month axes read the stored wall clock directly.
  //
  // Every axis defaults to unrestricted and a flight must pass ALL of them. One predicate feeds the
  // map, the all-flights table and every count, so the three can never disagree.
  const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Monday-first display order; values are Date#getDay()
  const DOW_LABELS: Record<number, string> = { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday', 0: 'Sunday' };
  const WEEKDAYS = [1, 2, 3, 4, 5];
  const WEEKEND = [6, 0];
  const MONTH_LABELS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  // Northern-hemisphere seasons — this archive is Turkish footage; a southern shoot would read these
  // inverted, which is why they are quick-select shortcuts rather than a filter axis of their own.
  const SEASONS: { label: string; months: number[] }[] = [
    { label: 'Winter', months: [11, 0, 1] },
    { label: 'Spring', months: [2, 3, 4] },
    { label: 'Summer', months: [5, 6, 7] },
    { label: 'Autumn', months: [8, 9, 10] },
  ];
  // Phase quick-selects: the groupings a photographer actually reaches for.
  const PHASE_GROUPS: { label: string; keys: SunPhase[] }[] = [
    { label: 'Golden hour', keys: ['sunrise', 'sunset'] },
    { label: 'Blue hour', keys: ['dawn', 'dusk'] },
    { label: 'Daylight', keys: ['morning', 'noon', 'afternoon'] },
    { label: 'Dark', keys: ['night'] },
  ];

  // Media kind. The archive holds two sorts of capture — video clips (a flight track) and located
  // photos — and until now photos had no toggle at all: they always drew.
  const MEDIA_KINDS: { key: 'video' | 'photo'; icon: string; label: string }[] = [
    { key: 'video', icon: '🎥', label: 'Videos' },
    { key: 'photo', icon: '📷', label: 'Photos' },
  ];
  const PHOTO_FORMATS: { key: 'jpeg' | 'raw'; label: string }[] = [
    { key: 'jpeg', label: 'JPEG' },
    { key: 'raw', label: 'RAW (DNG)' },
  ];

  let typeFacet = $state<Record<string, boolean>>({ ...S0.typeFacet });
  let fmtFacet = $state<Record<string, boolean>>({ ...S0.fmtFacet });
  let phaseFacet = $state<Record<string, boolean>>({ ...S0.phaseFacet });
  let dowFacet = $state<Record<string, boolean>>({ ...S0.dowFacet });
  let monthFacet = $state<Record<string, boolean>>({ ...S0.monthFacet });
  let hourFrom = $state(S0.hourFrom);
  let hourTo = $state(S0.hourTo);
  let numRange = $state({ ...S0.numRange });

  // What a capture's timestamp tells us. Parsed once per item and keyed by id, so the filters, the
  // table's Time column and the search index all read this instead of re-parsing.
  type TimeFacts = { hour: number; minute: number; tod: number; dow: number; month: number; phase: SunPhase | null };
  /**
   * Both media kinds hand us an UNZONED local wall clock, just punctuated differently — a flight's
   * `startTime` is "YYYY-MM-DD HH:MM:SS" (archiveScan's projection) and a photo's `takenAt` is
   * "YYYY-MM-DDTHH:MM:SS" (EXIF DateTimeOriginal, which carries no zone). Neither is a UTC instant, so
   * `new Date()` on it parses as local — which is what the digits mean. The sun maths is then right
   * whenever footage and viewer share a timezone, the assumption lib/media/timecode.ts documents.
   *
   * NB: do NOT run either through `localWallClock()`. That converts a UTC instant to local; these are
   * already local, so it would shift them a second time — the exact bug fixed in djiSrt.
   */
  function timeFactsFrom(wall: string | undefined, origin: [number, number] | null): TimeFacts | null {
    if (!wall || wall.length < 16) return null; // no usable clock → no time facts, honestly
    const hour = Number(wall.slice(11, 13));
    const minute = Number(wall.slice(14, 16));
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    const d = new Date(wall.replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return null;
    return { hour, minute, tod: hour * 60 + minute, dow: d.getDay(), month: d.getMonth(), phase: origin ? sunPhase(d.getTime(), origin[1], origin[0]) : null };
  }
  // One map over BOTH kinds — ids are unique across flights and photos, and the unified table needs a
  // single lookup that works whichever kind a row is.
  const mediaTime = $derived.by((): Map<string, TimeFacts> => {
    const m = new Map<string, TimeFacts>();
    for (const f of myFlights) {
      // Takeoff point: the first ground segment's first vertex is what the map already uses for the
      // start dot; raw samples are the fallback for a flight with no drawable segment.
      const origin = f.segments[0]?.[0] ?? (f.samples[0] ? ([f.samples[0][0], f.samples[0][1]] as [number, number]) : null);
      const tf = timeFactsFrom(f.startTime, origin);
      if (tf) m.set(f.id, tf);
    }
    for (const p of myPhotos) {
      // A (0,0) fix is the archive's "no GPS" sentinel, not a spot in the Gulf of Guinea — shared
      // `gpsValid` is the same guard library.ts and archiveScan use. Without it such a photo would get a
      // confidently wrong sun phase computed at the null island.
      const tf = timeFactsFrom(p.takenAt, gpsValid(p.lat, p.lon) ? [p.lon, p.lat] : null);
      if (tf) m.set(p.id, tf);
    }
    return m;
  });

  // Is each axis still wide open? Drives both the predicate's fast path and the "filter is on" badge.
  const typeAll = $derived(MEDIA_KINDS.every((k) => typeFacet[k.key] !== false));
  const fmtAll = $derived(PHOTO_FORMATS.every((f) => fmtFacet[f.key] !== false));
  const phaseAll = $derived(SUN_PHASES.every((p) => phaseFacet[p.key] !== false));
  const dowAll = $derived(DOW_ORDER.every((d) => dowFacet[String(d)] !== false));
  const monthAll = $derived(MONTH_LABELS.every((_, i) => monthFacet[String(i)] !== false));
  const hourAll = $derived(hourFrom === 0 && (hourTo === 24 || hourTo === 0));
  const numAll = $derived(Object.values(numRange).every((v) => v == null));
  const timeAll = $derived(phaseAll && dowAll && monthAll && hourAll);
  const mediaFilterActive = $derived(!timeAll || !numAll || !typeAll || !fmtAll);

  // Clock window. `hourTo === 24` means "to the end of the day"; from > to wraps past midnight, so
  // 22 → 4 is a single night window rather than an empty one — the shape a night query needs.
  function hourInWindow(h: number): boolean {
    if (hourAll) return true;
    const to = hourTo === 0 ? 24 : hourTo;
    if (hourFrom === to) return true; // zero-width would hide everything → treat as unrestricted
    return hourFrom < to ? h >= hourFrom && h < to : h >= hourFrom || h < to;
  }
  // Inclusive numeric bound; a null bound is "unbounded on that side".
  function inRange(v: number, lo: number | null, hi: number | null): boolean {
    return (lo == null || v >= lo) && (hi == null || v <= hi);
  }
  /**
   * The time axes — shared by both media kinds, which is the point: "everything I shot at sunset"
   * should mean photos too, not just clips.
   *
   * An item with no readable timestamp cannot honestly be placed in any specific hour, weekday, month
   * or sun phase, so it survives only while all four of those axes are unrestricted, rather than being
   * silently assigned to a bucket it may not belong to.
   */
  function timePasses(id: string): boolean {
    const tf = mediaTime.get(id);
    if (!tf) return timeAll;
    if (!hourInWindow(tf.hour)) return false;
    if (dowFacet[String(tf.dow)] === false) return false;
    if (monthFacet[String(tf.month)] === false) return false;
    // Same rule one level down: a clock without coordinates yields no sun phase.
    if (tf.phase === null) return phaseAll;
    return phaseFacet[tf.phase] !== false;
  }
  function flightPasses(f: MyFlight): boolean {
    if (typeFacet.video === false) return false;
    if (!inRange((f.durationS ?? 0) / 60, numRange.durMin, numRange.durMax)) return false;
    if (!inRange(f.distanceKm ?? 0, numRange.distMin, numRange.distMax)) return false;
    if (!inRange(f.altMax ?? 0, numRange.altMin, numRange.altMax)) return false;
    return timePasses(f.id);
  }
  function photoPasses(p: MyPhoto): boolean {
    if (typeFacet.photo === false) return false;
    if (fmtFacet[p.raw ? 'raw' : 'jpeg'] === false) return false;
    // Duration / distance / altitude are deliberately NOT applied here: a photo has no such values, so
    // filtering it by them would be a category error rather than a narrowing. The type toggle is how
    // you remove photos — hence the "Videos only" note on those rows in the panel.
    return timePasses(p.id);
  }
  // What the map draws and the table lists. Identity-stable when nothing is filtered, so the common
  // case costs no allocation.
  const visibleFlights = $derived.by((): MyFlight[] => (mediaFilterActive ? myFlights.filter(flightPasses) : myFlights));
  const visiblePhotos = $derived.by((): MyPhoto[] => (mediaFilterActive ? myPhotos.filter(photoPasses) : myPhotos));

  // Per-option tallies so each checkbox states its own weight ("only 3 night flights") instead of
  // making the user select-and-see. Counted over EVERY capture of both kinds, so a count never depends
  // on the other axes' current selection — a count that moved as you filtered would be unreadable.
  const allMediaIds = $derived.by((): string[] => [...myFlights.map((f) => f.id), ...myPhotos.map((p) => p.id)]);
  function tally<K>(pick: (tf: TimeFacts) => K | null | undefined): Map<K, number> {
    const m = new Map<K, number>();
    for (const id of allMediaIds) {
      const tf = mediaTime.get(id);
      if (!tf) continue;
      const k = pick(tf);
      if (k !== null && k !== undefined) m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  }
  const phaseCounts = $derived.by((): Map<string, number> => tally((tf) => tf.phase));
  const dowCounts = $derived.by((): Map<number, number> => tally((tf) => tf.dow));
  const monthCounts = $derived.by((): Map<number, number> => tally((tf) => tf.month));
  const typeCounts = $derived({ video: myFlights.length, photo: myPhotos.length });
  const fmtCounts = $derived.by((): Record<string, number> => {
    let raw = 0;
    for (const p of myPhotos) if (p.raw) raw++;
    return { raw, jpeg: myPhotos.length - raw };
  });

  // Widest value present in the archive, per numeric axis — shown as the "max" placeholder so the
  // range boxes tell you the scale you are filtering within instead of demanding a guess.
  const flightExtent = $derived.by(() => {
    let dur = 0;
    let dist = 0;
    let alt = 0;
    for (const f of myFlights) {
      dur = Math.max(dur, (f.durationS ?? 0) / 60);
      dist = Math.max(dist, f.distanceKm ?? 0);
      alt = Math.max(alt, f.altMax ?? 0);
    }
    return { dur: Math.ceil(dur), dist: Math.ceil(dist), alt: Math.ceil(alt) };
  });

  // Compact per-row summaries for the collapsed panel: which phases (as icons), the clock window, how
  // many days/months — so the panel states what it is doing without the user opening a popup.
  const phaseIcons = $derived(SUN_PHASES.filter((p) => phaseFacet[p.key] !== false).map((p) => p.icon).join('') || t('None'));
  const dowSummary = $derived(dowAll ? t('All') : `${DOW_ORDER.filter((d) => dowFacet[String(d)] !== false).length}/7`);
  const monthSummary = $derived(monthAll ? t('All') : `${MONTH_LABELS.filter((_, i) => monthFacet[String(i)] !== false).length}/12`);
  // Type row shows the kinds still on, as icons, plus the RAW/JPEG narrowing when it is doing something.
  const typeSummary = $derived(
    typeAll && fmtAll
      ? t('All')
      : [MEDIA_KINDS.filter((k) => typeFacet[k.key] !== false).map((k) => k.icon).join('') || t('None'),
         fmtAll ? '' : PHOTO_FORMATS.filter((f) => fmtFacet[f.key] !== false).map((f) => f.key.toUpperCase()).join('/') || t('None')].filter(Boolean).join(' · '),
  );
  const fmtTod = (tf: TimeFacts): string => `${String(tf.hour).padStart(2, '0')}:${String(tf.minute).padStart(2, '0')}`;
  // Search synonyms must carry BOTH languages at once, never `t()`. `t()` returns only the language the
  // UI happens to be in, which silently halves the index: typing "cumartesi" while the UI sat in
  // English found nothing, even though "saturday" worked. Intl gives both names with no second table
  // to keep in sync, and the search key then stops depending on the current language entirely.
  const bothLocales = (d: Date, opt: Intl.DateTimeFormatOptions): string =>
    `${d.toLocaleDateString('en', { ...opt, timeZone: 'UTC' })} ${d.toLocaleDateString('tr', { ...opt, timeZone: 'UTC' })}`;
  // Walk forward from an arbitrary base to the wanted weekday, so this needs no claim about which day
  // the base date happens to be.
  const dowDate = (dow: number): Date => {
    const d = new Date(Date.UTC(2026, 0, 4));
    d.setUTCDate(d.getUTCDate() + ((dow - d.getUTCDay() + 7) % 7));
    return d;
  };
  const DOW_WORDS: Record<number, string> = Object.fromEntries(DOW_ORDER.map((d) => [d, bothLocales(dowDate(d), { weekday: 'long' })]));
  const MONTH_WORDS: string[] = MONTH_LABELS.map((_, m) => bothLocales(new Date(Date.UTC(2026, m, 1)), { month: 'long' }));
  // Phase synonyms are already bilingual in SUN_PHASES.words, plus the English label for exact hits.
  const PHASE_WORDS = new Map(SUN_PHASES.map((p) => [p.key, `${p.label} ${p.words}`]));
  const phaseWords = (p: SunPhase): string => PHASE_WORDS.get(p) ?? '';
  const hh = (h: number): string => `${String(h % 24).padStart(2, '0')}:00`;
  const hourSummary = $derived(hourAll ? t('All') : `${hh(hourFrom)}–${hourTo === 0 ? '24:00' : hh(hourTo)}`);
  // Sun phase and clock window are both "time of day", so they share one panel row and one popup.
  const todSummary = $derived(phaseAll && hourAll ? t('All') : [phaseAll ? '' : phaseIcons, hourAll ? '' : hourSummary].filter(Boolean).join(' · '));
  // True while the clock window runs backwards over midnight (22:00 → 04:00) — worth saying out loud,
  // since a from-value above the to-value otherwise reads as a mistake.
  const hourWraps = $derived(!hourAll && hourFrom > (hourTo === 0 ? 24 : hourTo));

  // Group buttons select ONLY their group — clicking "Weekend" means "just the weekend", not "add the
  // weekend to whatever was already on", which is what the word means to the user.
  const setPhaseOnly = (keys: SunPhase[]): void => { phaseFacet = Object.fromEntries(SUN_PHASES.map((p) => [p.key, keys.includes(p.key)])); };
  const setPhaseAll = (on: boolean): void => { phaseFacet = Object.fromEntries(SUN_PHASES.map((p) => [p.key, on])); };
  const setDowOnly = (days: number[]): void => { dowFacet = Object.fromEntries(DOW_ORDER.map((d) => [String(d), days.includes(d)])); };
  const setDowAll = (on: boolean): void => { dowFacet = Object.fromEntries(DOW_ORDER.map((d) => [String(d), on])); };
  const setMonthOnly = (months: number[]): void => { monthFacet = Object.fromEntries(MONTH_LABELS.map((_, i) => [String(i), months.includes(i)])); };
  const setMonthAll = (on: boolean): void => { monthFacet = Object.fromEntries(MONTH_LABELS.map((_, i) => [String(i), on])); };
  const setTypeOnly = (keys: string[]): void => { typeFacet = Object.fromEntries(MEDIA_KINDS.map((k) => [k.key, keys.includes(k.key)])); };
  const setTypeAll = (on: boolean): void => { typeFacet = Object.fromEntries(MEDIA_KINDS.map((k) => [k.key, on])); fmtFacet = Object.fromEntries(PHOTO_FORMATS.map((f) => [f.key, on])); };
  function resetMediaFilters(): void {
    typeFacet = {};
    fmtFacet = {};
    phaseFacet = {};
    dowFacet = {};
    monthFacet = {};
    hourFrom = 0;
    hourTo = 24;
    numRange = { durMin: null, durMax: null, distMin: null, distMax: null, altMin: null, altMax: null };
  }

  // ---- searchable directory of everything on the map (flights + zones + NOTAMs) -----------------
  // `search` is a precomputed lowercased key so filtering thousands of items per keystroke stays fast.
  type IndexItem = { kind: 'flight' | 'photo' | 'nofly' | 'ok' | 'notam'; id: string; label: string; sub: string; color: string; icon: string; cat: string; search: string; bounds: [[number, number], [number, number]] };
  const INDEX_GROUPS: { kind: IndexItem['kind']; label: string }[] = $derived([
    ...(feat.archive ? [{ kind: 'flight' as const, label: 'My flights' }, { kind: 'photo' as const, label: 'My photos' }] : []),
    // NOTAM scope disappears with the data it searches — see `notamsOk`.
    { kind: 'nofly', label: 'No-fly (red)' },
    { kind: 'ok', label: 'Allowed (green)' },
    ...(notamsOk ? [{ kind: 'notam' as const, label: notamLayerLabel }] : []),
  ]);
  // Scope chips for the search palette (which data types the query looks through) — with type colour + icon.
  // `grp` clusters them inside the scope frame: SHGM airspace data vs the user's own flights + geocoded places.
  const SCOPES: { k: keyof typeof searchScope; label: string; color: string; icon: string; grp: 'shgm' | 'other' }[] = $derived([
    { k: 'nofly', label: 'No-fly (red)', color: '#ef4444', icon: '⛔', grp: 'shgm' },
    { k: 'ok', label: 'Allowed (green)', color: '#22c55e', icon: '✅', grp: 'shgm' },
    ...(notamsOk ? ([{ k: 'notam', label: notamLayerLabel, color: '#f59e0b', icon: '⚠', grp: 'shgm' }] as const) : []),
    ...(feat.archive
      ? ([
          { k: 'flight', label: 'My flights', color: '#22d3ee', icon: '🛩', grp: 'other' },
          { k: 'photo', label: 'My photos', color: '#e879f9', icon: '📷', grp: 'other' },
        ] as const)
      : []),
    { k: 'place', label: 'Places', color: '#94a3b8', icon: '📍', grp: 'other' },
  ]);
  const shgmScopes = $derived(SCOPES.filter((s) => s.grp === 'shgm'));
  const otherScopes = $derived(SCOPES.filter((s) => s.grp === 'other'));
  function ptsBbox(pts: number[][]): [[number, number], [number, number]] {
    let m0 = Infinity, n0 = Infinity, m1 = -Infinity, n1 = -Infinity;
    for (const [lo, la] of pts) {
      if (lo < m0) m0 = lo;
      if (la < n0) n0 = la;
      if (lo > m1) m1 = lo;
      if (la > n1) n1 = la;
    }
    return [[m0, n0], [m1, n1]];
  }
  // Fold text for search matching: case-, accent- AND dotless-i-insensitive, so "Celal İbiş",
  // "celal ibis" and "CELAL IBIŞ" all match the same key. The Turkish İ/I/ı split is the trap —
  // toLocaleLowerCase('tr') keeps ı and i distinct, so a user typing a plain "i" for an "İ" name
  // (or vice-versa) found nothing. Map every i-variant to "i" up front, then NFKD-strip the rest
  // of the diacritics (ş→s, ğ→g, ü→u, ö→o, ç→c) so search is forgiving of keyboard/case differences.
  const foldTr = (s: string): string =>
    s.replace(/İ/g, 'i').replace(/ı/g, 'i').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  // `extra` is searchable but never displayed — it carries synonyms (time-of-day words, weekday and
  // month names in both languages) that would bloat a result row if they were shown.
  const mkItem = (kind: IndexItem['kind'], id: string, label: string, sub: string, color: string, icon: string, cat: string, bounds: IndexItem['bounds'], extra = ''): IndexItem => ({ kind, id, label, sub, color, icon, cat, search: foldTr(`${label} ${sub} ${cat} ${extra}`), bounds });
  // Rebuilt when the dataset (updatedAt) or the flights change; filtering happens separately on the query.
  const indexItems = $derived.by((): IndexItem[] => {
    void notamEpoch; // re-derive when a NOTAM window edge passes — this list filters by Date.now() below
    void updatedAt;
    const items: IndexItem[] = [];
    for (const f of myFlights) {
      const segs = f.segments.filter((s) => s.length > 1);
      if (!segs.length) continue;
      // Show the clock time + sun-phase icon in the result row (a flight's "when" is as identifying as
      // its name), and make the phase / weekday / month words searchable without showing them.
      const tf = mediaTime.get(f.id);
      const when = tf ? `${fmtTod(tf)}${tf.phase ? ` ${sunPhaseIcon(tf.phase)}` : ''}` : '';
      const words = tf ? `${tf.phase ? phaseWords(tf.phase) : ''} ${DOW_WORDS[tf.dow]} ${MONTH_WORDS[tf.month]}` : '';
      items.push(mkItem('flight', f.id, flightTitle(f), [f.videoDesc ? f.name : '', f.clipName, f.date, when].filter(Boolean).join(' · '), flightColor(f.id), '🛩', '', ptsBbox(segs.flat()), words));
    }
    // Photos are searchable on exactly the same terms as clips — including the time words, so "gün
    // batımı" or "cumartesi" reaches both kinds. A photo is a single point, so its bbox is degenerate;
    // `goToItem` flies to it at a fixed zoom rather than fitting a zero-size box.
    for (const p of myPhotos) {
      if (!gpsValid(p.lat, p.lon)) continue; // unlocatable → nothing for the map to fly to
      const tf = mediaTime.get(p.id);
      const when = tf ? `${fmtTod(tf)}${tf.phase ? ` ${sunPhaseIcon(tf.phase)}` : ''}` : '';
      const words = tf
        ? `${tf.phase ? phaseWords(tf.phase) : ''} ${DOW_WORDS[tf.dow]} ${MONTH_WORDS[tf.month]} ${p.raw ? 'raw dng' : 'jpeg jpg'} photo fotoğraf ${p.model ?? ''}`
        : `${p.raw ? 'raw dng' : 'jpeg jpg'} photo fotoğraf ${p.model ?? ''}`;
      items.push(mkItem('photo', p.id, p.name, [p.folder, p.takenAt?.slice(0, 10) ?? '', when].filter(Boolean).join(' · '), '#e879f9', '📷', '', ptsBbox([[p.lon, p.lat]]), words));
    }
    for (const z of zones) {
      if (!z.rings[0]?.length) continue;
      const red = z.area_type === 909;
      const cat = zoneCategory(z.name || '');
      items.push(mkItem(red ? 'nofly' : 'ok', z.id, clean(z.name) || zoneAreaLabel(z.area_type), [cat.key !== 'other' ? `${cat.icon} ${cat.label}` : '', zoneAuthorityLabel(z.authority)].filter(Boolean).join(' · '), red ? '#ef4444' : '#22c55e', cat.key !== 'other' ? cat.icon : '', cat.key, ptsBbox(z.rings[0])));
    }
    const now = Date.now();
    // Same trust rule as the map layer. Having told the visitor that temporary restrictions are not
    // being shown because the data is too old, it would be incoherent to keep handing them those
    // restrictions through search — where a result is clickable and flies the map to it.
    for (const n of notamsOk ? notams : []) {
      if (n.start_ms > now || now > n.end_ms || !n.rings[0]?.length) continue;
      items.push(mkItem('notam', n.id, clean(n.description || n.name) || notamKind(n), notamTypeLabel(n.area_type), '#f59e0b', '⚠', `notam:${n.area_type}`, ptsBbox(n.rings[0])));
    }
    return items;
  });
  // Distinct category / NOTAM-type sub-filters present in the currently-scoped data (granular chips).
  const availPalCats = $derived.by(() => {
    const seen = new Map<string, { key: string; label: string; icon: string }>();
    for (const it of indexItems) {
      if (!it.cat || it.cat === 'other' || !searchScope[it.kind] || seen.has(it.cat)) continue;
      if (it.cat.startsWith('notam:')) seen.set(it.cat, { key: it.cat, label: notamTypeLabel(it.cat.slice(6)), icon: '⚠' });
      else seen.set(it.cat, { key: it.cat, label: categoryLabel(it.cat), icon: categoryIcon(it.cat) });
    }
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label, 'tr'));
  });
  // The breakdown chips split into two clusters: zone categories vs NOTAM types (the 'notam:' prefix).
  const palCatZones = $derived(availPalCats.filter((c) => !c.key.startsWith('notam:')));
  const palCatNotams = $derived(availPalCats.filter((c) => c.key.startsWith('notam:')));
  // NOTAM codes grouped by family (Yasak/Kısıtlı/Tehlikeli/Kontrol/Uyarı/Diğer) for the facet popup —
  // otherwise the ~24 raw codes are a flat wall of noise.
  const NOTAM_FAM_ORDER = ['prohibited', 'restricted', 'danger', 'control', 'warning', 'other'];
  const notamGrouped = $derived.by(() => {
    const fams = new Map<string, { label: string; codes: string[] }>();
    for (const nt of availNotamTypes) {
      const f = notamFamily(nt);
      if (!fams.has(f.key)) fams.set(f.key, { label: f.label, codes: [] });
      fams.get(f.key)!.codes.push(nt);
    }
    return [...fams.entries()]
      .sort((a, b) => NOTAM_FAM_ORDER.indexOf(a[0]) - NOTAM_FAM_ORDER.indexOf(b[0]))
      .map(([key, v]) => ({ key, label: v.label, codes: v.codes.sort((a, b) => notamTypeLabel(a).localeCompare(notamTypeLabel(b), 'tr')) }));
  });
  // Fuzzy query match. Every whitespace token must hit the haystack — as a substring (so scattered / out-of-order
  // words still match: "gece kışla" finds "Kışla + Gece uçuşu"), or failing that within a small edit distance of
  // some word (so light typos match: "kisla" → "kışla" after folding). Haystack + query are Turkish-folded by the
  // caller. Substring is the fast path; the costly edit-distance walk runs only for tokens that don't substring-hit.
  function boundedLev(a: string, b: string, max: number): number {
    const la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    let prev = Array.from({ length: lb + 1 }, (_, i) => i);
    for (let i = 1; i <= la; i++) {
      const cur = [i];
      let best = i;
      for (let j = 1; j <= lb; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        const v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        cur[j] = v;
        if (v < best) best = v;
      }
      if (best > max) return max + 1; // whole row already over budget → give up early
      prev = cur;
    }
    return prev[lb];
  }
  function tokenMatch(hay: string, tok: string): boolean {
    if (hay.includes(tok)) return true;
    if (tok.length < 4) return false; // too short to fuzz without matching everything
    const max = tok.length <= 6 ? 1 : 2;
    for (const w of hay.split(/[^\p{L}\p{N}]+/u)) if (w.length >= 3 && boundedLev(w, tok, max) <= max) return true;
    return false;
  }
  function fuzzyMatch(hay: string, q: string): boolean {
    if (!q) return true;
    for (const tok of q.split(/\s+/)) if (tok && !tokenMatch(hay, tok)) return false;
    return true;
  }
  // On-map matches: scoped by type + granular category + query, grouped + capped per group.
  const paletteGroups = $derived.by(() => {
    const q = foldTr(searchQuery.trim());
    const cats = catFilter;
    const match = indexItems.filter(
      (it) => searchScope[it.kind] && (cats.length === 0 || cats.includes(it.cat)) && fuzzyMatch(it.search, q),
    );
    const CAP = 40;
    return INDEX_GROUPS.filter((g) => searchScope[g.kind]).map((g) => {
      const all = match.filter((it) => it.kind === g.kind);
      return { kind: g.kind, label: g.label, total: all.length, items: all.slice(0, CAP), more: Math.max(0, all.length - CAP) };
    }).filter((g) => g.total > 0);
  });
  // Per-chip object counts (respecting the live query AND any active category selection, so a chip's number
  // always matches what it actually lists — a selected category narrows every scope, so flights/places drop
  // to 0 while a zone category is picked). Category counts only tally kinds currently in scope.
  const scopeCounts = $derived.by(() => {
    const q = foldTr(searchQuery.trim());
    const cats = catFilter;
    const m = new Map<string, number>();
    for (const it of indexItems) {
      if (!fuzzyMatch(it.search, q)) continue;
      if (cats.length && !cats.includes(it.cat)) continue;
      m.set(it.kind, (m.get(it.kind) ?? 0) + 1);
    }
    return m;
  });
  const catCounts = $derived.by(() => {
    const q = foldTr(searchQuery.trim());
    const m = new Map<string, number>();
    for (const it of indexItems) {
      if (!it.cat || it.cat === 'other' || !searchScope[it.kind]) continue;
      if (!fuzzyMatch(it.search, q)) continue;
      m.set(it.cat, (m.get(it.cat) ?? 0) + 1);
    }
    return m;
  });
  // Keyboard navigation of the results: one flat, ordered list of everything selectable (places first,
  // then each on-map group in display order) so ↑/↓ walk it and Enter flies to the highlighted row —
  // the palette is fully operable without the mouse.
  type FlatEntry = { kind: 'place'; r: GeoResult } | { kind: 'item'; it: IndexItem };
  const flatResults = $derived.by<FlatEntry[]>(() => {
    const out: FlatEntry[] = [];
    if (searchScope.place) for (const r of searchResults) out.push({ kind: 'place', r });
    for (const g of paletteGroups) for (const it of g.items) out.push({ kind: 'item', it });
    return out;
  });
  let activeIdx = $state(0);
  const activeEntry = $derived(flatResults.length ? flatResults[Math.min(activeIdx, flatResults.length - 1)] : undefined);
  function previewActive(): void {
    const e = activeEntry;
    if (!e) return;
    if (e.kind === 'place') hoverPlace(e.r);
    else hoverItem(e.it);
  }
  async function moveActive(delta: number): Promise<void> {
    const len = flatResults.length;
    if (!len) return;
    activeIdx = (Math.min(activeIdx, len - 1) + delta + len) % len;
    previewActive();
    await tick();
    document.querySelector('.fp-pal-body .fp-index-row.active')?.scrollIntoView({ block: 'nearest' });
  }
  function activateActive(): void {
    const e = activeEntry;
    if (!e) return;
    if (e.kind === 'place') pickResult(e.r);
    else goToItem(e.it);
  }
  const toggleCat = (k: string) => (catFilter = catFilter.includes(k) ? catFilter.filter((x) => x !== k) : [...catFilter, k]);
  // Pill behaviour: plain click toggles that pill; Cmd/Ctrl-click toggles all the OTHER pills (fast
  // "only this" / "all but this").
  function scopeChip(k: keyof typeof searchScope, e: MouseEvent): void {
    if (e.metaKey || e.ctrlKey) for (const s of SCOPES) { if (s.k !== k) searchScope[s.k] = !searchScope[s.k]; }
    else searchScope[k] = !searchScope[k];
  }
  function catChip(k: string, e: MouseEvent): void {
    if (e.metaKey || e.ctrlKey) for (const c of availPalCats) { if (c.key !== k) toggleCat(c.key); }
    else toggleCat(k);
  }
  // Outline an index item on the map (used by both hover-preview and click).
  function highlightItem(it: IndexItem): void {
    if (it.kind === 'flight') highlightFlight(it.id);
    // A photo has no geometry to outline — mark its spot the same way a geocoded place is marked.
    else if (it.kind === 'photo') {
      const p = photoById(it.id);
      if (p) setPathPoint(p.lon, p.lat);
    } else if (it.kind === 'notam') {
      const n = notamById.get(it.id);
      if (n) highlightGeom({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: n.rings.map(closeRing) } });
    } else {
      const z = zoneById.get(it.id);
      if (z) highlightGeom({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: z.rings.map(closeRing) } });
    }
  }
  // Hover-preview: highlight where an item is WITHOUT flying/selecting, so you can disambiguate similar
  // names by just moving the cursor down the list.
  function hoverItem(it: IndexItem): void {
    if (mapReady) highlightItem(it);
  }
  function hoverPlace(r: GeoResult): void {
    if (mapReady) setPathPoint(r.lon, r.lat); // places have no geometry → a point marker
  }
  function hoverEnd(): void {
    clearHighlight();
    clearPathPoint();
  }
  function goToItem(it: IndexItem): void {
    closePalette();
    // A photo's bbox is a single point, so fitBounds on it is degenerate — fly to a sensible zoom
    // instead, then open the lightbox (the same pair of actions its map marker performs).
    if (it.kind === 'photo') {
      const p = photoById(it.id);
      if (!p) return;
      engine?.flyTo([p.lon, p.lat], 15);
      photoView = p;
      return;
    }
    engine?.fitBounds(it.bounds, { padding: 80, duration: 1400, maxZoom: 15 });
    highlightItem(it);
    if (it.kind === 'flight') openFlightDetail(it.id); // picking a flight from search opens its detail card
  }
  // Fly to a flight (fit its whole track) and open its detail card — used by the all-flights table rows.
  function flyToFlight(f: MyFlight): void {
    const pts = f.segments.flat();
    if (pts.length) engine?.fitBounds(ptsBbox(pts), { padding: 80, duration: 1200, maxZoom: 15 });
    openFlightDetail(f.id);
  }
  /**
   * The media table lists BOTH kinds, so a row is a tagged union rather than a bare flight. Photos have
   * no duration / distance / altitude — those cells render '—' and sort to one end (see `val`), which is
   * honest about the gap instead of pretending a photo is a zero-length flight.
   */
  type MediaRow = { kind: 'video'; id: string; f: MyFlight } | { kind: 'photo'; id: string; p: MyPhoto };
  const mediaRowTitle = (r: MediaRow): string => (r.kind === 'video' ? flightTitle(r.f) : r.p.name);
  // Date shown in the Date column, and the string the query searches. A photo's `takenAt` is an unzoned
  // local wall clock ("YYYY-MM-DDTHH:MM:SS"), so its date is a plain slice — no conversion.
  const mediaRowDate = (r: MediaRow): string => (r.kind === 'video' ? r.f.date : (r.p.takenAt?.slice(0, 10) ?? ''));
  // Second line under the title. A flight's title is its session/description, so the CLIP name adds
  // information; a photo's title is already the filename, so repeating it there says nothing — show the
  // containing folder instead.
  const mediaRowSub = (r: MediaRow): string => (r.kind === 'video' ? r.f.clipName : r.p.folder);
  // Rows: filtered by the query, then sorted by the chosen column. Text sorts locale-aware, the rest
  // numerically. Starts from the visible sets, so the panel's filters narrow the table exactly as they
  // narrow the map — the table is never a second, disagreeing view of the same archive.
  const mediaTableRows = $derived.by((): MediaRow[] => {
    const q = foldTr(flightsTableQuery.trim());
    let rows: MediaRow[] = [
      ...visibleFlights.map((f): MediaRow => ({ kind: 'video', id: f.id, f })),
      ...visiblePhotos.map((p): MediaRow => ({ kind: 'photo', id: p.id, p })),
    ];
    if (q) {
      rows = rows.filter((r) => fuzzyMatch(
        foldTr(r.kind === 'video'
          ? `${flightTitle(r.f)} ${r.f.clipName} ${r.f.folder} ${r.f.date}`
          : `${r.p.name} ${r.p.folder} ${r.p.takenAt ?? ''} ${r.p.model ?? ''} ${r.p.raw ? 'raw dng' : 'jpeg jpg'}`),
        q,
      ));
    }
    const dir = flightsSortDir === 'asc' ? 1 : -1;
    const key = flightsSortKey;
    // A photo has no duration/distance/altitude. -1 parks those rows at one end rather than mixing them
    // in among genuinely zero-valued flights.
    const val = (r: MediaRow): string | number => {
      if (key === 'title') return mediaRowTitle(r).toLocaleLowerCase('tr');
      if (key === 'date') return r.kind === 'video' ? (r.f.startTime || r.f.date) : (r.p.takenAt?.replace('T', ' ') ?? '');
      // Time-of-day sorts every capture by its clock time regardless of date, so "all my dawn shots"
      // cluster together across years. -1 keeps unknown-time rows out of 00:00.
      if (key === 'tod') return mediaTime.get(r.id)?.tod ?? -1;
      if (r.kind === 'photo') return -1;
      return r.f[key] ?? 0;
    };
    return rows.sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      return (va < vb ? -1 : va > vb ? 1 : 0) * dir;
    });
  });
  const rowCounts = $derived.by(() => {
    let video = 0;
    for (const r of mediaTableRows) if (r.kind === 'video') video++;
    return { video, photo: mediaTableRows.length - video };
  });
  function sortFlights(key: FlightSortKey): void {
    if (flightsSortKey === key) flightsSortDir = flightsSortDir === 'asc' ? 'desc' : 'asc';
    else {
      flightsSortKey = key;
      flightsSortDir = key === 'title' ? 'asc' : 'desc'; // text A→Z; numbers/dates big→small first
    }
  }
  const sortArrow = (key: FlightSortKey): string => (flightsSortKey !== key ? '' : flightsSortDir === 'asc' ? ' ↑' : ' ↓');
  // Clicking a row goes to that capture: a flight fits its whole track and opens its detail card, a photo
  // flies to its point and opens the lightbox (the same two actions the map markers already offer).
  function goToMediaRow(r: MediaRow): void {
    if (r.kind === 'video') { flyToFlight(r.f); return; }
    engine?.flyTo([r.p.lon, r.p.lat], 15);
    photoView = r.p;
  }

  // --- All-flights table: lazy poster thumbnails + hover-scrub storyboard -----------------------------
  const THUMB_N = 12; // frames per storyboard — matches the detail card + server background fill (one shared cache)
  const THUMB_CONCURRENCY = 3; // cap concurrent decodes — 586 clips would otherwise flood the decoder
  const THUMB_CACHE_MAX = 60; // LRU bound — each entry is ~10 JPEG data-URLs; browsing all 586 would grow forever
  let thumbCache = $state<Record<string, string[]>>({}); // flight.id → storyboard frames (cached per session)
  const thumbLoading = new Set<string>();
  const thumbQueue: MyFlight[] = [];
  let thumbActive = 0;
  let thumbIO: IntersectionObserver | undefined;
  const thumbNode = new Map<Element, MyFlight>();
  function pumpThumbs(): void {
    while (thumbActive < THUMB_CONCURRENCY && thumbQueue.length) {
      const f = thumbQueue.shift()!;
      if (!f.video || thumbCache[f.id] || thumbLoading.has(f.id)) continue;
      thumbLoading.add(f.id);
      thumbActive++;
      // Same server endpoint (ffmpeg, cached, LUT-graded) as the detail card + background fill — n=12 so all
      // three share ONE cache entry. Reliable where the old browser-seek strip silently failed on 4K HEVC.
      fetch(`/api/flight-thumbs?p=${encodeURIComponent(f.video)}&n=${THUMB_N}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d: { frames?: { t: number; url: string }[] } | null) => {
          if (d?.frames?.length) thumbCache = { ...thumbCache, [f.id]: d.frames.map((fr) => fr.url) };
        })
        .catch(() => {})
        .finally(() => {
          thumbLoading.delete(f.id);
          thumbActive--;
          // Evict the oldest-inserted flights once over the cap (keys keep first-seen order). (perf#6)
          const keys = Object.keys(thumbCache);
          if (keys.length > THUMB_CACHE_MAX) {
            const next = { ...thumbCache };
            for (const k of keys.slice(0, keys.length - THUMB_CACHE_MAX)) delete next[k];
            thumbCache = next;
          }
          pumpThumbs();
        });
    }
  }
  function queueThumb(f: MyFlight): void {
    if (!f.video || thumbCache[f.id] || thumbLoading.has(f.id) || thumbQueue.includes(f)) return;
    thumbQueue.push(f);
    pumpThumbs();
  }
  // Lazy: extract a row's storyboard only once it scrolls near view (one shared observer over all rows).
  function lazyThumb(node: HTMLElement, f: MyFlight) {
    thumbNode.set(node, f);
    thumbIO ??= new IntersectionObserver(
      (ents) => { for (const e of ents) if (e.isIntersecting) { const ff = thumbNode.get(e.target); if (ff) queueThumb(ff); } },
      { rootMargin: '250px' },
    );
    thumbIO.observe(node);
    return {
      update(nf: MyFlight) { thumbNode.set(node, nf); },
      destroy() { thumbIO?.unobserve(node); thumbNode.delete(node); },
    };
  }
  // Hover-scrub: moving across a row's thumbnail scrubs its storyboard; not hovering shows the mid frame.
  let hoverThumb = $state<{ id: string; i: number } | null>(null);
  function scrubThumb(f: MyFlight, ev: MouseEvent): void {
    const frames = thumbCache[f.id];
    if (!frames?.length) return;
    const el = ev.currentTarget as HTMLElement;
    const frac = Math.min(0.999, Math.max(0, (ev.clientX - el.getBoundingClientRect().left) / el.offsetWidth));
    hoverThumb = { id: f.id, i: Math.floor(frac * frames.length) };
  }
  function thumbFrame(f: MyFlight): string {
    const frames = thumbCache[f.id];
    if (!frames?.length) return '';
    if (hoverThumb?.id === f.id) {
      const h = frames[Math.min(hoverThumb.i, frames.length - 1)];
      if (h) return h;
    }
    return frames[Math.floor(frames.length / 2)] || frames.find(Boolean) || ''; // poster = mid; else first ready
  }
  // Resizable table panel (drag the bottom-left grip: leftward = wider, downward = taller scroll area).
  let tableW = $state(500);
  let tableScrollH = $state(380);
  // Resizable flight detail card (drag the bottom-right grip). Width grows rightward (card is left-anchored);
  // the scrolling body's cap grows downward. Persisted across opens so the user's chosen size sticks.
  let flightCardW = $state(300);
  let flightBodyMaxH = $state(460);
  function resizeFlightCard(node: HTMLElement) {
    let sx = 0, sy = 0, sw = 0, sh = 0;
    const move = (e: PointerEvent) => {
      flightCardW = Math.max(260, Math.min(620, sw + (e.clientX - sx)));
      flightBodyMaxH = Math.max(140, Math.min(window.innerHeight - 170, sh + (e.clientY - sy)));
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    const down = (e: PointerEvent) => {
      if (e.button !== 0) return;
      e.stopPropagation(); // don't also start a panel drag
      e.preventDefault();
      sx = e.clientX; sy = e.clientY; sw = flightCardW; sh = flightBodyMaxH;
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    };
    node.addEventListener('pointerdown', down);
    return { destroy() { node.removeEventListener('pointerdown', down); window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); } };
  }
  function resizeTable(node: HTMLElement) {
    let sx = 0, sy = 0, sw = 0, sh = 0;
    // Track the drag on WINDOW (not the tiny grip) so moving the pointer off the 16px grip keeps resizing.
    const move = (e: PointerEvent) => {
      tableW = Math.max(340, Math.min(820, sw + (sx - e.clientX)));
      tableScrollH = Math.max(160, Math.min(window.innerHeight - 150, sh + (e.clientY - sy)));
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    const down = (e: PointerEvent) => {
      if (e.button !== 0) return;
      e.stopPropagation(); // don't also start a panel drag
      e.preventDefault();
      sx = e.clientX; sy = e.clientY; sw = tableW; sh = tableScrollH;
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    };
    node.addEventListener('pointerdown', down);
    return { destroy() { node.removeEventListener('pointerdown', down); window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); } };
  }
  function closePalette(): void {
    clearTimeout(paletteBlurTimer);
    searchFocused = false;
    (document.activeElement as HTMLElement | null)?.blur?.();
  }

  // SHGM zone group (item #30/#31): one parent that toggles the three official layers together.
  let shgmAll = $derived(layers.nofly && layers.allowed && layers.notam);
  let shgmSome = $derived(layers.nofly || layers.allowed || layers.notam);
  function toggleShgm(on: boolean): void {
    layers.nofly = on;
    layers.allowed = on;
    layers.notam = on;
  }

  // Wind display helper: engine works in m/s; the UI shows km/h by default.
  const fmtWind = (ms: number): string => (windUnit === 'kmh' ? `${(ms * 3.6).toFixed(0)} km/h` : `${ms.toFixed(1)} m/s`);
  let basemap = $state<'streets' | 'satellite'>(S0.basemap); // planning defaults to satellite imagery
  let is3D = $state((V0.pitch ?? 0) >= 8); // terrain relief + 3D buildings — restored from the last view's tilt

  // Persist every panel setting whenever any of them changes (reading each one here tracks it). The camera
  // (center/zoom/pitch/bearing) is saved separately, on moveend — see onMount. Spreads snapshot the reactive
  // proxies to plain objects so JSON.stringify serialises them cleanly.
  $effect(() => {
    lsSave(LS_SETTINGS, {
      basemap, showFilters, showFlightsTable,
      searchScope: { ...searchScope }, layers: { ...layers },
      wxShow: { ...wxShow }, wxHourOffset, effective,
      terrain: { ...terrain }, poi, poiSymbols, poiNames,
      placeLabels: { ...placeLabels }, showFlights, showPhotos, flightViz: { ...flightViz }, windUnit,
      typeFacet: { ...typeFacet }, fmtFacet: { ...fmtFacet },
      phaseFacet: { ...phaseFacet }, dowFacet: { ...dowFacet }, monthFacet: { ...monthFacet },
      hourFrom, hourTo, numRange: { ...numRange },
    });
  });

  function toggle3D(on: boolean): void {
    is3D = on;
    engine?.set3D(on);
  }
  // Rich weather-point tooltip (shown while hovering a wx marker) — a dedicated popup so it doesn't fight
  // the zone/flight hover popup.
  let windBusy = $state(false);
  let windAbort: AbortController | null = null;
  let windDebounce: ReturnType<typeof setTimeout> | undefined;

  const LEVEL: Record<string, { label: string; icon: string; cls: string }> = {
    good: { label: 'Suitable to fly', icon: '✓', cls: 'good' },
    marginal: { label: 'Marginal — caution', icon: '!', cls: 'marginal' },
    nofly: { label: 'Do not fly', icon: '✕', cls: 'nofly' },
  };
  const flagged = (metric: string) => assessment?.weather.reasons.some((r) => r.metric === metric) ?? false;

  // ---- data layers ------------------------------------------------------------------------------

  function closeRing(r: number[][]): GeoJSON.Position[] {
    return (r.length && (r[0][0] !== r[r.length - 1][0] || r[0][1] !== r[r.length - 1][1]) ? [...r, r[0]] : r) as GeoJSON.Position[];
  }
  // Approx polygon area (shoelace, deg²) — only used to rank overlaps so the SMALLEST zone renders on
  // top and wins the hover, otherwise a small zone inside a big one is unreachable.
  function ringArea(ring: number[][]): number {
    let a = 0;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) a += (ring[j][0] + ring[i][0]) * (ring[j][1] - ring[i][1]);
    return Math.abs(a / 2);
  }
  const zoneArea = (rings: number[][][]): number => rings.reduce((s, r) => s + ringArea(r), 0);

  // Largest first → smallest painted last (on top) and picked first on hover.
  function bySmallestOnTop<T extends GeoJSON.Feature>(feats: T[]): T[] {
    return feats.sort((a, b) => Number(b.properties?.area ?? 0) - Number(a.properties?.area ?? 0));
  }
  function zoneFC(kind: 'nofly' | 'ok'): GeoJSON.FeatureCollection {
    const want = kind === 'nofly' ? 909 : 908;
    const label = kind === 'nofly' ? t('No-fly (red)') : t('Allowed (green)');
    const feats: GeoJSON.Feature[] = zones
      .filter((z) => z.area_type === want)
      .map((z) => {
        const cat = zoneCategory(z.name || '');
        return {
          type: 'Feature' as const,
          geometry: { type: 'Polygon' as const, coordinates: z.rings.map(closeRing) },
          properties: {
            cls: kind,
            id: z.id,
            name: clean(z.name) || label,
            kind: label,
            auth: zoneAuthorityLabel(z.authority), // "who designated it" — its own tooltip row
            area: zoneArea(z.rings),
            authority: z.authority ?? -1, // filterable facet
            category: cat.key, // why-restricted sub-breakdown facet
            catLabel: `${cat.icon} ${cat.label}`,
          },
        };
      });
    return { type: 'FeatureCollection', features: bySmallestOnTop(feats) };
  }
  function notamFC(): GeoJSON.FeatureCollection {
    const now = Date.now();
    const feats: GeoJSON.Feature[] = notams
      .filter((n) => n.start_ms <= now && now <= n.end_ms)
      .map((n) => ({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: n.rings.map(closeRing) },
        properties: notamTipProps(n),
      }));
    return { type: 'FeatureCollection', features: bySmallestOnTop(feats) };
  }
  // A symbol per zone that has a drone-relevant reason (military / dam / SİT / nuclear / …), placed at
  // the zone's vertex-average and carrying the category icon + name for the map marker and tooltip.
  function ringCentroid(ring: number[][]): [number, number] {
    let x = 0;
    let y = 0;
    for (const [lon, lat] of ring) {
      x += lon;
      y += lat;
    }
    return [x / ring.length, y / ring.length];
  }
  function poiZoneFC(): GeoJSON.FeatureCollection {
    const feats: GeoJSON.Feature[] = [];
    for (const z of zones) {
      const cat = zoneCategory(z.name || '');
      if (!categoryHasMapSymbol(cat.key) || !z.rings[0]?.length) continue; // only categories with an icon
      feats.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: ringCentroid(z.rings[0]) },
        properties: { sym: cat.icon, name: z.name || cat.label, kindLabel: cat.label, category: cat.key },
      });
    }
    return { type: 'FeatureCollection', features: feats };
  }
  // Heliport isn't a zone category (it's a basemap aerodrome class) but shares the same emoji-icon scheme.
  const HELIPORT_ICON = '🚁';

  // NOTAM active-window clock.
  //
  // The raw NOTAM layer, the merged geometry and the search index all filter by `Date.now()` at the
  // moment pushLayers() runs, and nothing revisited it: the evaluation stood for as long as the
  // dataset id did, which is up to the 12 h NOTAM trust window. A restriction that BEGAN during that
  // window was missing from a map still telling the visitor its NOTAM layer could be trusted.
  //
  // So wake at the exact edge instead of polling. `notamEpoch` is what the search index reads to
  // re-derive; pushLayers() rebuilds the layer and invalidates the merge on its own.
  let notamEpoch = $state(0);
  let notamTimer: ReturnType<typeof setTimeout> | undefined;
  const NOTAM_WAKE_CAP_MS = 6 * 60 * 60 * 1000; // setTimeout saturates near 24.8 days; also re-checks a suspended tab
  function scheduleNotamWake(): void {
    clearTimeout(notamTimer);
    const at = nextNotamChange(notams, Date.now());
    if (at == null) return; // no edge left in this dataset — nothing to wake for
    // +250ms so the callback lands strictly PAST the edge rather than on it.
    const delay = Math.min(Math.max(at - Date.now(), 0) + 250, NOTAM_WAKE_CAP_MS);
    notamTimer = setTimeout(() => {
      if (Date.now() >= at) { notamEpoch++; pushLayers(); } // pushLayers re-arms; a capped wake just re-arms
      else scheduleNotamWake();
    }, delay);
  }
  // (emoji map-icons registered by the engine's buildLayers)
  function pushLayers(): void {
    ptime('pushLayers (zone/notam/poi FC build + setData)', () => {
      zoneById = new Map(zones.map((z) => [z.id, z]));
      notamById = new Map(notams.map((n) => [n.id, n]));
      zoneGrid = new RingGrid(zones);
      notamGrid = new RingGrid(notams);
      engine?.setZoneData(zoneFC('nofly'), zoneFC('ok'));
      engine?.setNotamData(notamsOk ? notamFC() : { type: 'FeatureCollection', features: [] });
      engine?.setPoiData(poiZoneFC());
      effStamp = -1; // data changed → the cached merge is stale
      effReady = false; // until the new merge lands, effective mode falls back to raw (zones never blank out)
      effFailed = false; // a new dataset is a new chance for the worker
    });
    scheduleNotamWake(); // re-arm on the new dataset's edges (also the re-arm after a wake fired)
    void ensureEffective(); // eagerly (re)build the effective geometry in the worker so the switch stays instant
  }
  // The effective merge is EAGER + CACHED + off-thread: as soon as zone data is (re)loaded we kick a Web
  // Worker to build the merged geometry, so by the time the user flips to Effective mode it's already done
  // — instant, and the ~3 s country-wide union never freezes the UI. The cache is keyed by the dataset id
  // (updatedAt), so it's reused across toggles/filters and only rebuilt when the DATA changes (never
  // stale: a result whose stamp no longer matches is discarded and recomputed).
  let effWorker: Worker | null = null;
  let effReqStamp = -1; // dataset id of the in-flight worker request (-1 = idle)
  async function ensureEffective(): Promise<void> {
    if (!mapReady) return;
    const stamp = updatedAt ?? 0;
    if (effStamp === stamp || effReqStamp === stamp) return; // already built or building for this dataset
    if (!zones.length && !notams.length) return;
    effReqStamp = stamp;
    effBusy = true;
    // A merge persisted for THIS dataset? Use it — skips the ~3 s union AND the ~20 MB worker postMessage.
    const persisted = await readMerge(stamp);
    if ((updatedAt ?? 0) !== stamp) { effReqStamp = -1; effBusy = false; return; } // data changed during the read
    if (persisted) {
      effReqStamp = -1;
      effBusy = false;
      effGeom = persisted;
      effStamp = stamp;
      applyEffData();
      effReady = true;
      return;
    }
    if (!effWorker) {
      const w = new MergeWorker();
      w.onmessage = (e: MessageEvent<{ geom: EffectiveGeom; stamp: number; validUntil: number | null }>) => {
        effReqStamp = -1;
        effBusy = false;
        const cur = updatedAt ?? 0;
        if (e.data.stamp !== cur) return void ensureEffective(); // data changed mid-merge → rebuild, discard stale
        effGeom = e.data.geom;
        effStamp = e.data.stamp;
        applyEffData();
        effReady = true; // merge done → the visibility effect can now switch from raw to effective
        void writeMerge(e.data.stamp, e.data.geom, e.data.validUntil); // persist so the next load skips the recompute
      };
      // Without these, a worker that dies leaves `effReqStamp` latched at this dataset's stamp, so
      // the guard at the top of `ensureEffective` returns early forever and no retry is possible;
      // `effBusy` spins next to "Zone display" for the rest of the session. Reachable by the merge
      // chunk 404ing after a redeploy while a client holds the old index.html, or by a CSP without
      // `worker-src`. Falling back to the raw zones is safe — they show MORE, not less — but it has
      // to say so rather than look like a load that never finished.
      const died = (e: unknown): void => {
        console.error('[planning] merge worker failed; effective mode falls back to the raw zones', e);
        effReqStamp = -1;
        effBusy = false;
        effFailed = true;
        effReady = false;
      };
      w.onerror = died;
      w.onmessageerror = died;
      effWorker = w;
    }
    effWorker.postMessage({ zones, notams, now: Date.now(), stamp });
  }
  // Push the cached footprints into the eff-* sources (tagged for setFilter). Existing layer filters
  // (facets) apply automatically — no recompute.
  function applyEffData(): void {
    if (!effGeom) return;
    engine?.setEffectiveData(effClassFC(effGeom, 'nofly'), effClassFC(effGeom, 'notam'), effClassFC(effGeom, 'ok'));
  }
  // Whether an effective footprint is currently shown — mirrors the eff-* layer filters, so the hover
  // flood-fill (effComponentAt) groups only VISIBLE adjacent footprints and stays consistent with filters.
  function effVisible(f: EffFootprint): boolean {
    if (f.cls === 'notam') return notamVisible(filterState, f.ntype);
    return zoneVisible(filterState, f.cls, f.category, f.authority);
  }
  // The same rules over RAW records, for the effective-mode tooltip: `effVisible` decides which
  // footprints are drawn, these decide which rows the tooltip may mention, and one is the pixels
  // while the other is the sentence explaining them.
  const zoneShownRaw = (z: Zone): boolean => zoneRecordVisible(filterState, z, zoneCategory(z.name || '').key);
  const notamShownRaw = (n: Notam): boolean => notamRecordVisible(filterState, n);
  // Discover the authority/NOTAM-type facets present in the data; default new ones to visible.
  function computeFacets(): void {
    const auths = [...new Set(zones.map((z) => z.authority ?? -1))].sort((a, b) => a - b);
    const ntypes = [...new Set(notams.map((n) => n.area_type || '?'))].sort();
    for (const a of auths) if (!(String(a) in authFacet)) authFacet[String(a)] = true;
    for (const nt of ntypes) if (!(nt in notamFacet)) notamFacet[nt] = true;
    availAuthorities = auths;
    availNotamTypes = ntypes;
    // Single pass over zones (was three): bucket categories by kind + collect the site-symbol categories.
    const noflySet = new Set<string>(), okSet = new Set<string>(), siteSet = new Set<string>();
    for (const z of zones) {
      const key = zoneCategory(z.name || '').key;
      if (z.area_type === 909) noflySet.add(key);
      else if (z.area_type === 908) okSet.add(key);
      if (categoryHasMapSymbol(key)) siteSet.add(key);
    }
    // Category sub-breakdown per kind (order: most-specific reasons first, "Diğer" last).
    const catSort = (a: string, b: string) => (a === 'other' ? 1 : b === 'other' ? -1 : a.localeCompare(b));
    const nofly = [...noflySet].sort(catSort);
    const ok = [...okSet].sort(catSort);
    for (const c of nofly) if (!(c in catFacet.nofly)) catFacet.nofly[c] = true;
    for (const c of ok) if (!(c in catFacet.ok)) catFacet.ok[c] = true;
    availCats = { nofly, ok };
    // Site-symbol categories = zone categories that have a dedicated map icon (poi-zone features).
    const siteCats = [...siteSet].sort();
    for (const c of siteCats) if (!(c in siteCatFacet)) siteCatFacet[c] = true;
    availSiteCats = siteCats;
  }
  // Quick "select all / none" for a facet — lets the user clear everything then pick one fast.
  function setCatAll(kind: 'nofly' | 'ok', val: boolean): void {
    for (const c of availCats[kind]) catFacet[kind][c] = val;
  }
  function setNotamAll(val: boolean): void {
    for (const nt of availNotamTypes) notamFacet[nt] = val;
  }
  function setNotamFam(codes: string[], val: boolean): void {
    for (const nt of codes) notamFacet[nt] = val;
  }
  function setAuthAll(val: boolean): void {
    for (const a of availAuthorities) authFacet[String(a)] = val;
  }
  function setSiteCatAll(val: boolean): void {
    for (const c of availSiteCats) siteCatFacet[c] = val;
  }

  // Outline the FULL zone under the cursor (looked up by id → complete geometry, not the tile-clipped one).
  // The hover highlight rebuilt the full ring geometry (closeRing over every ring) on EVERY hover frame,
  // even while hovering the SAME zone. Cache the built feature per zone/NOTAM object (WeakMap → auto-drops
  // when the dataset reloads), so following a track/zone with the cursor is allocation-free.
  const highlightGeomCache = new WeakMap<object, GeoJSON.Feature>();
  function highlightFeature(cls: string, id: string): void {
    const item = cls === 'notam' ? notamById.get(id) : zoneById.get(id);
    if (!item) { engine?.setHighlight(null); return; }
    let feat = highlightGeomCache.get(item);
    if (!feat) {
      feat = { type: 'Feature', geometry: { type: 'Polygon', coordinates: item.rings.map(closeRing) }, properties: {} };
      highlightGeomCache.set(item, feat);
    }
    engine?.setHighlight(feat);
  }
  function clearHighlight(): void {
    engine?.setHighlight(null);
  }
  // Outline an arbitrary geometry (used for effective-mode merged blocks + flight tracks).
  function highlightGeom(feature: GeoJSON.Feature | null): void {
    engine?.setHighlight(feature);
  }

  // Distinct high-contrast colour per flight so overlapping paths never blur into one — assigned by
  // order and kept stable in a per-id map (used by the line, dot, label, curtain, and hover highlight).
  const FLIGHT_COLORS = ['#22d3ee', '#f59e0b', '#a855f7', '#84cc16', '#ec4899', '#f43f5e', '#14b8a6', '#eab308', '#38bdf8', '#fb923c'];
  let flightColorById = new Map<string, string>();
  const flightColor = (id: string): string => flightColorById.get(id) ?? '#22d3ee';

  // The user's own flights — scanned + parsed from the archive by the dev/preview server (/api/flights).
  function pushFlights(): void {
    const shown = visibleFlights;
    ptime(`pushFlights (${shown.length}/${myFlights.length} flights → FCs + deck paths)`, () => {
      // Colours are assigned over ALL flights, not the visible subset: keying them to the filtered
      // list would re-shuffle every track's colour each time a filter moved.
      flightColorById = new Map(myFlights.map((f, i) => [f.id, FLIGHT_COLORS[i % FLIGHT_COLORS.length]]));
      const withSeg = shown.map((f) => ({ f, segs: f.segments.filter((s) => s.length > 1) })).filter((x) => x.segs.length);
      const props = (f: MyFlight) => ({ id: f.id, title: flightTitle(f), sub: f.videoDesc ? f.name : '', clipName: f.clipName, date: f.date, color: flightColor(f.id) });
      // MultiLineString per flight (clip) — a clip's own legs stay separate if its GPS jumped.
      const trackFC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: withSeg.map(({ f, segs }) => ({ type: 'Feature', geometry: { type: 'MultiLineString', coordinates: segs }, properties: props(f) })) };
      const dotsFC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: withSeg.map(({ f, segs }) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: segs[0][0] }, properties: props(f) })) };
      engine?.setFlightData(trackFC, dotsFC);
      engine?.setFlightPaths(flightPaths(shown));
    });
  }
  // The airborne routes for the clean deck.gl 3D line: each flight's samples as contiguous legs of
  // [lon, lat, relAltMetres], split at GPS gaps (same gap rule as the ground segments). The leg-splitting
  // walks every raw sample of every flight — the bulk of pushFlights' cost (measured ~800 ms for 653 flights,
  // re-paid on every scan-stream tick). Memoise by flight id: a clip's GPS never changes, so during streaming
  // only the newly-arrived flights build legs; the colour (index-based) is re-read fresh each call, cheaply.
  const legsCache = new Map<string, [number, number, number][][]>();
  function flightPaths(flights: MyFlight[]): FlightPath[] {
    return flights.map((f) => {
      let legs = legsCache.get(f.id);
      if (!legs) {
        legs = [];
        let cur: [number, number, number][] = [];
        const s = f.samples;
        for (let i = 0; i < s.length; i++) {
          const p = s[i];
          if (i > 0) {
            const a = s[i - 1];
            if (Math.abs(a[0] - p[0]) > 0.02 || Math.abs(a[1] - p[1]) > 0.02) { if (cur.length > 1) legs.push(cur); cur = []; }
          }
          cur.push([p[0], p[1], p[3] ?? 0]);
        }
        if (cur.length > 1) legs.push(cur);
        legsCache.set(f.id, legs);
      }
      return { id: f.id, color: flightColor(f.id), legs };
    });
  }
  // The user's own photos — scanned from the archive's EXIF (/api/flights → photos). One marker each; the
  // FC carries only {id, archived} so the heavy thumbnail stays in `myPhotos` (looked up by id for the tip).
  function pushPhotos(): void {
    // `showPhotos` gates the LAYER; the filters gate the SET. Kept separate so switching the layer off
    // doesn't empty the media table, exactly as `showFlights` doesn't.
    const shown = showPhotos ? visiblePhotos : [];
    const photosFC: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      // Filtered the same way flights are — an empty FC simply draws no markers, so neither the layer
      // toggle nor the type filter needs a new engine method.
      features: shown.map((p) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
        properties: { id: p.id, archived: !p.present },
      })),
    };
    engine?.setPhotoData(photosFC);
  }
  // (The airborne route + altitude curtain are drawn by the deck.gl overlay — see FlightDeck via
  // setFlightPaths(). The former MapLibre fill-extrusion feeds for them, curtainFC/airRouteFC + their
  // eachFlightSegment helper, were removed: the engine ignored them and they cost a full pass over every
  // flight on each update. deck.gl draws both, smoother and terrain-following.)
  // `force` re-scans the archive server-side (?refresh) so footage added AFTER the server started is
  // picked up — without it the server returns its cached scan and new clips never appear.
  let flightsSeq = 0; // guards against an in-flight fetch resolving after a newer one (timer vs manual)
  async function fetchFlights(force = false): Promise<void> {
    if (!feat.archive) return; // no local archive on this deployment — /api/flights does not exist
    const seq = ++flightsSeq;
    try {
      const r = await fetch(force ? '/api/flights?refresh' : '/api/flights');
      if (!r.ok) return; // endpoint absent (a plain static build) → no flights layer, silently
      const j = (await r.json()) as { flights?: MyFlight[]; photos?: MyPhoto[]; scanning?: boolean };
      if (seq !== flightsSeq) return; // a newer fetch started while we awaited → drop this stale result
      applyFlights(j);
      if (j.scanning && !scanPolling) { scanPolling = true; scanDismissed = false; lastIncAt = Date.now(); void pollScanStatus(); }
      startThumbPolling(); // the server fills storyboards in the background (even on a warm start) — track it

    } catch {
      /* not running under the dev/preview server — skip */
    }
  }

  // Apply a /api/flights payload to the map. setData only SWAPS source data — it never moves the camera,
  // so calling this repeatedly as the scan streams results leaves the user's zoom/center/pitch untouched.
  function applyFlights(j: { flights?: MyFlight[]; photos?: MyPhoto[] }): void {
    myFlights = Array.isArray(j.flights) ? j.flights : [];
    myPhotos = Array.isArray(j.photos) ? j.photos : [];
    // Both kinds reach the map through the `visibleFlights` / `visiblePhotos` effect below, so
    // newly-scanned media and a changed filter take the exact same path — no way for one to render a
    // set the other wouldn't.
  }
  // Silently pull the (growing) result set and render it — no view change, no scan re-trigger. Shares the
  // `flightsSeq` guard so an in-flight pull that resolves AFTER a newer fetch (e.g. a manual force-refresh)
  // can't clobber the fresher result with a stale/partial snapshot.
  async function pullFlightsSilently(): Promise<void> {
    if (!feat.archive) return;
    const seq = ++flightsSeq;
    try {
      const r = await fetch('/api/flights');
      if (r.ok && seq === flightsSeq) applyFlights((await r.json()) as { flights?: MyFlight[]; photos?: MyPhoto[] });
    } catch {
      /* ignore — the next poll retries */
    }
  }
  // Follow the background scan: poll status for the console + bar, and pull the growing results (throttled)
  // so points STREAM onto the map as they're found. Single loop (scanPolling guard prevents duplicates).
  let scanPolling = false;
  let scanDismissed = false; // user closed the console mid-scan → don't let the next poll tick re-open it
  let lastIncAt = 0;
  async function pollScanStatus(): Promise<void> {
    clearTimeout(scanPollTimer);
    try {
      if (!feat.archive) { scanPolling = false; return; }
      const r = await fetch('/api/scan-status');
      if (!r.ok) { scanPolling = false; scanStatus = null; return; }
      const s = (await r.json()) as ScanStatus;
      if (!scanDismissed) scanStatus = s;
      if (s.scanning) {
        if (Date.now() - lastIncAt > 1500) { lastIncAt = Date.now(); void pullFlightsSilently(); } // stream in
        scanPollTimer = setTimeout(() => void pollScanStatus(), 700);
        return;
      }
      scanPolling = false;
      await pullFlightsSilently(); // final set
      scanPollTimer = setTimeout(() => { scanStatus = null; }, 4000); // let the "complete" note linger, then hide
    } catch {
      scanPolling = false;
      scanStatus = null;
    }
  }
  // Poll the server's storyboard background-fill while it has work; drives the console's storyboard progress row.
  function startThumbPolling(): void { if (!feat.archive || thumbPolling) return; thumbPolling = true; void pollThumbStatus(); }
  async function pollThumbStatus(): Promise<void> {
    clearTimeout(thumbPollTimer);
    try {
      if (!feat.archive) { thumbPolling = false; return; }
      const r = await fetch('/api/thumb-status');
      if (!r.ok) { thumbPolling = false; thumbStatus = null; return; }
      const s = (await r.json()) as ThumbStatus;
      thumbStatus = s;
      if (s.pending > 0 || s.active > 0) {
        thumbPollTimer = setTimeout(() => void pollThumbStatus(), 2000);
      } else {
        thumbPolling = false;
        thumbPollTimer = setTimeout(() => { thumbStatus = null; }, 4000); // let "ready" linger, then hide
      }
    } catch {
      thumbPolling = false;
      thumbStatus = null;
    }
  }

  // Manual refresh — force a fresh zone fetch (bypass the stale check) plus a flights RE-SCAN.
  async function manualRefresh(): Promise<void> {
    if (refreshing) return;
    refreshing = true;
    try {
      await Promise.all([
        track(t('Loading airspace zones'), loadData(true)),
        ...(feat.archive ? [track(t('Scanning flights'), fetchFlights(true))] : []),
      ]);
    } finally {
      refreshing = false;
    }
  }

  // Serve the whole country from the IndexedDB cache instantly (map opens FULL, no per-view fetch, no
  // zoom gate); refresh in the background when stale. First-ever open (no cache) shows an indexing note.
  function applyDataset(d: CachedDataset): void {
    const { drawn, withheld, live } = drawableNotams(d);
    notams = drawn;
    // Identifies what is drawn, so a merge built with these NOTAMs is not reused without them.
    updatedAt = d.builtAt + withheld;
    zonesAt = d.zonesAt ?? null;
    zonesOk = zonesFresh(d);
    notamsOk = d.notamsLoaded;
    notamsWithheld = withheld;
    liveNotams = live;
  }

  async function loadData(force = false): Promise<void> {
    const t = plog('loadData: reading IndexedDB cache');
    const cached = await readCache();
    if (cached) {
      zones = cached.zones;
      applyDataset(cached);
      pushLayers();
      computeFacets();
      plog(`loadData: cache hit (${cached.zones.length} zones) → rendered`, t);
    } else {
      plog('loadData: cache MISS → network fetch (first-ever open)', t);
    }
    if (cached && !isStale(cached) && !force) return;
    // Stale by the clock is not the same as out of date. Ask the manifest (723 bytes) whether a new
    // bake exists before pulling the country again; if none does, record that we looked and stop.
    // The data stays as old as it is — so a withdrawn NOTAM layer stays withdrawn, correctly.
    if (cached && !force) {
      const same = await revalidate(cached);
      if (same) {
        const before = updatedAt;
        applyDataset(same);
        if (updatedAt !== before) {
          pushLayers();
          computeFacets();
        }
        plog('loadData: manifest unchanged → no refetch', t);
        return;
      }
    }
    dataAbort?.abort();
    const ac = new AbortController();
    dataAbort = ac;
    indexing = !cached; // only block-ish the very first time
    indexProgress = 0;
    dataError = null;
    try {
      const d = await refreshDataset(ac.signal, (n) => (indexProgress = n));
      if (ac.signal.aborted) return;
      zones = d.zones;
      applyDataset(d);
      pushLayers();
      computeFacets();
    } catch (e) {
      if (!ac.signal.aborted && !zones.length) dataError = e instanceof Error ? e.message : String(e);
    } finally {
      if (dataAbort === ac) indexing = false;
    }
  }

  // ---- launch-point assessment ------------------------------------------------------------------

  async function assess(p: Point): Promise<void> {
    // Guard at the SOURCE, not just at the markup: with the card off, running this would still
    // abort a request, hit the weather API and mutate state for something nobody can see.
    if (!feat.assessment) return;
    assessAbort?.abort();
    const ac = new AbortController();
    assessAbort = ac;
    assessPoint = p;
    assessErr = null;
    assessment = null;
    weather = null;
    elevation = null;
    // Zone/NOTAM info is already loaded locally → show it IMMEDIATELY (even inside a zone), no wait,
    // no extra click. Weather + verdict fill in when the fetch returns.
    const now = Date.now();
    pointZones = zoneGrid.at(p.lat, p.lon);
    pointNotams = notamGrid.at(p.lat, p.lon).filter((n) => isActive(n, now));
    engine?.setAssessMarker([p.lon, p.lat]);
    // Click-to-assess weather shares Open-Meteo's per-IP budget with the field grid. Honour the SAME 429
    // backoff so a burst of launch checks can't blow the rate limit — the zone/NOTAM verdict already showed
    // without weather, so this only defers the wind/precip line.
    if (Date.now() < wxCooldownUntil) { assessErr = t('Weather is rate-limited right now; try again shortly.'); return; }
    try {
      const w = await fetchWeather(p.lat, p.lon, ac.signal);
      if (ac.signal.aborted) return;
      if (!w) throw new Error(t('Weather is unavailable for this location.'));
      wxBudgetAdd(0.05); // one-location current-only fetch — tiny, but count it so click-storms still budget
      weather = w.weather;
      elevation = w.elevation;
      assessment = assessLaunch(w.weather, p, now, zones, notams);
    } catch (e) {
      // A rate-limit here trips the shared cooldown, so the field grid backs off in lockstep with clicks.
      if (e instanceof Error && /\b429\b/.test(e.message)) wxCooldownUntil = Date.now() + 20000;
      if (!ac.signal.aborted) assessErr = e instanceof Error ? e.message : String(e);
    }
  }
  function closeAssess(): void {
    assessAbort?.abort();
    assessPoint = null;
    assessment = null;
    weather = null;
    elevation = null;
    pointZones = [];
    pointNotams = [];
    assessErr = null;
    engine?.setAssessMarker(null);
  }

  // ---- weather wind-arrow grid ------------------------------------------------------------------

  type WxPoint = { lon: number; lat: number; wind: number; gust: number; dir: number; temp: number; precip: number };
  type OMHourly = {
    time?: string[];
    wind_speed_10m?: number[];
    wind_gusts_10m?: number[];
    wind_direction_10m?: number[];
    temperature_2m?: number[];
    precipitation?: number[];
  };
  type OMLoc = {
    latitude: number;
    longitude: number;
    current?: { wind_speed_10m?: number; wind_gusts_10m?: number; wind_direction_10m?: number; temperature_2m?: number; precipitation?: number };
    hourly?: OMHourly;
  };
  let wxData: WxPoint[] = []; // the FETCHED grid (row-major over wxGrid), cached for interpolation/re-render
  // Fetched grid geometry (padded bbox + dims) — lets us BILINEARLY INTERPOLATE between the sampled points
  // for a continuous temperature field + faint in-between wind arrows, and cache across small pans.
  type WxGrid = { rows: number; cols: number; s: number; n: number; w: number; e: number };
  let wxGrid: WxGrid | null = null;
  let wxCacheKey = ''; // the lattice-snapped bbox + forecast offset the cached grid was fetched for
  // Rate-limit backoff: after a 429 we stop fetching for a cooldown window (rapid panning would otherwise
  // hammer the API) and schedule ONE self-heal retry so weather reappears without the user having to pan.
  let wxCooldownUntil = 0;
  let wxRetryTimer: ReturnType<typeof setTimeout> | undefined;
  // Set when the self-imposed daily request budget is spent — weather runs cache-only until UTC midnight.
  let wxPaused = $state(false);

  // Bilinear interpolation of the sampled grid at (lon,lat). Wind is interpolated via u/v components so
  // direction blends correctly through calms. Returns null outside the fetched grid.
  function wxAt(lon: number, lat: number): WxPoint | null {
    const g = wxGrid;
    if (!g || wxData.length !== g.rows * g.cols) return null;
    const fx = ((lon - g.w) / (g.e - g.w)) * g.cols - 0.5;
    const fy = ((lat - g.s) / (g.n - g.s)) * g.rows - 0.5;
    const x0 = Math.max(0, Math.min(g.cols - 1, Math.floor(fx)));
    const y0 = Math.max(0, Math.min(g.rows - 1, Math.floor(fy)));
    const x1 = Math.min(g.cols - 1, x0 + 1);
    const y1 = Math.min(g.rows - 1, y0 + 1);
    const tx = Math.max(0, Math.min(1, fx - x0));
    const ty = Math.max(0, Math.min(1, fy - y0));
    const at = (x: number, y: number) => wxData[y * g.cols + x];
    const p00 = at(x0, y0), p10 = at(x1, y0), p01 = at(x0, y1), p11 = at(x1, y1);
    const bl = (a: number, b: number, c: number, d: number) => (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
    const uv = (p: WxPoint): [number, number] => { const to = ((p.dir + 180) * Math.PI) / 180; return [p.wind * Math.sin(to), p.wind * Math.cos(to)]; };
    const [u00, v00] = uv(p00), [u10, v10] = uv(p10), [u01, v01] = uv(p01), [u11, v11] = uv(p11);
    const u = bl(u00, u10, u01, u11), v = bl(v00, v10, v01, v11);
    const wind = Math.hypot(u, v);
    const dir = ((Math.atan2(u, v) * 180) / Math.PI + 360 + 180) % 360; // u/v are "to"; back to "from" bearing
    return { lon, lat, wind, gust: bl(p00.gust, p10.gust, p01.gust, p11.gust), dir, temp: bl(p00.temp, p10.temp, p01.temp, p11.temp), precip: bl(p00.precip, p10.precip, p01.precip, p11.precip) };
  }

  // A dense interpolated grid over the CURRENT viewport (rebuilt on move so it's always continuous at the
  // shown zoom) — feeds the smooth temp field + the faint wind flow. `cols`/`rows` = on-screen density.
  function wxViewGrid(cols: number, rows: number): WxPoint[] {
    const b = engine?.getBounds();
    if (!b || !wxGrid) return [];
    const s = b.south, n = b.north, w = b.west, e = b.east;
    const out: WxPoint[] = [];
    for (let i = 0; i < rows; i++)
      for (let j = 0; j < cols; j++) {
        const p = wxAt(w + ((e - w) * (j + 0.5)) / cols, s + ((n - s) * (i + 0.5)) / rows);
        if (p) out.push(p);
      }
    return out;
  }
  // The sampled points that fall inside the current viewport — the DETAILED (bold) wind arrows go here;
  // the interpolated grid gives the faint in-between flow.
  function wxSampledInView(): WxPoint[] {
    const b = engine?.getBounds();
    if (!b || !wxGrid) return wxData;
    const pad = 0.05 * (b.east - b.west);
    return wxData.filter((p) => p.lon >= b.west - pad && p.lon <= b.east + pad && p.lat >= b.south - pad && p.lat <= b.north + pad);
  }

  // Wind severity for DRONE flight (m/s): calm/fine (green) → caution ~18 km/h (amber) → risky ~29 km/h
  // (orange) → dangerous ~43 km/h (red). Applied INDEPENDENTLY to sustained wind and to gusts.
  const windColor = (s: number) => (s >= 12 ? '#ef4444' : s >= 8 ? '#f97316' : s >= 5 ? '#eab308' : '#22c55e');
  const windSeverity = (s: number): string => (s >= 12 ? t('Dangerous') : s >= 8 ? t('Risky') : s >= 5 ? t('Caution') : t('Calm'));
  const tempColor = (c: number) => (c <= 0 ? '#2563eb' : c < 10 ? '#22d3ee' : c < 18 ? '#22c55e' : c < 25 ? '#eab308' : c < 32 ? '#f97316' : '#ef4444');
  // (TEMP_RAMP moved to the engine)

  // Big two-tier wind arrow pointing where the wind blows TO (dir is the "from" bearing, so +180). The
  // SOLID arrow is the sustained (average) wind — its length AND thickness AND colour all grow with speed.
  // A faded, thinner, LONGER arrow behind it is the gust: its extra reach shows how much stronger the peaks
  // are. Symmetric around the grid point so it rotates cleanly in place, like a compass needle.
  // Arrow length is PROPORTIONAL to speed across a wide, visible range (a small floor keeps calm winds
  // just visible; strong winds get long arrows), so a 12 vs 18 km/h difference — and a 2 vs 14 — is
  // obvious. The gust "ghost" behind is coloured + sized by the GUST value, the solid arrow by the
  // SUSTAINED value, so each reads its own severity. Symmetric around the point → rotates like a needle.
  const windLen = (s: number): number => Math.min(80, 10 + s * 4.6); // px in the 90-unit viewBox
  function windArrowSvg(p: WxPoint): string {
    const to = (p.dir + 180) % 360;
    const cx = 45, cy = 45, hw = 7.5;
    const avgLen = windLen(p.wind);
    const gustLen = Math.max(avgLen, windLen(p.gust));
    const thick = Math.min(8.5, 3.4 + p.wind * 0.5);
    const arrow = (len: number, w: number, col: string, opacity: number): string => {
      const h = len / 2;
      const tail = (cy + h).toFixed(1);
      const head = (cy - h).toFixed(1);
      const barb = (cy - h + hw).toFixed(1);
      return (
        `<path d="M${cx} ${tail} L${cx} ${head} M${cx} ${head} L${(cx - hw).toFixed(1)} ${barb} M${cx} ${head} L${(cx + hw).toFixed(1)} ${barb}" ` +
        `stroke="${col}" stroke-width="${w.toFixed(1)}" fill="none" stroke-linecap="round" stroke-linejoin="round" opacity="${opacity}"/>`
      );
    };
    const ghost = gustLen > avgLen + 1 ? arrow(gustLen, 2.6, windColor(p.gust), 0.55) : '';
    return `<svg width="76" height="76" viewBox="0 0 90 90" class="fp-wx-arrow" style="transform:rotate(${to.toFixed(0)}deg)">${ghost}${arrow(avgLen, thick, windColor(p.wind), 1)}</svg>`;
  }
  // One grid marker stacks the ENABLED point read-outs: a colour-coded temperature chip on top, the wind
  // arrow + "avg↟gust" speed in the middle, raindrops (count by intensity) at the bottom. The smooth
  // temperature / precipitation COLOUR FIELDS live in map layers underneath — the marker is the exact
  // number, the field is the at-a-glance comparison. Returns null when nothing is enabled/present here.
  function wxEl(p: WxPoint): HTMLDivElement | null {
    let html = '';
    if (wxShow.temp && !Number.isNaN(p.temp)) html += `<span class="fp-wx-temp" style="background:${tempColor(p.temp)}">${p.temp.toFixed(0)}°</span>`;
    if (wxShow.wind && !Number.isNaN(p.wind) && !Number.isNaN(p.dir)) {
      const spd = windUnit === 'kmh' ? Math.round(p.wind * 3.6) : Math.round(p.wind);
      const gst = windUnit === 'kmh' ? Math.round(p.gust * 3.6) : Math.round(p.gust);
      const gustTag = gst > spd ? `<span class="fp-wx-gust" style="color:${windColor(p.gust)}">↑${gst}</span>` : '';
      html += windArrowSvg(p) + `<span class="fp-wx-spd" style="color:${windColor(p.wind)}">${spd}${gustTag}</span>`;
    }
    if (wxShow.rain && p.precip > 0) {
      const n = p.precip < 0.5 ? 1 : p.precip < 2 ? 2 : p.precip < 5 ? 3 : 4;
      html += `<span class="fp-wx-drops">${'💧'.repeat(n)}</span>`;
    }
    if (!html) return null;
    const el = document.createElement('div');
    el.className = 'fp-wx';
    el.innerHTML = html;
    return el;
  }
  // The 8-point compass label for a "from" bearing (Turkish abbreviations).
  const COMPASS = ['K', 'KD', 'D', 'GD', 'G', 'GB', 'B', 'KB'];
  function wxTipHtml(p: WxPoint): string {
    const from = COMPASS[Math.round((p.dir % 360) / 45) % 8];
    const spd = fmtWind(p.wind);
    const gst = fmtWind(p.gust);
    const wc = windColor(p.wind);
    const gc = windColor(p.gust);
    const tc = tempColor(p.temp);
    const rows: string[] = [];
    rows.push(`<div class="wxr"><span class="wxk" style="background:${tc}">${p.temp.toFixed(0)}°C</span><span class="wxlabel">${t('Temperature')}</span></div>`);
    rows.push(
      `<div class="wxr"><span class="wxarrow" style="color:${wc};transform:rotate(${((p.dir + 180) % 360).toFixed(0)}deg)">↑</span>` +
        `<span class="wxwind"><b style="color:${wc}">${spd}</b> <span class="wxsev" style="color:${wc}">${windSeverity(p.wind)}</span>` +
        (p.gust > p.wind ? ` · ${t('gust')} <b style="color:${gc}">${gst}</b> <span class="wxsev" style="color:${gc}">${windSeverity(p.gust)}</span>` : '') +
        `<span class="wxfrom">${from}${t('’den')} (${p.dir.toFixed(0)}°)</span></span></div>`,
    );
    if (p.precip > 0) {
      const n = p.precip < 0.5 ? 1 : p.precip < 2 ? 2 : p.precip < 5 ? 3 : 4;
      rows.push(`<div class="wxr"><span class="wxdrops">${'💧'.repeat(n)}</span><span class="wxlabel">${t('Precipitation')} ${p.precip.toFixed(1)} mm</span></div>`);
    }
    return `<div class="fp-wxtip">${rows.join('')}</div>`;
  }
  // GeoJSON for a colour-field layer: temperature keeps every point; precipitation keeps only the wet ones
  // (dry areas stay unpainted, per the "only show rain where it rains" rule).
  // Field FC from the DENSE interpolated viewport grid → the coloured circles overlap into a CONTINUOUS
  // field (no gaps between sampled points). Precip keeps only wet cells (dry stays unpainted).
  function wxFieldFC(pts: WxPoint[], metric: 'temp' | 'precip'): GeoJSON.FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: pts
        .filter((p) => (metric === 'temp' ? !Number.isNaN(p.temp) : p.precip > 0.05))
        .map((p) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
          properties: metric === 'temp' ? { temp: p.temp } : { precip: p.precip },
        })),
    };
  }
  // Faint wind FC (interpolated in-between points) — a symbol layer draws a light rotated arrow at each,
  // so the wind FLOW reads as a field and the estimated (non-sampled) points are visibly softer.
  function wxWindFC(pts: WxPoint[]): GeoJSON.FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: pts
        .filter((p) => !Number.isNaN(p.wind) && !Number.isNaN(p.dir))
        .map((p) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
          properties: { to: (p.dir + 180) % 360, wind: p.wind },
        })),
    };
  }
  function renderWx(): void {
    const on = layers.weather;
    // Dense interpolated grid over the current viewport (continuous field + faint flow). ~34×24 cells.
    const dense = on && (wxShow.temp || wxShow.rain || wxShow.wind) ? wxViewGrid(34, 24) : [];
    engine?.setWeatherField(
      on && wxShow.temp ? wxFieldFC(dense, 'temp') : null,
      on && wxShow.rain ? wxFieldFC(dense, 'precip') : null,
      on && wxShow.wind ? wxWindFC(wxViewGrid(20, 14)) : null,
    );
    // Bold, detailed dual-arrow markers only at the REAL sampled points in view (the faint layer fills
    // between them). The panel builds each marker's DOM + tooltip; the engine owns the Marker + popup.
    if (!on || (!wxShow.temp && !wxShow.wind && !wxShow.rain)) { engine?.setWindMarkers([]); return; }
    const markers = wxSampledInView()
      .map((p) => ({ p, el: wxEl(p) }))
      .filter((m): m is { p: WxPoint; el: HTMLDivElement } => m.el !== null)
      .map((m) => ({ lnglat: [m.p.lon, m.p.lat] as [number, number], el: m.el, tip: wxTipHtml(m.p) }));
    engine?.setWindMarkers(markers);
  }

  // Open-Meteo hourly times are GMT strings "YYYY-MM-DDTHH:00"; build the one matching now + offset.
  function targetHourIso(offsetH: number): string {
    const d = new Date(Date.now() + offsetH * 3_600_000);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:00`;
  }
  function wxTimeLabel(offsetH: number): string {
    if (offsetH === 0) return t('Now');
    const d = new Date(Date.now() + offsetH * 3_600_000);
    const hhmm = d.toLocaleTimeString(i18n.lang === 'tr' ? 'tr-TR' : 'en-GB', { hour: '2-digit', minute: '2-digit' });
    const day = d.toLocaleDateString([], { weekday: 'short' });
    return `${day} ${hhmm} · +${offsetH} ${t('h')}`;
  }
  async function loadWeather(): Promise<void> {
    // Two independent gates: `caps` is whether the renderer could draw it, `feat` is whether this
    // deployment has weather at all. Either being false means no metered call is ever made.
    if (!layers.weather || !caps.weather || !feat.weather) return;
    if ((engine?.getZoom() ?? 0) < WIND_MIN_ZOOM) {
      wxData = [];
      wxGrid = null;
      renderWx(); // wipe markers + fields at whole-world zoom (a global request would be meaningless)
      return;
    }
    const vb = engine?.getBounds();
    if (!vb) return;
    const viewW = vb.east - vb.west;
    // Above country scale (either span > 30°) the field is meaningless AND a grid fetch over a continental
    // box (the observed lat 10→53 request) just burns the rate limit. Skip the fetch and clear the field;
    // zooming back to a country/region view refetches (cache hit if it's a box we've seen).
    if (viewW > 30 || vb.north - vb.south > 30) { wxData = []; wxGrid = null; renderWx(); return; }
    // Snap the fetch bbox OUT to a fixed lat/lon lattice (cell size scales with zoom). Nearby pans then
    // produce the SAME snapped box → the same cache key → a hit, instead of a fresh fetch per move. The
    // snapped box always contains the view (+ up to one cell of padding), so interpolation stays valid.
    const snap = viewW > 24 ? 8 : viewW > 12 ? 4 : viewW > 6 ? 2 : viewW > 3 ? 1 : viewW > 1.5 ? 0.5 : viewW > 0.6 ? 0.25 : 0.1;
    const w = Math.floor(vb.west / snap) * snap;
    const e = Math.ceil(vb.east / snap) * snap;
    const s = Math.floor(vb.south / snap) * snap;
    const n = Math.ceil(vb.north / snap) * snap;
    const key = `${wxHourOffset}:${w.toFixed(3)},${s.toFixed(3)},${e.toFixed(3)},${n.toFixed(3)}`;
    // 1) In-memory hit — same snapped box this session: re-interpolate for the current view, no network.
    if (wxGrid && wxCacheKey === key) {
      renderWx();
      return;
    }
    // 2) Persistent hit — survives reloads (TTL'd); restores the grid without touching the network.
    const cached = wxCacheGet<{ data: WxPoint[]; grid: WxGrid }>(key);
    if (cached) {
      wxData = cached.data;
      wxGrid = cached.grid;
      wxCacheKey = key;
      wxPaused = false;
      renderWx();
      return;
    }
    // 3) Backing off after a rate-limit: keep the last-good grid on screen; the retry/next move refetches.
    if (Date.now() < wxCooldownUntil) return;
    // 4) Daily budget: Open-Meteo weights a fetch by nLocations·(nDays/14)·(nVariables/10). Estimate it and
    //    soft-pause (cache-only) once the self-imposed daily cap is hit, protecting the shared per-IP quota.
    const future = wxHourOffset > 0;
    const forecastDays = wxHourOffset <= 24 ? 2 : 3; // only enough days to cover the selected hour
    // 8×6 = 48 locations (was 12×9 = 108). Open-Meteo weights a request by its location count, so a dense
    // grid over rapid pans blew the per-IP limit → 429s → no weather at all. The field is bilinearly
    // interpolated between grid points anyway, so 48 reads look the same while costing less than half.
    const cols = 8, rows = 6;
    const estWeight = cols * rows * ((future ? forecastDays : 1) / 14) * (5 / 10);
    if (wxBudgetSpent() + estWeight > WX_DAILY_WEIGHT_CAP) {
      wxPaused = true;
      return;
    }
    const lats: string[] = [];
    const lons: string[] = [];
    for (let i = 0; i < rows; i++)
      for (let j = 0; j < cols; j++) {
        lats.push((s + ((n - s) * (i + 0.5)) / rows).toFixed(4));
        lons.push((w + ((e - w) * (j + 0.5)) / cols).toFixed(4));
      }
    windAbort?.abort();
    const ac = new AbortController();
    windAbort = ac;
    windBusy = true;
    try {
      const params = new URLSearchParams({ latitude: lats.join(','), longitude: lons.join(','), wind_speed_unit: 'ms' });
      if (future) {
        params.set('hourly', 'wind_speed_10m,wind_gusts_10m,wind_direction_10m,temperature_2m,precipitation');
        params.set('forecast_days', String(forecastDays));
      } else {
        params.set('current', 'wind_speed_10m,wind_gusts_10m,wind_direction_10m,temperature_2m,precipitation');
      }
      const r = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`, { signal: ac.signal });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = (await r.json()) as OMLoc | OMLoc[];
      if (ac.signal.aborted) return;
      wxBudgetAdd(estWeight); // count the successful fetch against today's self-imposed budget
      const wantIso = future ? targetHourIso(wxHourOffset) : '';
      // Keep the grid COMPLETE (one entry per input point, in order) so bilinear indexing stays valid;
      // a bad point becomes NaN and the interpolation skips only the cells that need it.
      const arr = Array.isArray(data) ? data : [data];
      wxData = arr.map((loc) => {
        let wind: number | undefined, gust: number | undefined, dir: number | undefined, temp: number | undefined, precip: number | undefined;
        if (future) {
          const h = loc.hourly;
          const idx = h?.time?.indexOf(wantIso) ?? -1;
          if (idx >= 0) { wind = h?.wind_speed_10m?.[idx]; gust = h?.wind_gusts_10m?.[idx]; dir = h?.wind_direction_10m?.[idx]; temp = h?.temperature_2m?.[idx]; precip = h?.precipitation?.[idx]; }
        } else {
          wind = loc.current?.wind_speed_10m; gust = loc.current?.wind_gusts_10m; dir = loc.current?.wind_direction_10m; temp = loc.current?.temperature_2m; precip = loc.current?.precipitation;
        }
        return {
          lon: loc.longitude, lat: loc.latitude,
          wind: typeof wind === 'number' ? wind : NaN,
          gust: typeof gust === 'number' ? gust : typeof wind === 'number' ? wind : NaN,
          dir: typeof dir === 'number' ? dir : NaN,
          temp: typeof temp === 'number' ? temp : NaN,
          precip: typeof precip === 'number' ? precip : 0,
        };
      });
      wxGrid = wxData.length === rows * cols ? { rows, cols, s, n, w, e } : null;
      wxCacheKey = key;
      wxPaused = false;
      if (wxGrid) wxCacheSet(key, { data: wxData, grid: wxGrid }); // persist for reloads + nearby views
      renderWx();
    } catch (err) {
      // The weather grid is a nicety — stay silent. But on a rate-limit, back off for a window and schedule
      // one self-heal retry so weather returns once the limit clears (without needing a manual pan).
      if (!ac.signal.aborted && err instanceof Error && err.message === 'HTTP 429') {
        wxCooldownUntil = Date.now() + 20000;
        clearTimeout(wxRetryTimer);
        wxRetryTimer = setTimeout(() => void loadWeather(), 20500);
      }
    } finally {
      if (windAbort === ac) windBusy = false;
    }
  }
  function toggleWeather(on: boolean): void {
    if (!feat.weather) return;
    layers.weather = on;
    if (on) void loadWeather();
    else {
      windAbort?.abort();
      clearTimeout(wxRetryTimer); // don't let a pending rate-limit retry refetch after the user turned it off
      wxData = [];
      renderWx(); // clears both the arrow markers AND the temp/precip field layers
    }
  }

  // Re-render the grid when the enabled metrics or wind unit change (no refetch — values are cached).
  $effect(() => {
    wxShow.temp;
    wxShow.wind;
    wxShow.rain;
    windUnit;
    renderWx();
  });

  // The forecast time changing DOES need a refetch (different hour of data). `layers.weather` is read
  // first so the guard doesn't hide the dependency; skip the run when the layer is off.
  let wxTimePrimed = false;
  $effect(() => {
    const off = wxHourOffset;
    const on = layers.weather;
    if (!wxTimePrimed) {
      wxTimePrimed = true; // the map-load path already did the first fetch; don't double it
      return;
    }
    void off;
    if (on) void loadWeather();
  });

  // Zone visibility: direct mode shows the raw semi-transparent zones; effective mode shows the
  // geometrically merged blocks instead (built lazily by pushEffective). One effect owns BOTH so they
  // never race. Read the reactive flags FIRST so the guard doesn't hide dependencies on the initial run.
  $effect(() => {
    void mapReady;
    const { nofly, allowed, notam } = layers;
    const eff = effective && effReady; // show RAW until the merge is ready → zones never briefly vanish
    void updatedAt; // rebuild the merge when the dataset changes
    void notam; // read for reactivity; `notamsVisible` is the value that decides
    engine?.setZoneVisibility(nofly, allowed, notamsVisible, eff);
    if (eff) {
      void ensureEffective(); // no-op if already cached; else the worker fills the sources when it returns
      if (effGeom && effStamp === (updatedAt ?? 0)) applyEffData(); // cached → populate the sources instantly
    }
  });

  // Terrain layers follow their toggles.
  $effect(() => {
    void mapReady;
    const { hillshade, elevation, contours } = terrain;
    engine?.setTerrainLayers(hillshade, elevation, contours);
  });
  // POI symbols follow their toggle — basemap aerodromes + drone-relevant zone-site markers — and the
  // zone-site markers are filtered by the selected "why" categories.
  $effect(() => {
    void mapReady;
    const on = poi;
    const sym = poiSymbols;
    const names = poiNames;
    const sf = { ...siteCatFacet };
    const cats = availSiteCats;
    engine?.setPoi(on, sym, names, cats.filter((c) => sf[c] !== false));
  });
  // Basemap place labels by admin level (OMT `place` class). Buckets map the OMT classes to the Turkish
  // il/ilçe/köy the user thinks in; country/continent always show (few, and orienting).
  const PLACE_CLASSES = {
    il: ['city', 'state'],
    ilce: ['town'],
    koy: ['village', 'hamlet', 'isolated_dwelling', 'suburb', 'quarter', 'neighbourhood'],
  };
  $effect(() => {
    void mapReady;
    const pl = { ...placeLabels };
    const sel = ['country', 'continent'];
    if (pl.il) sel.push(...PLACE_CLASSES.il);
    if (pl.ilce) sel.push(...PLACE_CLASSES.ilce);
    if (pl.koy) sel.push(...PLACE_CLASSES.koy);
    engine?.setPlaceLabels(sel);
  });
  // The user's flight tracks follow their toggles: master (showFlights) gates everything, then the
  // ground projection (2D line) and the 3D curtain are independently switchable.
  $effect(() => {
    void mapReady;
    const on = showFlights;
    const { air, ground, curtain } = flightViz;
    engine?.setFlightVisibility(on, air, ground, curtain);
  });
  // WHICH captures are drawn. Reading the visible sets here means the map re-renders both when the scan
  // streams in new media and when a filter changes — one path, so the map, the table and the counts can
  // never drift apart. `setData` only swaps source data, so the camera never moves.
  $effect(() => {
    void mapReady;
    void visibleFlights;
    pushFlights();
  });
  $effect(() => {
    void mapReady;
    void visiblePhotos;
    void showPhotos;
    pushPhotos();
  });
  // Focus aid for overlapping paths: when a flight's detail card is open, that flight stays bright and
  // the others fade back, so the user can pick out the one they're looking at.
  $effect(() => {
    void mapReady;
    const fid = flightDetail?.id ?? null;
    engine?.setFlightFocus(fid);
  });
  // Faceted sub-filters: restrict zones to selected authorities AND why-restricted categories, and
  // NOTAMs to selected types.
  $effect(() => {
    void mapReady;
    const af = { ...authFacet }; // track
    const nf = { ...notamFacet };
    const cn = { ...catFacet.nofly };
    const co = { ...catFacet.ok };
    const auths = availAuthorities;
    const ntypes = availNotamTypes;
    const catsN = availCats.nofly;
    const catsO = availCats.ok;
    const selN = catsN.filter((c) => cn[c] !== false);
    const selO = catsO.filter((c) => co[c] !== false);
    const selType = ntypes.filter((t) => nf[t] !== false);
    engine?.setZoneFacets(auths.filter((a) => af[String(a)] !== false), selN, selO, selType);
  });

  // ---- basemap toggle ---------------------------------------------------------------------------

  $effect(() => {
    void mapReady;
    engine?.setBasemap(basemap);
    // Vector labels ride on top of BOTH basemaps (they stay upright when the map rotates, unlike the
    // old baked-in raster labels), so no per-basemap toggle needed.
  });


  // The engine owns hover/click PLUMBING; this host supplies tooltip CONTENT + ACTIONS. The engine routes
  // flights back through us (flightById → object → nearestSample/flightPathTip), so all domain logic stays
  // in the panel and the engine stays renderer-agnostic.
  const interactionHost: MapInteractionHost<MyFlight> = {
    showFlights: () => showFlights,
    poi: () => poi,
    effective: () => effective,
    flightById: (id) => myFlights.find((x) => x.id === id) ?? null,
    nearestSample: (f, lng, lat) => nearestSample(f, lng, lat),
    flightPathTip: (f, sample) => flightPathTip(f, sample as unknown as FlightSample),
    flightTip: (props) => flightTip(props),
    photoTip: (props) => photoTip(props),
    symbolTip: (props) => symbolTip(props),
    zoneTip: (sorted) => tipHtml(sorted),
    dedupeZones: (feats) => dedupeZones(feats),
    effInfosAt: (lat, lng) => effInfosAt(lat, lng),
    effComponentFeature: (lng, lat) => (effGeom ? (effComponentAt(effGeom, lng, lat, effVisible)?.feature ?? null) : null),
    highlightFlight: (id) => highlightFlight(id),
    highlightFeature: (cls, id) => highlightFeature(cls, id),
    highlightGeom: (feature) => highlightGeom(feature),
    clearHighlight: () => clearHighlight(),
    setPathPoint: (lng, lat, alt) => setPathPoint(lng, lat, alt),
    clearPathPoint: () => clearPathPoint(),
    onHoverFrame: (f, sample) => scheduleHoverFrame(f, sample as unknown as FlightSample),
    openFlightDetail: (id, at) => openFlightDetail(id, at),
    openPhoto: (id) => openPhoto(id),
    assess: (pt) => void assess(pt),
    t: (key) => t(key),
  };

  onMount(() => {
    const mountT = plog('FlightPlanning: onMount (pane revealed)');
    // Register shortcuts FIRST so they work even if map init later fails.
    window.addEventListener('keydown', onShortcut);
    // The renderer lives behind an engine (MapLibreEngine here); the panel drives it, and once every
    // map op has migrated a LeafletEngine can drop in for the WebGL-less fallback with the same panels.
    // WebGL can be unavailable (hardware accel off, GPU blocklisted) — MapLibre throws synchronously from
    // its constructor; catch it and surface the fallback instead of a blank map.
    const initEngine = (e: PlanningMapEngine): void => {
      engine = e;
      e.mount(container, { center: V0.center, zoom: V0.zoom, basemap, pitch: V0.pitch, bearing: V0.bearing });
      caps = e.capabilities;
      e.setupInteraction(interactionHost);
      e.onReady(() => {
        if (destroyed) return; // component torn down while the map was loading → don't start data/timers
        plog('planning: engine ready (layers built, map usable)', mountT);
        // First visit: frame the country instead of trusting a fixed zoom, which fills a laptop but
        // leaves a 4K monitor showing Italy and Egypt. Done HERE, not right after mount(): the map
        // must be usable before a camera op, and the container must have its final size or the
        // fitted zoom is computed against the wrong box.
        if (!hasStoredView) {
          try { e.fitBounds(TR_BOUNDS, { padding: 24, duration: 0 }); }
          catch (err) { console.warn('[planning] could not frame the default view', err); }
        }
        void track(t('Loading airspace zones'), loadData());
        if (feat.archive) void track(t('Scanning flights'), fetchFlights());
        if (feat.weather && layers.weather) void track(t('Loading weather'), loadWeather());
        e.onMoveEnd(() => {
          if (feat.weather && layers.weather) {
            clearTimeout(windDebounce);
            windDebounce = setTimeout(() => void track(t('Loading weather'), loadWeather()), 500);
          }
          lsSave(LS_VIEW, e.getViewState()); // remember exactly where the user left the map
        });
        // Tilt drives 2D/3D: a hand-tilt past the threshold flips the presentation and this mirrors it back
        // into the button state (the button itself just animates the camera via set3D).
        e.on3DChange((on) => { is3D = on; });
        refreshTimer = setInterval(() => { void loadData(); if (feat.archive) void fetchFlights(true); }, 60 * 60 * 1000);
        mapReady = true; // re-run the toggle effects now that every layer exists
      });
    };
    // MapLibre is WebGL-only and throws synchronously if WebGL is unavailable (hardware accel off, GPU
    // blocklisted). Fall back to the Leaflet raster engine — SAME panels, reduced capabilities. Leaflet is
    // lazy-loaded so it never ships on the normal (WebGL-OK) path.
    try {
      initEngine(new MapLibreEngine());
    } catch (err) {
      console.warn('[planning] WebGL unavailable — using the Leaflet raster fallback', err);
      // mount() may already have put a map in the container before the throw. Without this the
      // fallback mounts a SECOND renderer on top of a live one.
      try { engine?.destroy(); } catch { /* half-built engine — nothing to tear down */ }
      engine = undefined;
      container.replaceChildren();
      reducedMap = true;
      void import('../lib/planning/map/leafletEngine.ts')
        .then(({ LeafletEngine }) => { if (!destroyed) initEngine(new LeafletEngine()); })
        .catch((e) => {
          // The container was emptied before this import, and `loadData()` only runs inside
          // `onReady` — so a failed chunk leaves a grey rectangle, no data, and no error state,
          // under a banner that says everything else works. Two ways to get here: a stale
          // index.html after a deploy, and the network dropping between first paint and the import.
          console.error('[planning] raster fallback failed to load', e);
          reducedMap = false;
          mapFailed = true;
        });
    }

    resizeObs = new ResizeObserver(() => engine?.resize());
    resizeObs.observe(container);
  });

  function onShortcut(e: KeyboardEvent): void {
    const el = document.activeElement;
    const typing = el instanceof HTMLElement && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    if (e.key === 'Escape') { if (photoView) { photoView = null; return; } closePalette(); facetPop = null; shortcutsOpen = false; return; }
    if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
    switch (e.key) {
      case '/': e.preventDefault(); searchFocused = true; searchInputEl?.focus(); break;
      case 'f': showFilters = !showFilters; break;
      case 'e': if (caps.effectiveZones) effective = !effective; break;
      case '3': if (caps.threeD) toggle3D(!is3D); break;
      case 'w': if (caps.weather && feat.weather) toggleWeather(!layers.weather); break;
      case 'l': showFlights = !showFlights; break;
      case 't': if (feat.archive) showFlightsTable = !showFlightsTable; break;
      case '?': shortcutsOpen = !shortcutsOpen; break;
    }
  }

  onDestroy(() => {
    destroyed = true;
    window.removeEventListener('keydown', onShortcut);
    dataAbort?.abort();
    clearTimeout(searchTimer);
    clearTimeout(paletteBlurTimer);
    searchAbort?.abort();
    clearTimeout(windDebounce);
    clearTimeout(wxRetryTimer);
    windAbort?.abort();
    effWorker?.terminate();
    thumbIO?.disconnect();
    if (refreshTimer) clearInterval(refreshTimer);
    clearTimeout(scanPollTimer);
    mapLut?.dispose();
    clearTimeout(hoverSeekTimer);
    for (const url of hoverFrameCache.values()) URL.revokeObjectURL(url);
    hoverFrameCache.clear();
    if (hoverVideo) hoverVideo.src = '';
    assessAbort?.abort();
    clearTimeout(notamTimer);
    resizeObs?.disconnect();
    engine?.destroy();
  });

  function escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
  }
  // SHGM/NOTAM text fields sometimes carry HTML (e.g. <font …> tags), which would show as literal markup
  // in labels/tooltips. Strip tags + decode the common entities + collapse whitespace before display.
  // Memoised: clean() runs ~7 regex passes and is called on the same zone/NOTAM names repeatedly across the
  // on-load builders (zone FCs, search index, POI FC) and again on every hover tooltip — caching by input
  // turns the repeats into O(1) lookups. Bounded by the dataset's distinct name strings.
  const cleanCache = new Map<string, string>();
  function clean(s: unknown): string {
    const key = String(s ?? '');
    const hit = cleanCache.get(key);
    if (hit !== undefined) return hit;
    const out = key
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#39;|&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/\s+/g, ' ')
      .trim();
    cleanCache.set(key, out);
    return out;
  }
  /**
   * How OLD the data is, not when it arrived. A clock time reads as fresh at a glance — "23:56" looks
   * like tonight whatever day it is — whereas "3 gün önce" cannot be misread.
   */
  function ageLabel(at: number): string {
    const mins = Math.max(0, Math.round((Date.now() - at) / 60000));
    if (mins < 60) return t('just now');
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} ${t('h ago')}`;
    return `${Math.round(hrs / 24)} ${t('d ago')}`;
  }

  // ---- NOTAM tooltip enrichment: decode the ICAO code + present validity / altitude the way a pilot
  // reads them. Shared by both hover paths (raw layer query + effective-mode point-in-polygon) so the two
  // modes show identical detail.
  const fmtNotamDate = (ms: number): string =>
    ms ? new Date(ms).toLocaleString('tr', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  // SHGM standing restrictions span decades (e.g. 2000→2050) — collapse those to "Kalıcı" instead of
  // printing meaningless bounds; genuine temporary NOTAMs show the real date range.
  function notamValidity(start: number, end: number): string {
    const Y2005 = 1104534000000;
    if (start && start <= Y2005 && end >= STANDING_END_MS) return t('permanent');
    const s = fmtNotamDate(start);
    const e = end && end < STANDING_END_MS ? fmtNotamDate(end) : t('open-ended');
    return s ? `${s} – ${e}` : e;
  }
  // Altitude band. SHGM/ICAO limits are in feet; 0 = surface, a very high ceiling = unlimited. The metres
  // hint lets the pilot judge whether the restriction even reaches drone altitude (a 5000 ft floor doesn't).
  const ftM = (ft: number): string => `${ft.toLocaleString('tr')} ft (~${Math.round(ft * 0.3048).toLocaleString('tr')} m)`;
  function notamAlt(lower: number, upper: number): string {
    const noHi = !upper || upper >= 60000;
    if (lower <= 0 && noHi) return ''; // whole vertical column — nothing worth a row
    return `${lower > 0 ? ftM(lower) : t('surface')} – ${noHi ? t('unlimited') : ftM(upper)}`;
  }
  const notamKind = (n: Notam): string => t(isStanding(n) ? 'Airspace structure' : 'NOTAM');
  function notamTipProps(n: Notam): Record<string, unknown> {
    return {
      cls: 'notam',
      id: n.id,
      name: clean(n.description || n.name) || notamKind(n),
      kind: notamKind(n),
      typeLabel: notamTypeLabel(n.area_type),
      raw: n.area_type, // the ICAO code itself (P / R / WULW / …)
      meaning: notamTypeDesc(n.area_type),
      valid: notamValidity(n.start_ms, n.end_ms),
      alt: notamAlt(n.lower_limit, n.upper_limit),
      desc: clean(n.description),
      area: zoneArea(n.rings),
      atype: n.area_type || '?', // filterable facet
    };
  }

  // ---- flight detail card -----------------------------------------------------------------------
  let flightVideoEl = $state<HTMLVideoElement | undefined>(undefined);
  let flightFrames = $state<{ url: string; t: number }[]>([]);
  // Why the storyboard is empty, when it is — surfaced in the card instead of silently blank.
  let flightFramesError = $state('');
  let flightFramesLoading = $state(false); // server is generating the storyboard → show a "preparing" note
  let framesToken = 0;
  // Storyboard event pins overlaid on the frame strip — the key moments derived from the alt/position
  // samples (all we have per sample). Positioned by time fraction so they line up with the even frames.
  const flightEvents = $derived.by((): { t: number; frac: number; icon: string; label: string }[] => {
    const f = flightDetail;
    const s = f?.samples ?? [];
    if (!f || s.length < 2) return [];
    const dur = f.durationS || s[s.length - 1][2] || 1;
    let maxAltI = 0;
    let farI = 0;
    let farD = -1;
    const home = s[0];
    for (let i = 1; i < s.length; i++) {
      if (s[i][3] > s[maxAltI][3]) maxAltI = i;
      const d = metersBetween(home[0], home[1], s[i][0], s[i][1]);
      if (d > farD) { farD = d; farI = i; }
    }
    const evs = [
      { t: 0, icon: '🛫', label: t('Takeoff') },
      { t: s[maxAltI][2], icon: '⛰', label: `${t('Max altitude')} · ${Math.round(s[maxAltI][3])} m` },
      { t: s[farI][2], icon: '📍', label: `${t('Farthest point')} · ${fmtDist(farD)}` },
      { t: s[s.length - 1][2], icon: '🛬', label: t('Landing') },
    ];
    return evs.map((e) => ({ ...e, frac: Math.max(0, Math.min(1, e.t / dur)) })).sort((a, b) => a.t - b.t);
  });

  // Preview card position: next to the clicked flight (map-container px), or null = the CSS default corner
  // (used when opened from the table/search, where there's no click location).
  let flightCardPos = $state<{ left: number; top: number } | null>(null);
  function cardPosNear(at: { x: number; y: number }): { left: number; top: number } | null {
    if (!container) return null;
    const W = flightCardW; // .fp-flight width (user-resizable)
    const cw = container.clientWidth, ch = container.clientHeight;
    return { left: Math.max(8, Math.min(at.x + 14, cw - W - 8)), top: Math.max(48, Math.min(at.y - 24, ch - 260)) };
  }
  function openFlightDetail(id: string, at?: { x: number; y: number }): void {
    const f = myFlights.find((x) => x.id === id) ?? null;
    const wasOpen = flightDetail != null; // an already-visible card stays where it is; don't teleport it
    flightDetail = f;
    if (!wasOpen) flightCardPos = at ? cardPosNear(at) : null;
    void extractFrames(f);
  }
  function selectFlight(f: MyFlight): void {
    const wasOpen = flightDetail != null; // switching sibling clips shouldn't jerk the card to the corner
    flightDetail = f;
    if (!wasOpen) flightCardPos = null; // first open from the table/search → default corner
    clearPlayPoint(); // the previous clip's marker; the new clip repositions it on play
    void extractFrames(f);
  }
  function closeFlightDetail(): void {
    framesToken++; // cancel any in-flight frame extraction
    flightDetail = null;
    flightFrames = [];
    flightFramesError = '';
    flightFramesLoading = false;
    clearPlayPoint();
  }
  const flightVideoUrl = (f: MyFlight | null): string => (f?.video ? `/api/flight-video?p=${encodeURIComponent(f.video)}` : '');
  // Sibling clips from the same session folder (for quick switching in the detail card).
  const siblingClips = (f: MyFlight): MyFlight[] => myFlights.filter((x) => x.folder === f.folder);
  const hhmm = (ts: string): string => (ts.length >= 16 ? ts.slice(11, 16) : ts);
  function fmtDuration(s: number): string {
    const m = Math.floor(s / 60);
    const sec = Math.round(s % 60);
    return m ? `${m}:${String(sec).padStart(2, '0')}` : `${sec} sn`;
  }
  function seekFlightVideo(t: number): void {
    if (flightVideoEl) {
      flightVideoEl.currentTime = t;
      void flightVideoEl.play().catch(() => {});
    }
  }
  // Playback ↔ map sync: as the detail-card video plays, drop a marker at the flight sample nearest the
  // current time, so you SEE where on the route the footage is. `t` is seconds from clip start = sample[2].
  function sampleAtTime(f: MyFlight, t: number): FlightSample | null {
    let best: FlightSample | null = null;
    let bd = Infinity;
    for (const s of f.samples) {
      const d = Math.abs(s[2] - t);
      if (d < bd) { bd = d; best = s; }
    }
    return best;
  }
  function setPlayPoint(lon: number, lat: number, alt = 0): void {
    engine?.setPlaybackMarker([lon, lat], alt);
  }
  function clearPlayPoint(): void {
    engine?.setPlaybackMarker(null);
  }
  function onFlightTimeUpdate(): void {
    if (!flightDetail || !flightVideoEl) return;
    cardTime = flightVideoEl.currentTime;
    if (cardGraded) cardRenderOnce(); // keep the graded canvas in step on seeks (onseeked → this)
    const s = sampleAtTime(flightDetail, flightVideoEl.currentTime);
    if (s) setPlayPoint(s[0], s[1], s[3]); // s = [lon,lat,tSec,relAlt] → marker rides the airborne path
  }

  // The LUT mode for a clip (no color_md metadata on the map, so filename fallback), gated by the toggle.
  const lutModeFor = (clipName: string): string => (app.lutEnabled ? detectColorMode('', clipName) : '');

  // ---- Inline detail-card player, LUT-graded for D-Log clips -------------------------------------------
  // A plain <video> can't apply a 3D LUT, and a canvas laid over it would swallow the native controls. So for
  // D-Log footage the <video> is hidden (it stays the source — audio, timing, seeking) and each frame is
  // painted through the WebGL LUT onto a canvas, with our own minimal controls. Normal footage needs no grade
  // and keeps the native player untouched (user: "LUT gereken bir format değilse LUT atmak bozar").
  let cardCanvasEl = $state<HTMLCanvasElement | undefined>(undefined);
  let cardLut: LutEngine | undefined;
  let cardRaf = 0;
  let cardPlaying = $state(false);
  let cardTime = $state(0);
  let cardDur = $state(0);
  const cardGraded = $derived(!!flightDetail?.video && lutModeFor(flightDetail?.clipName ?? '') !== '');
  // Just the LUT mode string — keying the effect on THIS (not the clip) means switching between D-Log clips
  // (same mode) reuses the one WebGL engine instead of tearing a context down and rebuilding it on the canvas.
  const cardLutMode = $derived(cardGraded ? lutModeFor(flightDetail?.clipName ?? '') : '');
  function cardRenderOnce(): void {
    if (!cardLut || !flightVideoEl?.videoWidth) return;
    const w = 480;
    const h = Math.round((w * flightVideoEl.videoHeight) / flightVideoEl.videoWidth) || 270;
    cardLut.renderSource(flightVideoEl, w, h);
  }
  function cardLoop(): void {
    if (!cardLut) return;
    cardRenderOnce();
    cardRaf = requestAnimationFrame(cardLoop);
  }
  function teardownCardLut(): void {
    if (cardRaf) { cancelAnimationFrame(cardRaf); cardRaf = 0; }
    cardLut?.dispose();
    cardLut = undefined;
  }
  // Build / tear down the graded pipeline as the card opens on a D-Log clip and the canvas mounts. Not reading
  // cardPlaying here (only paused, which isn't reactive) so play/pause doesn't rebuild the WebGL engine.
  $effect(() => {
    const canvas = cardCanvasEl;
    const info = LUTS[cardLutMode];
    if (!canvas || !info) { teardownCardLut(); return; }
    let alive = true;
    const eng = new LutEngine(canvas);
    void eng.load(info.url).then(() => {
      if (!alive) { eng.dispose(); return; }
      cardLut = eng;
      cardRenderOnce();
      if (flightVideoEl && !flightVideoEl.paused) cardLoop();
    });
    return () => { alive = false; teardownCardLut(); };
  });
  function toggleCardPlay(): void {
    if (!flightVideoEl) return;
    if (flightVideoEl.paused) void flightVideoEl.play().catch(() => {});
    else flightVideoEl.pause();
  }
  function onCardPlay(): void { cardPlaying = true; if (cardGraded && !cardRaf) cardLoop(); }
  function onCardPause(): void { cardPlaying = false; if (cardRaf) { cancelAnimationFrame(cardRaf); cardRaf = 0; } cardRenderOnce(); }
  function onCardMeta(): void { cardDur = flightVideoEl?.duration || 0; cardRenderOnce(); }
  function onCardSeek(e: Event): void {
    if (flightVideoEl) flightVideoEl.currentTime = Number((e.currentTarget as HTMLInputElement).value);
  }

  // Auto-LUT for the hover-scrub fallback frame (when the server storyboard isn't cached yet): D-Log M footage
  // is flat, so grade it through the same Rec.709 LUT (one shared WebGL engine, offscreen).
  let mapLut: LutEngine | undefined;
  let mapLutCanvas: HTMLCanvasElement | undefined;
  let mapLutUrl = '';
  /** Load the LUT for `mode`; returns the engine when a grade applies, else null (footage passes through raw). */
  async function ensureLut(mode: string): Promise<LutEngine | undefined> {
    const info = LUTS[mode];
    if (!info) return undefined;
    try {
      if (!mapLutCanvas) mapLutCanvas = document.createElement('canvas');
      if (!mapLut) mapLut = new LutEngine(mapLutCanvas);
      if (mapLutUrl !== info.url) { await mapLut.load(info.url); mapLutUrl = info.url; }
      return mapLut;
    } catch (e) {
      console.warn('[map grade] LUT unavailable — raw frames:', e);
      return undefined;
    }
  }
  /** The engine IF the LUT for `mode` is already loaded (sync path for hover); warms it otherwise. */
  function readyLut(mode: string): LutEngine | undefined {
    const info = LUTS[mode];
    if (!info) return undefined;
    if (mapLut && mapLutUrl === info.url) return mapLut;
    void ensureLut(mode); // load for the next frame
    return undefined;
  }

  // Fetch the storyboard from the server (ffmpeg, cached on disk). The browser's own <video>+canvas seek path
  // was unreliable for the archive's 4K HEVC clips under the map's main-thread load — some clips got frames,
  // some didn't. Cancellable via framesToken so switching clips / closing the card abandons a stale request.
  async function extractFrames(f: MyFlight | null): Promise<void> {
    const token = ++framesToken;
    flightFrames = [];
    flightFramesError = '';
    if (!f?.video) return;
    flightFramesLoading = true; // may need on-the-fly ffmpeg (first open of an un-cached clip → a few seconds)
    try {
      const res = await fetch(`/api/flight-thumbs?p=${encodeURIComponent(f.video)}&n=12`);
      if (token !== framesToken) return; // superseded by a newer open
      if (!res.ok) {
        flightFramesError =
          res.status === 422
            ? t('Preview unavailable — the video duration could not be read.')
            : t('Preview unavailable — frames could not be extracted.');
        return;
      }
      const data = (await res.json()) as { frames?: { t: number; url: string }[] };
      if (token !== framesToken) return;
      const frames = data.frames ?? [];
      if (!frames.length) { flightFramesError = t('Preview unavailable — frames could not be extracted.'); return; }
      flightFrames = frames;
    } catch {
      if (token === framesToken) flightFramesError = t('Preview unavailable — frames could not be extracted.');
    } finally {
      if (token === framesToken) flightFramesLoading = false;
    }
  }

  // Make a floating box draggable from ANY empty part of it (not a tiny grip). Grabs start anywhere on
  // the box except on real controls (inputs/buttons/etc.) and the scrolling body, so checkboxes still
  // work and the body still scrolls. Accumulates a translate offset over the box's CSS anchor.
  // The facet sub-popup's BODY is excluded (its checkboxes must work), but its HEAD is NOT — grabbing the
  // sub-popup header therefore bubbles to this same handler and drags the whole panel, so the panel and its
  // attached popup move as one piece.
  const DRAG_IGNORE = 'input,button,select,textarea,a,label,summary,option,video,img,[role="slider"],[contenteditable="true"],.fp-facet-pop-body,.fp-filters-body,.fp-index-body,.fp-story,.fp-flight-clips,.fp-flight-body,.fp-flight-grip,.fp-table-body';
  function draggable(node: HTMLElement) {
    let sx = 0, sy = 0, ox = 0, oy = 0, on = false;
    const down = (e: PointerEvent) => {
      if (e.button !== 0) return;
      if (e.target instanceof Element && e.target.closest(DRAG_IGNORE)) return; // let controls/scroll work
      on = true; sx = e.clientX; sy = e.clientY;
      try { node.setPointerCapture(e.pointerId); } catch { /* non-capturable pointer — move still tracks */ }
      node.classList.add('fp-dragging');
    };
    const move = (e: PointerEvent) => {
      if (!on) return;
      node.style.transform = `translate(${ox + e.clientX - sx}px, ${oy + e.clientY - sy}px)`;
    };
    const up = (e: PointerEvent) => {
      if (!on) return;
      on = false; ox += e.clientX - sx; oy += e.clientY - sy;
      // Clamp so the box can never be dragged fully off-screen and lost (keep ≥ 48px reachable).
      const r = node.getBoundingClientRect();
      const M = 48;
      if (r.right < M) ox += M - r.right;
      else if (r.left > window.innerWidth - M) ox += window.innerWidth - M - r.left;
      if (r.bottom < M) oy += M - r.bottom;
      else if (r.top > window.innerHeight - M) oy += window.innerHeight - M - r.top;
      node.style.transform = `translate(${ox}px, ${oy}px)`;
      node.classList.remove('fp-dragging');
      node.releasePointerCapture?.(e.pointerId);
    };
    // Double-click an empty part of the box resets it to its anchored position.
    const reset = (e: MouseEvent) => {
      if (e.target instanceof Element && e.target.closest(DRAG_IGNORE)) return;
      ox = 0; oy = 0; node.style.transform = '';
    };
    node.addEventListener('pointerdown', down);
    node.addEventListener('pointermove', move);
    node.addEventListener('pointerup', up);
    node.addEventListener('dblclick', reset);
    return {
      destroy() {
        node.removeEventListener('pointerdown', down);
        node.removeEventListener('pointermove', move);
        node.removeEventListener('pointerup', up);
        node.removeEventListener('dblclick', reset);
      },
    };
  }

  const ZONE_DOT: Record<string, string> = { nofly: '#ef4444', ok: '#22c55e', notam: '#f59e0b' };

  // Minimal shape the tooltip needs — satisfied by both queryRenderedFeatures results and the synthetic
  // point-in-polygon infos built in effective mode.
  type TipFeature = { properties: Record<string, unknown> | null };

  // queryRenderedFeatures can hand back the same zone twice (tile seams / overlapping fill layers);
  // key by class+id so each real zone shows once in the tooltip.
  function dedupeZones(feats: TipFeature[]): TipFeature[] {
    const seen = new Set<string>();
    const out: TipFeature[] = [];
    for (const f of feats) {
      const key = `${f.properties?.cls}:${f.properties?.id ?? f.properties?.name}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(f);
    }
    return out;
  }

  // In effective mode the raw zone layers are hidden, so hover can't queryRenderedFeatures them. Fall
  // back to point-in-polygon over the in-memory zones/NOTAMs to build the same combined tooltip.
  function effInfosAt(lat: number, lon: number): TipFeature[] {
    // Filtered by the SAME rules the layers are. Without this the tooltip did point-in-polygon over
    // the unfiltered grids, so hovering in effective mode listed zones the visitor had switched off
    // and NOTAMs the freshness rule had withdrawn — the map showing one thing and its own
    // explanation another. Direct mode never had this, because `queryRenderedFeatures` can only
    // return layers that are actually drawn.
    const zs = zoneGrid.at(lat, lon).filter(zoneShownRaw).map((z) => {
      const kind = z.area_type === 909 ? t('No-fly (red)') : t('Allowed (green)');
      const cat = zoneCategory(z.name || '');
      return {
        properties: {
          cls: z.area_type === 909 ? 'nofly' : 'ok',
          id: z.id,
          name: clean(z.name) || kind,
          kind,
          auth: zoneAuthorityLabel(z.authority),
          category: cat.key,
          catLabel: `${cat.icon} ${cat.label}`,
          area: zoneArea(z.rings),
        },
      };
    });
    const now = Date.now();
    const ns = notamGrid.at(lat, lon).filter((n) => isActive(n, now) && notamShownRaw(n)).map((n) => ({ properties: notamTipProps(n) }));
    return [...zs, ...ns].sort((a, b) => Number(a.properties.area) - Number(b.properties.area));
  }

  // A hover tooltip that lists each overlapping zone as its own labelled row (dot · name · kind·code),
  // rather than collapsing them into "+N more here".
  function tipHtml(sorted: TipFeature[]): string {
    const MAX = 6;
    const cards = sorted.slice(0, MAX).map((f) => {
      const p = f.properties ?? {};
      const cls = String(p.cls ?? '');
      const accent = ZONE_DOT[cls] ?? 'var(--line-strong)';
      // Header = a colour-coded badge (class) + a title; body = a key→value table with only the rows that
      // actually inform. Colour never stands alone — the badge text says it too (a11y).
      const rows: [string, string][] = [];
      let badge: string;
      let title: string;
      if (cls === 'notam') {
        badge = String(p.kind);
        title = String(p.typeLabel || p.name || badge); // "what kind" is the useful headline
        if (p.raw) rows.push([t('Code'), String(p.raw)]); // the ICAO code the pilot cross-checks
        if (p.meaning) rows.push([t('Meaning'), String(p.meaning)]);
        if (p.valid) rows.push([t('Valid'), String(p.valid)]);
        if (p.alt) rows.push([t('Altitude'), String(p.alt)]);
        const d = String(p.desc ?? '').trim();
        if (d && d !== title) rows.push([t('Description'), d.length > 220 ? `${d.slice(0, 219)}…` : d]);
      } else {
        // Drop the trailing "(kırmızı)" — the badge colour already says red/green, so repeating it is noise.
        badge = String(p.kind ?? '').replace(/\s*\([^)]*\)\s*$/, '').trim();
        const nm = String(p.name ?? '').trim();
        title = nm && nm !== String(p.kind ?? '') ? nm : ''; // no title when the name is just the class label
        // The catch-all "Diğer" category says nothing → skip it; a real reason (military/dam/…) shows.
        if (String(p.category ?? '') !== 'other' && p.catLabel) rows.push([t('Reason'), String(p.catLabel)]);
        if (p.auth) rows.push([t('Authority'), String(p.auth)]);
      }
      const meta = rows
        .map(([k, v]) => `<div class="fp-tip-mrow"><dt class="fp-tip-mk">${escapeHtml(k)}</dt><dd class="fp-tip-mv">${escapeHtml(v)}</dd></div>`)
        .join('');
      return `<div class="fp-tip-card" style="--bx:${accent}"><div class="fp-tip-chead"><span class="fp-tip-badge">${escapeHtml(badge)}</span>${title ? `<span class="fp-tip-name">${escapeHtml(title)}</span>` : ''}</div>${meta ? `<dl class="fp-tip-meta">${meta}</dl>` : ''}</div>`;
    });
    const extra = sorted.length > MAX ? `<div class="fp-tip-more">+${sorted.length - MAX} ${t('more here')}</div>` : '';
    return `<div class="fp-tip-list">${cards.join('')}${extra}</div>`;
  }

  // ---- hover for the OTHER annotations (symbols + flights) ---------------------------------------
  function symbolTip(p: Record<string, unknown>): string {
    const cls = String(p.category ?? p.class ?? '');
    const name = escapeHtml(String(p.name ?? p['name:latin'] ?? p.label ?? t('Marked site')));
    const kind = String(p.kindLabel ?? (cls === 'military' ? t('Military airfield') : cls === 'heliport' ? t('Heliport') : t('Airport')));
    // Basemap aero POIs carry `class` not `sym`; derive the SAME emoji the map icon uses so they match.
    const icon = String(p.sym ?? (cls === 'military' ? categoryIcon('military') : cls === 'heliport' ? HELIPORT_ICON : cls === 'airport' ? categoryIcon('airport') : '📍'));
    return `<div class="fp-tip-list"><div class="fp-tip-row"><span class="fp-tip-sym">${icon}</span><span class="fp-tip-txt"><span class="fp-tip-name">${name}</span><span class="fp-tip-kind">${escapeHtml(kind)}</span></span></div></div>`;
  }
  function flightTip(p: Record<string, unknown>): string {
    const name = escapeHtml(String(p.title ?? p.name ?? t('My flight')));
    const dot = String(p.color ?? '#22d3ee');
    const meta = [String(p.sub ?? ''), String(p.clipName ?? ''), String(p.date ?? '')].filter(Boolean).map(escapeHtml).join(' · ');
    return `<div class="fp-tip-list"><div class="fp-tip-row"><span class="fp-tip-dot" style="background:${dot}"></span><span class="fp-tip-txt"><span class="fp-tip-name">${name}</span>${meta ? `<span class="fp-tip-kind">${meta} · ${escapeHtml(t('click for detail'))}</span>` : ''}</span></div></div>`;
  }
  const photoById = (id: string): MyPhoto | undefined => myPhotos.find((p) => p.id === id);
  // "1/400s", "2s", or "" — EXIF stores exposure time in seconds.
  const fmtShutter = (s?: number): string => (s == null ? '' : s >= 1 ? `${s}s` : `1/${Math.round(1 / s)}s`);
  // Rich photo tooltip (§2.5): the thumbnail first, then filename + when, then camera / exposure / place —
  // grouped so the eye reads the picture, then what/when, then how. Reuses the flight-tip chrome.
  function photoTip(props: Record<string, unknown>): string {
    const p = photoById(String(props.id ?? ''));
    if (!p) return '';
    const thumb = p.thumb
      ? `<img class="fp-tip-photoframe" src="${p.thumb}" alt="" />`
      : `<div class="fp-tip-frameph">${p.raw ? 'RAW' : '📷'}</div>`;
    const when = p.takenAt ? escapeHtml(p.takenAt.replace('T', ' · ').slice(0, 19)) : '';
    const cam = [p.model ?? '', p.focalLen ? `${Math.round(p.focalLen)}mm` : ''].filter(Boolean).map(escapeHtml).join(' · ');
    const exp = [p.fnum ? `ƒ${p.fnum}` : '', fmtShutter(p.shutter), p.iso ? `ISO ${p.iso}` : ''].filter(Boolean).map(escapeHtml).join(' · ');
    const place = escapeHtml([p.absAlt != null ? `⛰ ${Math.round(p.absAlt)} m` : '', `📍 ${p.lat.toFixed(5)}, ${p.lon.toFixed(5)}`].filter(Boolean).join('  '));
    const name = escapeHtml(p.name) + (p.present ? '' : ` · ${escapeHtml(t('archived'))}`);
    return `<div class="fp-tip-flight"><div class="fp-tip-head"><span class="fp-tip-sym">📷</span><span class="fp-tip-name">${name}</span></div>${when ? `<div class="fp-tip-sub">${when}</div>` : ''}${thumb}<div class="fp-tip-stats">${cam ? `<span class="fp-tip-stat">${cam}</span>` : ''}${exp ? `<span class="fp-tip-stat">${exp}</span>` : ''}<span class="fp-tip-stat">${place}</span></div><div class="fp-tip-sub fp-tip-hint">${escapeHtml(t('click to enlarge'))}</div></div>`;
  }
  function openPhoto(id: string): void {
    photoView = photoById(id) ?? null;
  }
  // Outline a whole flight track (looked up by id → full geometry, not the tile-clipped hover feature).
  function highlightFlight(id: string): void {
    const f = myFlights.find((x) => x.id === id);
    const segs = f?.segments.filter((s) => s.length > 1) ?? [];
    highlightGeom(segs.length ? { type: 'Feature', properties: {}, geometry: { type: 'MultiLineString', coordinates: segs } } : null);
  }

  // ---- flight-path point hover: telemetry + the video frame at that instant --------------------
  let hoverVideo: HTMLVideoElement | undefined;
  let hoverCanvas: HTMLCanvasElement | undefined;
  let hoverVideoSrc = '';
  let hoverSeekTimer: ReturnType<typeof setTimeout> | undefined;
  let hoverToken = 0;
  // Map-hover coalescing state (perf#1) — component-scoped so mouseout/onDestroy can cancel a pending frame.
  let hoverFrameUrl: string | null = null;
  let pathPointOn = false;

  function nearestSample(f: MyFlight, lon: number, lat: number): FlightSample | null {
    let best: FlightSample | null = null;
    let bd = Infinity;
    for (const s of f.samples) {
      const d = (s[0] - lon) ** 2 + (s[1] - lat) ** 2;
      if (d < bd) {
        bd = d;
        best = s;
      }
    }
    return best;
  }
  function setPathPoint(lon: number, lat: number, alt = 0): void {
    pathPointOn = true;
    engine?.setPathPoint([lon, lat], alt);
  }
  function clearPathPoint(): void {
    if (!pathPointOn) return;
    pathPointOn = false;
    hoverFrameUrl = null;
    hoverToken++;
    clearTimeout(hoverSeekTimer);
    engine?.setPathPoint(null);
  }
  // Horizontal great-circle distance in metres (samples are lon/lat degrees).
  function metersBetween(lon1: number, lat1: number, lon2: number, lat2: number): number {
    const R = 6_371_000;
    const toR = Math.PI / 180;
    const dLat = (lat2 - lat1) * toR;
    const dLon = (lon2 - lon1) * toR;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * toR) * Math.cos(lat2 * toR) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
  }
  // Derived telemetry at a sample: radial distance from the launch point (a key safety number) and the
  // ground speed from the previous sample. Only [lon,lat,t,alt] exist per sample, so both are horizontal.
  function pathStats(f: MyFlight, s: FlightSample): { distHome: number; speed: number } {
    const home = f.samples[0];
    const distHome = home ? metersBetween(home[0], home[1], s[0], s[1]) : 0;
    const i = f.samples.indexOf(s);
    let speed = 0;
    if (i > 0) {
      const p = f.samples[i - 1];
      const dt = s[2] - p[2];
      if (dt > 0) speed = metersBetween(p[0], p[1], s[0], s[1]) / dt; // m/s
    }
    return { distHome, speed };
  }
  const fmtDist = (m: number): string => (m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(2)} km`);
  function flightPathTip(f: MyFlight, s: FlightSample): string {
    const st = pathStats(f, s);
    const dot = `<span class="fp-tip-dot" style="background:${flightColor(f.id)}"></span>`;
    // Prefer the reliable server storyboard (LUT-graded, cached): pick the frame nearest the hovered time.
    // Fall back to the browser-seek frame while it loads, then a "preparing" box so the tooltip never jumps.
    const cached = thumbCache[f.id];
    let frame: string;
    if (cached?.length) {
      const dur = f.durationS || (f.samples.length ? f.samples[f.samples.length - 1][2] : 0);
      const frac = dur > 0 ? Math.min(0.999, Math.max(0, s[2] / dur)) : 0.5;
      const url = cached[Math.min(cached.length - 1, Math.floor(frac * cached.length))];
      frame = url ? `<img class="fp-tip-frame" src="${url}" alt="" />` : '';
    } else if (hoverFrameUrl) {
      frame = `<img class="fp-tip-frame" src="${hoverFrameUrl}" alt="" />`;
    } else {
      frame = `<div class="fp-tip-frameph">${escapeHtml(t('Preparing…'))}</div>`;
    }
    const sub = [f.clipName, f.date].filter(Boolean).map(escapeHtml).join(' · ');
    const stats = [
      `<span class="fp-tip-stat">⏱ ${fmtDuration(s[2])}</span>`,
      `<span class="fp-tip-stat">⛰ ${Math.round(s[3])} m</span>`,
      `<span class="fp-tip-stat" title="${escapeHtml(t('Distance from launch'))}">📍 ${fmtDist(st.distHome)}</span>`,
      `<span class="fp-tip-stat">💨 ${Math.round(st.speed * 3.6)} km/h</span>`,
    ].join('');
    return `<div class="fp-tip-flight"><div class="fp-tip-head">${dot}<span class="fp-tip-name">${escapeHtml(flightTitle(f))}</span></div><div class="fp-tip-sub">${sub}</div>${frame}<div class="fp-tip-stats">${stats}</div></div>`;
  }
  // Grab the frame at the hovered time from a single persistent offscreen <video>, debounced so rapid
  // mouse movement doesn't thrash the decoder. When it lands, re-render the tooltip WITH the thumbnail.
  // Cache extracted hover frames by (flight, whole second): following a track re-hits the same seconds
  // constantly, and each miss cost a video seek + a 260px JPEG toDataURL (the dominant hover expense in the
  // profile). A hit is instant; the LRU keeps memory bounded.
  const HOVER_FRAME_CACHE_MAX = 400;
  const hoverFrameCache = new Map<string, string>(); // `${id}:${2s-bucket}` → object-URL (blob)
  function scheduleHoverFrame(f: MyFlight, s: FlightSample): void {
    clearTimeout(hoverSeekTimer);
    if (!f.video) return;
    // The reliable server storyboard is usually already cached (background fill / row hover) → use it, no seek.
    if (thumbCache[f.id]?.length) { engine?.updateHoverTip(flightPathTip(f, s)); return; }
    queueThumb(f); // fetch the LUT'd storyboard for next time; fall through to a browser-seek frame for now
    // 2-second buckets: a hover preview doesn't need per-frame precision, and coarser keys mean far more
    // cache hits when following a track (fewer video seeks + encodes).
    const key = `${f.id}:${Math.round(s[2] / 2)}`;
    const cached = hoverFrameCache.get(key);
    if (cached) { hoverFrameUrl = cached; engine?.updateHoverTip(flightPathTip(f, s)); return; } // instant, no seek/encode
    const token = hoverToken;
    hoverSeekTimer = setTimeout(() => {
      if (token !== hoverToken) return;
      if (!hoverVideo) {
        hoverVideo = document.createElement('video');
        hoverVideo.muted = true;
        hoverVideo.preload = 'auto';
      }
      if (!hoverCanvas) hoverCanvas = document.createElement('canvas');
      const url = flightVideoUrl(f);
      const grab = () => {
        const v = hoverVideo!;
        if (token !== hoverToken || !v.videoWidth) return;
        const onSeeked = () => {
          v.removeEventListener('seeked', onSeeked);
          if (token !== hoverToken) return;
          const w = 260; // matches the tooltip's 232px frame (a touch larger) so the preview stays crisp
          const h = Math.round(w * (v.videoHeight / v.videoWidth || 0.5625));
          hoverCanvas!.width = w;
          hoverCanvas!.height = h;
          hoverCanvas!.getContext('2d')?.drawImage(v, 0, 0, w, h);
          // Grade the frame through the auto-LUT (if loaded) so the D-Log preview matches the player; a
          // not-yet-loaded LUT falls back to the raw frame and warms for the next hover.
          const lut = readyLut(lutModeFor(f.clipName));
          const encodeCanvas = lut ? (lut.renderSource(hoverCanvas!, w, h), mapLutCanvas!) : hoverCanvas!;
          // toBlob is ASYNC — the JPEG encode happens off the main thread (toDataURL blocked it synchronously,
          // the dominant hover cost in the profile). Store the object-URL; revoke the oldest when over cap.
          encodeCanvas.toBlob((blob) => {
            if (!blob) return;
            const url = URL.createObjectURL(blob);
            hoverFrameCache.set(key, url);
            if (hoverFrameCache.size > HOVER_FRAME_CACHE_MAX) {
              const oldest = hoverFrameCache.keys().next().value!;
              const oldUrl = hoverFrameCache.get(oldest);
              hoverFrameCache.delete(oldest);
              if (oldUrl) URL.revokeObjectURL(oldUrl);
            }
            if (token === hoverToken) { hoverFrameUrl = url; engine?.updateHoverTip(flightPathTip(f, s)); }
          }, 'image/jpeg', 0.6);
        };
        v.addEventListener('seeked', onSeeked);
        v.currentTime = Math.min(s[2], v.duration || s[2]);
      };
      if (hoverVideoSrc !== url) {
        hoverVideoSrc = url;
        hoverVideo.src = url;
        hoverVideo.onloadedmetadata = grab;
      } else if (hoverVideo.videoWidth) {
        grab();
      } else {
        hoverVideo.onloadedmetadata = grab;
      }
    }, 140);
  }
</script>

<!-- Tracked so an open facet popup is re-placed on resize — the narrow-window case is exactly when it
     would otherwise be stranded off screen. -->
<svelte:window bind:innerWidth={winW} />

<div class="fp-pane">
  <div class="map" bind:this={container}></div>

  <!-- WebGL/MapLibre couldn't initialize (GPU crash / hardware acceleration off). Fall back to the Leaflet
       raster map — no WebGL, but still shows basemap + no-fly zones + flights. Lazy-imported so Leaflet only
       loads on this failure path, never on the normal (WebGL-OK) path. -->
  {#if mapFailed}
    <div class="fp-databanner" role="alert">⚠ {t('The map could not be started in this browser. This page cannot tell you where you may fly — check İHATTYS instead.')}</div>
  {:else if reducedMap}
    <div class="fp-reduced" role="status">⚠ {t('Reduced map (WebGL unavailable) — 3D, terrain and place-name level filtering are off; everything else works.')}</div>
  {/if}

  <!-- Safety banner: if the SHGM zone data failed to load, an empty map must NOT read as "clear airspace". -->
  {#if dataError && !updatedAt}
    <div class="fp-databanner" role="alert">⚠ {t('No-fly zone data could not be loaded — this map is NOT reliable right now. Do not use it to decide where to fly.')}</div>
  {/if}

  <!-- Unified search palette (top-center): one omnibox for EVERYTHING — on-map data (flights, zones,
       NOTAMs) + geocoded places. Nothing shows until focused; then it's the map-data directory + more. -->
  <div class="fp-search" class:open={searchFocused}>
    <span class="fp-search-ic" aria-hidden="true">🔍</span>
    <input
      bind:this={searchInputEl}
      type="search"
      placeholder={t('Search the map…')}
      aria-label={t('Search everything on the map')}
      bind:value={searchQuery}
      onfocus={() => (searchFocused = true)}
      onblur={() => { paletteBlurTimer = setTimeout(() => (searchFocused = false), 160); }}
      oninput={onSearchInput}
      onkeydown={(e) => {
        if (e.key === 'Escape') { closePalette(); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); moveActive(1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); moveActive(-1); }
        else if (e.key === 'Enter') { e.preventDefault(); activateActive(); }
      }}
    />
    {#if searchBusy}<span class="fp-search-busy" aria-hidden="true">…</span>{/if}
    {#if searchFocused}
      <!-- A short blur delay (paletteBlurTimer) lets a result click register before the palette closes. -->
      <div class="fp-palette" aria-label={t('Search results')}>
        {#snippet scopeCluster(label: string, arr: typeof SCOPES)}
          <div class="fp-pal-cluster">
            <span class="fp-pal-clbl">{label}</span>
            <div class="fp-pal-chips">
              {#each arr as s (s.k)}
                <button type="button" class="fp-pal-chip" class:on={searchScope[s.k]} aria-pressed={searchScope[s.k]} title={t('Click: toggle · Cmd/Ctrl-click: toggle the rest')} onmousedown={(e) => e.preventDefault()} onclick={(e) => scopeChip(s.k, e)}>
                  <span class="fp-chip-dot" style="background:{s.color}"></span>{t(s.label)}{#if scopeCounts.get(s.k)}<span class="fp-chip-n">{scopeCounts.get(s.k)}</span>{/if}
                </button>
              {/each}
            </div>
          </div>
        {/snippet}
        {#snippet catCluster(label: string, arr: typeof availPalCats)}
          <div class="fp-pal-cluster">
            <span class="fp-pal-clbl">{label}</span>
            <div class="fp-pal-chips">
              {#each arr as c (c.key)}
                <button type="button" class="fp-pal-chip fp-cat-chip" class:on={catFilter.includes(c.key)} aria-pressed={catFilter.includes(c.key)} title={t('Click: toggle · Cmd/Ctrl-click: toggle the rest')} onmousedown={(e) => e.preventDefault()} onclick={(e) => catChip(c.key, e)}>{c.icon} {c.label}{#if catCounts.get(c.key)}<span class="fp-chip-n">{catCounts.get(c.key)}</span>{/if}</button>
              {/each}
            </div>
          </div>
        {/snippet}

        <!-- Frame 1 — SEARCH SCOPE: which data the query looks through. Chips OR together (any lit type is
             included). Clustered SHGM airspace vs the user's flights + geocoded places. -->
        <fieldset class="fp-pal-fs">
          <legend class="fp-pal-legend">{t('Search scope')}</legend>
          {@render scopeCluster(t('SHGM airspace'), shgmScopes)}
          {@render scopeCluster(feat.archive ? t('Flights & places') : t('Places'), otherScopes)}
        </fieldset>

        <!-- Frame 2 — NARROW DOWN: sub-filters present in the scoped data. Chips OR together WITHIN this
             frame, but the frame as a whole ANDs with the scope above (pick "military" → only military,
             still constrained to the lit scopes). Clustered zone categories vs NOTAM types. -->
        {#if palCatZones.length || palCatNotams.length}
          <fieldset class="fp-pal-fs">
            <legend class="fp-pal-legend">
              {t('Narrow down')}
              {#if catFilter.length}<button type="button" class="fp-cat-clear" onmousedown={(e) => e.preventDefault()} onclick={() => (catFilter = [])}>✕ {t('Clear')}</button>{/if}
            </legend>
            {#if palCatZones.length}{@render catCluster(t('Zone type'), palCatZones)}{/if}
            {#if palCatNotams.length}{@render catCluster(t('Structure type'), palCatNotams)}{/if}
          </fieldset>
        {/if}
        <div class="fp-pal-body">
          {#if searchScope.place && (searchResults.length || (searchBusy && searchQuery.trim().length >= 2))}
            <div class="fp-index-group">
              <div class="fp-index-gh">{t('Places')} {#if searchBusy}<span class="dim">…</span>{/if}</div>
              {#each searchResults as r (r.id)}
                <button type="button" class="fp-index-row" class:active={activeEntry?.kind === 'place' && activeEntry.r === r} onmousedown={(e) => e.preventDefault()} onmouseenter={() => hoverPlace(r)} onmouseleave={hoverEnd} onclick={() => pickResult(r)}>
                  <span class="fp-index-ic">📍</span>
                  <span class="fp-index-txt"><span class="fp-index-lbl">{r.name.split(',')[0]}</span><span class="fp-index-sub">{r.name}</span></span>
                </button>
              {/each}
              <!-- The place data is CC BY, so the credit is a licence condition rather than a
                   courtesy. It sits under the results it credits and appears only once they do. -->
              {#if placeCredit}<p class="fp-index-credit">{placeCredit}</p>{/if}
            </div>
          {/if}
          {#if searchScope.place && searchError && !searchBusy}
            <p class="fp-index-err" role="status">⚠ {t('Place search is unavailable right now — check your connection.')}</p>
          {/if}
          {#each paletteGroups as g (g.kind)}
            <div class="fp-index-group">
              <div class="fp-index-gh">{t(g.label)} <span class="dim">({g.total})</span></div>
              {#each g.items as it (it.id)}
                <button type="button" class="fp-index-row" class:active={activeEntry?.kind === 'item' && activeEntry.it === it} onmousedown={(e) => e.preventDefault()} onmouseenter={() => hoverItem(it)} onmouseleave={hoverEnd} onclick={() => goToItem(it)}>
                  {#if it.icon}<span class="fp-index-ic" style="--dot:{it.color}">{it.icon}</span>{:else}<span class="fp-index-dot" style="background:{it.color}"></span>{/if}
                  <span class="fp-index-txt"><span class="fp-index-lbl">{it.label}</span>{#if it.sub}<span class="fp-index-sub">{it.sub}</span>{/if}</span>
                </button>
              {/each}
              {#if g.more}<div class="fp-index-more">+{g.more} · {t('refine search to see more')}</div>{/if}
            </div>
          {/each}
          {#if !paletteGroups.length && !(searchScope.place && searchResults.length) && !(searchScope.place && searchError)}
            <p class="fp-index-empty">{searchQuery.trim() ? t('No matches') : t('Start typing, or pick a category above')}</p>
          {/if}
        </div>
      </div>
    {/if}
  </div>

  <!-- Floating controls (top-right) — icon-only; the map/view segments now live inside the panel. -->
  <div class="fp-tr">
    <button class="fp-icon" class:on={shortcutsOpen} title={t('Keyboard shortcuts (?)')} aria-label={t('Keyboard shortcuts')} onclick={() => (shortcutsOpen = !shortcutsOpen)}>?</button>
    {#if feat.archive}
      <button class="fp-icon" class:on={showFlightsTable} title={t('All media table (t)')} aria-label={t('All media table')} aria-expanded={showFlightsTable} onclick={() => (showFlightsTable = !showFlightsTable)}>▦</button>
    {/if}
    <button class="fp-icon" class:on={showFilters} title={t('Layers & filters (f)')} aria-label={t('Layers & filters')} aria-expanded={showFilters} onclick={() => (showFilters = !showFilters)}>▤</button>
    {#if feat.host}
      <button class="fp-icon fp-icon-exit" title={t('Close flight planning')} aria-label={t('Close flight planning')} onclick={() => { app.hideSource('planning'); app.showSource('flights'); }}>✕</button>
    {/if}
  </div>

  {#snippet facetAllNone(all: () => void, none: () => void)}
    <span class="fp-facet-actions">
      <button type="button" onclick={(e) => { e.preventDefault(); e.stopPropagation(); all(); }}>{t('All')}</button>
      <button type="button" onclick={(e) => { e.preventDefault(); e.stopPropagation(); none(); }}>{t('None')}</button>
    </span>
  {/snippet}

  {#snippet facetBtn(key: string)}
    <button class="fp-facet-btn" class:on={facetPop === key} title={facetTitle(key)} aria-label={facetTitle(key)} aria-expanded={facetPop === key} onclick={() => (facetPop = facetPop === key ? null : key)}>⛃</button>
  {/snippet}

  {#if showFilters}
    <div class="fp-filters" role="group" aria-label={t('Layers & filters')} bind:this={filtersEl} use:draggable>
      <!-- Full-width header doubles as the drag handle (the whole panel is also draggable from any
           empty spot); grip dots signal it. -->
      <div class="fp-head" title={t('Drag to move')}>
        <span class="fp-head-grip" aria-hidden="true">⠿⠿⠿</span>
        <span class="fp-head-title">{t('Layers & filters')}</span>
        <button class="fp-facet-pop-x" title={t('Dismiss')} aria-label={t('Dismiss')} onclick={() => (showFilters = false)}>✕</button>
      </div>

      <!-- Zone sub-filter popup — opens to the LEFT of the panel (child, so it moves when dragged), keeping
           the main panel compact. Only one open at a time. -->
      {#if facetPop}
        <div class="fp-facet-pop" class:flip={facetFlip} style={facetShift === null ? '' : `left:${facetShift}px; right:auto;`} role="group" aria-label={facetTitle(facetPop)}>
          <div class="fp-facet-pop-head" title={t('Drag to move')}>
            <span class="fp-head-grip" aria-hidden="true">⠿⠿⠿</span>
            <span class="fp-facet-pop-title">{facetTitle(facetPop)}</span>
            {#if facetPop === 'nofly-cat'}{@render facetAllNone(() => setCatAll('nofly', true), () => setCatAll('nofly', false))}
            {:else if facetPop === 'ok-cat'}{@render facetAllNone(() => setCatAll('ok', true), () => setCatAll('ok', false))}
            {:else if facetPop === 'notam-type'}{@render facetAllNone(() => setNotamAll(true), () => setNotamAll(false))}
            {:else if facetPop === 'site-cat'}{@render facetAllNone(() => setSiteCatAll(true), () => setSiteCatAll(false))}
            {:else if facetPop === 'type'}{@render facetAllNone(() => setTypeAll(true), () => setTypeAll(false))}
            {:else if facetPop === 'tod'}{@render facetAllNone(() => setPhaseAll(true), () => setPhaseAll(false))}
            {:else if facetPop === 'dow'}{@render facetAllNone(() => setDowAll(true), () => setDowAll(false))}
            {:else if facetPop === 'month'}{@render facetAllNone(() => setMonthAll(true), () => setMonthAll(false))}
            {:else}{@render facetAllNone(() => setAuthAll(true), () => setAuthAll(false))}{/if}
            <button class="fp-facet-pop-x" title={t('Dismiss')} aria-label={t('Dismiss')} onclick={() => (facetPop = null)}>✕</button>
          </div>
          <div class="fp-facet-pop-body">
            {#if facetPop === 'nofly-cat'}
              {#each availCats.nofly as c (c)}<label class="sub"><input type="checkbox" bind:checked={catFacet.nofly[c]} /> {categoryIcon(c)} {categoryLabel(c)}</label>{/each}
            {:else if facetPop === 'ok-cat'}
              {#each availCats.ok as c (c)}<label class="sub"><input type="checkbox" bind:checked={catFacet.ok[c]} /> {categoryIcon(c)} {categoryLabel(c)}</label>{/each}
            {:else if facetPop === 'notam-type'}
              {#each notamGrouped as fam (fam.key)}
                <div class="fp-fam">
                  <div class="fp-fam-head">{fam.label} <button type="button" class="fp-fam-all" onclick={() => setNotamFam(fam.codes, true)}>{t('All')}</button><button type="button" class="fp-fam-all" onclick={() => setNotamFam(fam.codes, false)}>{t('None')}</button></div>
                  {#each fam.codes as nt (nt)}<label class="sub fp-sub-2l" title={notamTypeDesc(nt)}><input type="checkbox" bind:checked={notamFacet[nt]} /> <span class="fp-sub-txt"><span>{notamTypeLabel(nt)}</span>{#if notamTypeDesc(nt)}<span class="fp-sub-desc">{notamTypeDesc(nt)}</span>{/if}</span></label>{/each}
                </div>
              {/each}
            {:else if facetPop === 'site-cat'}
              {#each availSiteCats as c (c)}<label class="sub"><input type="checkbox" bind:checked={siteCatFacet[c]} /> {categoryIcon(c)} {categoryLabel(c)}</label>{/each}
            {:else if facetPop === 'type'}
              <!-- The two kinds of capture in the archive. RAW/JPEG is nested under Photos because it
                   only narrows photos — indenting it says that without a sentence. -->
              <div class="fp-fam">
                <div class="fp-fam-head">{t('Quick pick')}{#each MEDIA_KINDS as k (k.key)}<button type="button" class="fp-fam-all" onclick={() => setTypeOnly([k.key])}>{t(k.label)}</button>{/each}</div>
                {#each MEDIA_KINDS as k (k.key)}
                  <label class="sub"><input type="checkbox" checked={typeFacet[k.key] !== false} onchange={(e) => (typeFacet[k.key] = e.currentTarget.checked)} /> {k.icon} {t(k.label)} <span class="dim">({typeCounts[k.key]})</span></label>
                  <!-- Only offered when the archive actually holds both formats — an all-JPEG library
                       would get a RAW checkbox that can only ever hide everything. Same convention the
                       zone facets use (`availCats.length > 1`). -->
                  {#if k.key === 'photo' && typeFacet.photo !== false && fmtCounts.raw > 0 && fmtCounts.jpeg > 0}
                    <div class="fp-group">
                      {#each PHOTO_FORMATS as fm (fm.key)}
                        <label class="sub"><input type="checkbox" checked={fmtFacet[fm.key] !== false} onchange={(e) => (fmtFacet[fm.key] = e.currentTarget.checked)} /> {fm.label} <span class="dim">({fmtCounts[fm.key]})</span></label>
                      {/each}
                    </div>
                  {/if}
                {/each}
              </div>
            {:else if facetPop === 'tod'}
              <!-- Sun phases in the order the sun walks through them, with the elevation band that
                   defines each one — so "why is this flight sunset and not afternoon?" is answerable. -->
              <div class="fp-fam">
                <div class="fp-fam-head">{t('Quick pick')}{#each PHASE_GROUPS as g (g.label)}<button type="button" class="fp-fam-all" onclick={() => setPhaseOnly(g.keys)}>{t(g.label)}</button>{/each}</div>
                {#each SUN_PHASES as p (p.key)}
                  <label class="sub fp-sub-2l"><input type="checkbox" checked={phaseFacet[p.key] !== false} onchange={(e) => (phaseFacet[p.key] = e.currentTarget.checked)} /> <span class="fp-sub-txt"><span>{p.icon} {t(p.label)} <span class="dim">({phaseCounts.get(p.key) ?? 0})</span></span><span class="fp-sub-desc">{t(p.hint)}</span></span></label>
                {/each}
              </div>
              <div class="fp-cap">{t('Phases follow the sun’s real elevation at the takeoff point, so “sunset” is correct in every season.')}</div>
              <!-- Plain clock window, for when you want an exact hour range rather than a sun phase.
                   Lives in the same popup because it answers the same question. -->
              <div class="fp-fam">
                <div class="fp-fam-head">{t('Clock window')} <span class="dim">{hourSummary}</span>{#if !hourAll}<button type="button" class="fp-fam-all" onclick={() => { hourFrom = 0; hourTo = 24; }}>{t('All')}</button>{/if}</div>
                <label class="fp-hr-lbl" for="fp-hr-from">{t('From')} <b>{hh(hourFrom)}</b></label>
                <input id="fp-hr-from" class="fp-wx-range" type="range" min="0" max="24" step="1" bind:value={hourFrom} />
                <label class="fp-hr-lbl" for="fp-hr-to">{t('To')} <b>{hourTo === 0 ? '24:00' : hh(hourTo)}</b></label>
                <input id="fp-hr-to" class="fp-wx-range" type="range" min="0" max="24" step="1" bind:value={hourTo} />
                {#if hourWraps}<div class="fp-cap">↩ {t('Runs past midnight — one window across the night.')}</div>{/if}
              </div>
            {:else if facetPop === 'dow'}
              <div class="fp-fam">
                <div class="fp-fam-head">{t('Quick pick')}<button type="button" class="fp-fam-all" onclick={() => setDowOnly(WEEKDAYS)}>{t('Weekdays')}</button><button type="button" class="fp-fam-all" onclick={() => setDowOnly(WEEKEND)}>{t('Weekend')}</button></div>
                {#each DOW_ORDER as d (d)}
                  <label class="sub"><input type="checkbox" checked={dowFacet[String(d)] !== false} onchange={(e) => (dowFacet[String(d)] = e.currentTarget.checked)} /> {t(DOW_LABELS[d])} <span class="dim">({dowCounts.get(d) ?? 0})</span></label>
                {/each}
              </div>
            {:else if facetPop === 'month'}
              <div class="fp-fam">
                <div class="fp-fam-head">{t('Quick pick')}{#each SEASONS as s (s.label)}<button type="button" class="fp-fam-all" onclick={() => setMonthOnly(s.months)}>{t(s.label)}</button>{/each}</div>
                {#each MONTH_LABELS as label, i (label)}
                  <label class="sub"><input type="checkbox" checked={monthFacet[String(i)] !== false} onchange={(e) => (monthFacet[String(i)] = e.currentTarget.checked)} /> {t(label)} <span class="dim">({monthCounts.get(i) ?? 0})</span></label>
                {/each}
              </div>
            {:else}
              {#each availAuthorities as a (a)}<label class="sub"><input type="checkbox" bind:checked={authFacet[String(a)]} /> {a === -1 ? t('Undefined') : zoneAuthorityLabel(a)}</label>{/each}
            {/if}
          </div>
        </div>
      {/if}

      <div class="fp-filters-body">
      <div class="fp-sec">{t('View')}</div>
      <!-- Map + view mode (moved here from the top-right cluster). -->
      <div class="fp-segrow">
        <div class="fp-seg" role="group" aria-label={t('Basemap')}>
          <button class:on={basemap === 'streets'} aria-pressed={basemap === 'streets'} onclick={() => (basemap = 'streets')}>{t('Map')}</button>
          <button class:on={basemap === 'satellite'} aria-pressed={basemap === 'satellite'} onclick={() => (basemap = 'satellite')}>{t('Sat')}</button>
        </div>
      {#if caps.threeD}
        <div class="fp-seg" role="group" aria-label={t('View mode')}>
          <button class:on={!is3D} aria-pressed={!is3D} onclick={() => toggle3D(false)}>2D</button>
          <button class:on={is3D} aria-pressed={is3D} onclick={() => toggle3D(true)}>3D</button>
        </div>
      {/if}
      </div>
      <div class="fp-sec">{t('Zones')}</div>
      {#if caps.effectiveZones}
      <!-- Zone display is a MODE (how zones render), not a layer → a segmented option, sits with Zones. -->
      <div class="fp-eff">
        <span class="fp-eff-lbl">{t('Zone display')}{#if effBusy} <span class="dim">…</span>{/if}</span>
        {#if effFailed}<span class="fp-cap" role="status">{t('Merged view unavailable — showing the raw zones instead.')}</span>{/if}
        <div class="fp-seg" role="group" aria-label={t('Zone display')}>
          <button class:on={!effective} aria-pressed={!effective} onclick={() => (effective = false)}>{t('Direct')}</button>
          <button class:on={effective} aria-pressed={effective} onclick={() => (effective = true)}>{t('Effective')}</button>
        </div>
      </div>
      <div class="fp-cap">{t('Effective = overlapping zones merged; the most specific (smallest) at each point wins.')}</div>
      {/if}
      <!-- SHGM official zones — one parent toggles the three layers together. -->
      <label class="fp-parent">
        <input type="checkbox" checked={shgmAll} indeterminate={shgmSome && !shgmAll} onchange={(e) => toggleShgm(e.currentTarget.checked)} />
        {t('SHGM zones')}
      </label>
      <div class="fp-group">
        <div class="fp-frow">
          <label><input type="checkbox" bind:checked={layers.nofly} /> <i class="zi-hatch" style="background:#ef4444"></i>{t('No-fly (red)')}</label>
          {#if availCats.nofly.length > 1}{@render facetBtn('nofly-cat')}{/if}
        </div>
        <div class="fp-frow">
          <label><input type="checkbox" bind:checked={layers.allowed} /> <i style="background:#22c55e"></i>{t('Allowed (green)')}</label>
          {#if availCats.ok.length > 1}{@render facetBtn('ok-cat')}{/if}
        </div>
        <div class="fp-frow">
          <label class:fp-dis={!notamsOk}><input type="checkbox" bind:checked={layers.notam} disabled={!notamsOk} /> <i style="background:#f59e0b"></i>{t(notamLayerLabel)}</label>
          {#if availNotamTypes.length > 1}{@render facetBtn('notam-type')}{/if}
        </div>
        <!-- Below the row, not inside it: .fp-frow is a non-wrapping, vertically-centred flex row, so a
             third item of sentence length was squeezed between the label and the facet button and painted
             over the label. This paragraph explains why the checkbox above is disabled, so it belongs
             under it as its own block. -->
        {#if !notamsOk}
          <p class="fp-stale">{t('Airspace structures could not be loaded. Check İHATTYS.')}</p>
        {/if}
        {#if notamsWithheld > 0}
          <p class="fp-stale">{t('NOTAMs are not shown: the data is too old to be relied on. Check İHATTYS.')}</p>
        {/if}
        {#if availAuthorities.length > 1}
          <div class="fp-frow fp-frow-sub">
            <span class="fp-frow-lbl">{t('Authority')}</span>
            {@render facetBtn('authority')}
          </div>
        {/if}
      </div>
      <div class="fp-sec">{t('Overlays')}</div>
      <div class="fp-frow">
        <label><input type="checkbox" bind:checked={poi} /> <i class="ter-i" aria-hidden="true">📍</i>{t('Site symbols')}</label>
        {#if availSiteCats.length > 1}{@render facetBtn('site-cat')}{/if}
      </div>
      {#if poi}
        <div class="fp-group">
          <label class="sub"><input type="checkbox" bind:checked={poiSymbols} /> {t('Symbols')}</label>
          {#if caps.vectorLabels}<label class="sub"><input type="checkbox" bind:checked={poiNames} /> {t('Names')}</label>{/if}
        </div>
      {/if}
      {#if caps.vectorLabels}
      <label class="fp-parent">
        <input type="checkbox" checked={placeAll} indeterminate={placeSome && !placeAll} onchange={(e) => togglePlaceAll(e.currentTarget.checked)} />
        🏷 {t('Place names')}
      </label>
      <div class="fp-group">
        <label class="sub"><input type="checkbox" bind:checked={placeLabels.il} /> {t('Provinces')}</label>
        <label class="sub"><input type="checkbox" bind:checked={placeLabels.ilce} /> {t('Districts')}</label>
        <label class="sub"><input type="checkbox" bind:checked={placeLabels.koy} /> {t('Villages')}</label>
      </div>
      {/if}
      {#if feat.archive}
      <label><input type="checkbox" bind:checked={showFlights} /> <i style="background:#22d3ee"></i>{t('My flights')} {#if myFlights.length}<span class="dim">({mediaFilterActive ? `${visibleFlights.length} / ${myFlights.length}` : myFlights.length})</span>{/if}</label>
      {#if showFlights}
        <div class="fp-group">
          {#if caps.threeD}<label class="sub"><input type="checkbox" bind:checked={flightViz.air} /> {t('Airborne route (3D)')}</label>{/if}
          <label class="sub"><input type="checkbox" bind:checked={flightViz.ground} /> {t('Ground projection')}</label>
          {#if caps.threeD}<label class="sub"><input type="checkbox" bind:checked={flightViz.curtain} /> {t('3D altitude curtain')}</label>{/if}
        </div>
      {/if}
      <!-- Photos had no toggle at all before this: they always drew, with no way to hide them and no
           count. Same shape as "My flights" so the two kinds read as peers. -->
      <label><input type="checkbox" bind:checked={showPhotos} /> <i class="ter-i" aria-hidden="true">📷</i>{t('My photos')} {#if myPhotos.length}<span class="dim">({mediaFilterActive ? `${visiblePhotos.length} / ${myPhotos.length}` : myPhotos.length})</span>{/if}</label>
      <!-- Media filters sit directly under the two layers they narrow. One predicate drives the map, the
           media table and every count, so the three always agree. Shown even when a LAYER is off — the
           table still reads them. -->
      <div class="fp-ffilter" class:on={mediaFilterActive}>
        <div class="fp-ffilter-head">
          <span class="fp-ffilter-lbl">{t('Media filters')}</span>
          {#if mediaFilterActive}
            <span class="fp-ffilter-on">● {t('active')}</span>
            <button type="button" class="fp-ffilter-reset" onclick={resetMediaFilters}>{t('Reset')}</button>
          {/if}
        </div>
        <div class="fp-frow fp-frow-sub">
          <span class="fp-frow-lbl">{t('Type')}</span>
          <span class="fp-facet-sum" class:narrowed={!typeAll || !fmtAll}>{typeSummary}</span>
          {@render facetBtn('type')}
        </div>
        <div class="fp-frow fp-frow-sub">
          <span class="fp-frow-lbl">{t('Time of day')}</span>
          <span class="fp-facet-sum" class:narrowed={!phaseAll || !hourAll}>{todSummary}</span>
          {@render facetBtn('tod')}
        </div>
        <div class="fp-frow fp-frow-sub">
          <span class="fp-frow-lbl">{t('Day of week')}</span>
          <span class="fp-facet-sum" class:narrowed={!dowAll}>{dowSummary}</span>
          {@render facetBtn('dow')}
        </div>
        <div class="fp-frow fp-frow-sub">
          <span class="fp-frow-lbl">{t('Month')}</span>
          <span class="fp-facet-sum" class:narrowed={!monthAll}>{monthSummary}</span>
          {@render facetBtn('month')}
        </div>
        <!-- Numeric ranges. Both bounds optional; the "max" placeholder shows the archive's own extent
             so the scale is visible without guessing. Flight-only by nature — a photo has no duration,
             distance or altitude, so these leave photos alone rather than deleting them on a category
             error; the Type row above is how you drop photos. The sub-heading says so. -->
        <div class="fp-nsec">{t('Videos only')}</div>
        <div class="fp-nrow">
          <span class="fp-frow-lbl">{t('Duration')} <span class="dim">({t('minutes')})</span></span>
          <input class="fp-num" type="number" min="0" step="1" placeholder="0" aria-label={t('Minimum duration (minutes)')} bind:value={numRange.durMin} />
          <span class="fp-ndash" aria-hidden="true">–</span>
          <input class="fp-num" type="number" min="0" step="1" placeholder={String(flightExtent.dur)} aria-label={t('Maximum duration (minutes)')} bind:value={numRange.durMax} />
        </div>
        <div class="fp-nrow">
          <span class="fp-frow-lbl">{t('Distance')} <span class="dim">(km)</span></span>
          <input class="fp-num" type="number" min="0" step="0.1" placeholder="0" aria-label={t('Minimum distance (km)')} bind:value={numRange.distMin} />
          <span class="fp-ndash" aria-hidden="true">–</span>
          <input class="fp-num" type="number" min="0" step="0.1" placeholder={String(flightExtent.dist)} aria-label={t('Maximum distance (km)')} bind:value={numRange.distMax} />
        </div>
        <div class="fp-nrow">
          <span class="fp-frow-lbl">{t('Max alt')} <span class="dim">(m)</span></span>
          <input class="fp-num" type="number" min="0" step="1" placeholder="0" aria-label={t('Minimum altitude (m)')} bind:value={numRange.altMin} />
          <span class="fp-ndash" aria-hidden="true">–</span>
          <input class="fp-num" type="number" min="0" step="1" placeholder={String(flightExtent.alt)} aria-label={t('Maximum altitude (m)')} bind:value={numRange.altMax} />
        </div>
        {#if mediaFilterActive}
          <div class="fp-ffilter-count" role="status">
            {#if visibleFlights.length || visiblePhotos.length}
              🎥 {visibleFlights.length.toLocaleString()} / {myFlights.length.toLocaleString()}
              · 📷 {visiblePhotos.length.toLocaleString()} / {myPhotos.length.toLocaleString()}
            {:else}
              <span class="warn">⚠ {t('Nothing matches — widen a filter or reset.')}</span>
            {/if}
          </div>
        {/if}
      </div>
      {/if}
      {#if caps.weather && feat.weather}
      <label><input type="checkbox" checked={layers.weather} onchange={(e) => toggleWeather(e.currentTarget.checked)} /> <i class="wind-i" aria-hidden="true">➤</i>{t('Weather')} {#if windBusy}<span class="dim">…</span>{/if}</label>
      {#if layers.weather}
        <div class="fp-group">
          <!-- Independent metrics → checkboxes (any combination shows at once), not a segmented picker. -->
          <label class="sub"><input type="checkbox" bind:checked={wxShow.temp} /> {t('Temp')}</label>
          <label class="sub"><input type="checkbox" bind:checked={wxShow.wind} /> {t('Wind')}</label>
          <label class="sub"><input type="checkbox" bind:checked={wxShow.rain} /> {t('Rain')}</label>
          <div class="fp-wx-metric" role="group" aria-label={t('Wind unit')}>
            <button class:on={windUnit === 'kmh'} aria-pressed={windUnit === 'kmh'} onclick={() => (windUnit = 'kmh')}>km/h</button>
            <button class:on={windUnit === 'ms'} aria-pressed={windUnit === 'ms'} onclick={() => (windUnit = 'ms')}>m/s</button>
          </div>
          <!-- Compact legends so the field colours / arrow encoding read without guessing. -->
          {#if wxShow.temp}
            <div class="fp-wx-leg"><span class="fp-wx-leg-bar"></span><span class="fp-wx-leg-ends"><span>−10°</span><span>40°</span></span></div>
          {/if}
          {#if wxShow.wind}
            <div class="fp-wx-legrow"><span class="fp-wx-legarrow">➤</span>{t('length & thickness = average; faded = gust')}</div>
          {/if}
          {#if wxShow.rain}
            <div class="fp-wx-legrow">💧–💧💧💧💧 {t('more drops = heavier rain')}</div>
          {/if}
          <!-- Forecast time — planning is forward-looking, so pick a future hour. -->
          <label class="fp-wx-time" for="fp-wx-time-range">{t('Forecast')}: <span class="fp-wx-time-val">{wxTimeLabel(wxHourOffset)}</span></label>
          <input id="fp-wx-time-range" class="fp-wx-range" type="range" min="0" max="48" step="1" bind:value={wxHourOffset} aria-label={t('Forecast time')} />
          {#if wxPaused}
            <div class="fp-wx-paused" role="status">⏸ {t('Weather paused — daily request limit reached; showing cached data.')}</div>
          {/if}
        </div>
      {/if}
      {/if}
      {#if caps.terrain}
      <div class="fp-sec">{t('Terrain')}</div>
      <label><input type="checkbox" bind:checked={terrain.elevation} /> <i class="ter-i" aria-hidden="true">⛰</i>{t('Elevation colors')}</label>
      <label><input type="checkbox" bind:checked={terrain.hillshade} /> <i class="ter-i" aria-hidden="true">◔</i>{t('Hillshade relief')}</label>
      <label><input type="checkbox" bind:checked={terrain.contours} /> <i class="ter-i" aria-hidden="true">≋</i>{t('Contour lines')}</label>
      {/if}

      <!-- Footer: freshness, manual refresh, global settings. -->
      <div class="fp-foot">
        {#if indexing}
          <span class="fp-fresh">{t('Indexing zones…')} {indexProgress ? indexProgress.toLocaleString() : ''}</span>
        {:else if dataError && !updatedAt}
          <span class="fp-fresh err">⚠ {t('Zone data unavailable')}</span>
        {:else if updatedAt}
          <span class="fp-fresh" class:err={zonesAt == null} class:warn={zonesAt != null && (!notamsOk || !zonesOk || notamsWithheld > 0)}>
            {#if zonesAt == null}
              ⚠ {t('Data age unknown')}
            {:else}
              {!notamsOk || !zonesOk || notamsWithheld > 0 ? '⚠ ' : ''}{t('Zones')}: {ageLabel(zonesAt)}
            {/if}
          </span>
        {/if}
        <button class="fp-foot-btn" class:spin={refreshing} title={t('Refresh data')} aria-label={t('Refresh data')} disabled={refreshing || indexing} onclick={() => void manualRefresh()}>⟳</button>
        {#if feat.host}
          <button class="fp-foot-btn" title={t('Settings')} aria-label={t('Settings')} onclick={() => app.floatSourceAt('set:app', 0.32, 0.1, 0.36, 0.72)}>⚙</button>
        {:else}
          <button
            class="fp-foot-btn fp-lang"
            title={i18n.lang === 'tr' ? 'Switch to English' : 'Türkçeye geç'}
            aria-label={i18n.lang === 'tr' ? 'Switch to English' : 'Türkçeye geç'}
            onclick={() => (i18n.lang = i18n.lang === 'tr' ? 'en' : 'tr')}>{i18n.lang === 'tr' ? 'EN' : 'TR'}</button>
        {/if}
      </div>
      </div>
    </div>
  {/if}

  <!-- All-flights reference table (top-right). A sortable/filterable directory of every flight; click a
       row to fly to it + open its detail. A "power" surface for when you need to find a specific flight. -->
  {#if showFlightsTable}
    <div class="fp-table" role="group" aria-label={t('All media table')} style="width:{tableW}px" use:draggable>
      <div class="fp-head" title={t('Drag to move')}>
        <span class="fp-head-grip" aria-hidden="true">⠿⠿⠿</span>
        <!-- Breakdown, not just a total: with two kinds in one list "(412)" alone doesn't say what you
             are looking at. -->
        <span class="fp-head-title">▦ {t('All media')} <span class="dim">({mediaTableRows.length})</span> <span class="dim fp-tbl-mix">🎥 {rowCounts.video} · 📷 {rowCounts.photo}</span></span>
        <button class="fp-facet-pop-x" title={t('Dismiss')} aria-label={t('Dismiss')} onclick={() => (showFlightsTable = false)}>✕</button>
      </div>
      <div class="fp-table-body">
        <input class="fp-table-filter" type="search" placeholder={t('Filter media…')} aria-label={t('Filter media')} bind:value={flightsTableQuery} />
        <div class="fp-table-scroll" style="max-height:{tableScrollH}px">
          <table class="fp-tbl">
            <thead>
              <tr>
                <th class="thc" aria-label={t('Preview')}></th>
                <th class="s" onclick={() => sortFlights('title')}>{t('Capture')}{sortArrow('title')}</th>
                <th class="s" onclick={() => sortFlights('date')}>{t('Date')}{sortArrow('date')}</th>
                <th class="s" title={t('Clock time + sun phase — sorts by time of day across all dates')} onclick={() => sortFlights('tod')}>{t('Time')}{sortArrow('tod')}</th>
                <th class="s num" onclick={() => sortFlights('durationS')}>{t('Duration')}{sortArrow('durationS')}</th>
                <th class="s num" onclick={() => sortFlights('distanceKm')}>{t('Distance')}{sortArrow('distanceKm')}</th>
                <th class="s num" onclick={() => sortFlights('altMax')}>{t('Max alt')}{sortArrow('altMax')}</th>
              </tr>
            </thead>
            <tbody>
              {#each mediaTableRows as r (r.id)}
                {@const tf = mediaTime.get(r.id)}
                <tr class:on={r.kind === 'video' ? flightDetail?.id === r.id : photoView?.id === r.id} onclick={() => goToMediaRow(r)}>
                  <td class="thumb">
                    {#if r.kind === 'video'}
                      <!-- Poster + hover-scrub storyboard: sweep across it to preview the whole flight. -->
                      <div class="fp-tbl-thumb" use:lazyThumb={r.f} role="img" aria-label={t('Storyboard')} onmousemove={(e) => scrubThumb(r.f, e)} onmouseleave={() => { if (hoverThumb?.id === r.id) hoverThumb = null; }}>
                        {#if thumbFrame(r.f)}
                          <img src={thumbFrame(r.f)} alt="" />
                          {#if hoverThumb?.id === r.id && thumbCache[r.id]?.length}
                            <div class="fp-tbl-scrubbar" style="width:{((hoverThumb.i + 0.5) / thumbCache[r.id].length) * 100}%"></div>
                          {/if}
                        {:else}
                          <div class="fp-tbl-thumb-ph">{r.f.video ? '🛩' : '—'}</div>
                        {/if}
                      </div>
                    {:else}
                      <!-- A photo already carries its EXIF thumbnail, so there is nothing to generate or
                           scrub — one static frame. -->
                      <div class="fp-tbl-thumb">
                        {#if r.p.thumb}<img src={r.p.thumb} alt="" />{:else}<div class="fp-tbl-thumb-ph">📷</div>{/if}
                      </div>
                    {/if}
                  </td>
                  <td class="nm">
                    <!-- Kind is carried by an icon with a text tooltip, never by colour alone. -->
                    {#if r.kind === 'video'}
                      <span class="fp-tbl-dot" style="background:{flightColor(r.id)}"></span>
                    {:else}
                      <span class="fp-tbl-kind" title={t('Photo')}>📷</span>
                    {/if}
                    <span class="fp-tbl-nm"><span class="fp-tbl-t">{mediaRowTitle(r)}</span><span class="fp-tbl-sub">{mediaRowSub(r)}{#if r.kind === 'photo' && r.p.raw} · RAW{/if}</span></span>
                  </td>
                  <td>{mediaRowDate(r) || '—'}</td>
                  <!-- Icon carries the phase, text carries the phase name in the tooltip — colour is
                       never the only signal, and the clock time stays readable on its own. -->
                  <td class="fp-tbl-when" title={tf?.phase ? `${t(sunPhaseLabel(tf.phase))}` : ''}>{#if tf}{fmtTod(tf)}{#if tf.phase} <span class="fp-tbl-ph">{sunPhaseIcon(tf.phase)}</span>{/if}{:else}—{/if}</td>
                  <!-- A photo has no duration / distance / altitude; '—' says so rather than showing 0. -->
                  <td class="num">{r.kind === 'video' && r.f.durationS ? fmtDuration(r.f.durationS) : '—'}</td>
                  <td class="num">{r.kind === 'video' && r.f.distanceKm ? r.f.distanceKm.toFixed(2) : '—'}</td>
                  <td class="num">{r.kind === 'video' && r.f.altMax ? Math.round(r.f.altMax) : '—'}</td>
                </tr>
              {/each}
              {#if !mediaTableRows.length}
                <tr><td colspan="7" class="fp-tbl-empty">{mediaFilterActive ? t('Nothing matches — check the media filters in the panel.') : t('No flights match')}</td></tr>
              {/if}
            </tbody>
          </table>
        </div>
      </div>
      <div class="fp-table-resize" use:resizeTable title={t('Resize')} aria-hidden="true">⤡</div>
    </div>
  {/if}

  <!-- Info card (top-left) — appears on any click, even inside a zone; carries zone details + verdict. -->
  {#if feat.assessment && assessPoint}
    <div class="fp-card" aria-live="polite" use:draggable>
      <div class="fp-drag" title={t('Drag to move')} aria-hidden="true">⠿⠿⠿</div>
      <button class="fp-card-x" title={t('Dismiss')} aria-label={t('Dismiss')} onclick={closeAssess}>✕</button>

      {#if assessErr}
        <div class="verdict err"><span class="vicon" aria-hidden="true">!</span><span>{t('Could not assess this point')}</span></div>
        <p class="errmsg">{assessErr}</p>
      {:else if assessment}
        {@const lv = LEVEL[assessment.level] ?? LEVEL.marginal}
        <div class="verdict {lv.cls}"><span class="vicon" aria-hidden="true">{lv.icon}</span><span>{t(lv.label)}</span></div>
      {:else}
        <div class="verdict pending"><span class="vicon" aria-hidden="true">…</span><span>{t('Checking weather…')}</span></div>
      {/if}

      <div class="coord"><span aria-hidden="true">📍</span> {assessPoint.lat.toFixed(5)}, {assessPoint.lon.toFixed(5)}{#if elevation != null} · <span aria-hidden="true">⛰</span> {Math.round(elevation)} m{/if}</div>

      {#if pointZones.length || pointNotams.length}
        <section class="block">
          <h3>{t('Zones here')}</h3>
          <ul class="zlist">
            {#each pointZones as z (z.id)}
              <li><span class="zdot" style="background:{z.area_type === 909 ? '#ef4444' : '#22c55e'}"></span><span class="zn">{clean(z.name) || zoneAreaLabel(z.area_type)}</span><span class="zc">{zoneAreaLabel(z.area_type)}{#if z.authority != null} · {zoneAuthorityLabel(z.authority)}{/if}</span></li>
            {/each}
            {#each pointNotams as n (n.id)}
              <li><span class="zdot" style="background:#f59e0b"></span><span class="zn">{clean(n.description || n.name)}</span><span class="zc">{notamTypeLabel(n.area_type)}</span></li>
            {/each}
          </ul>
        </section>
      {:else}
        <p class="ok-note"><span class="zdot" style="background:#22c55e"></span>{t('No airspace zone at this point.')}</p>
      {/if}

      {#if weather}
        <dl class="wx">
          <div class:flag={flagged('wind')}><dt>{t('Wind')}</dt><dd>{fmtWind(weather.wind_speed_ms)}{#if flagged('wind')} <span aria-hidden="true">⚠</span>{/if}</dd></div>
          <div class:flag={flagged('gust')}><dt>{t('Gust')}</dt><dd>{fmtWind(weather.wind_gust_ms)}{#if flagged('gust')} <span aria-hidden="true">⚠</span>{/if}</dd></div>
          <div class:flag={flagged('precipitation')}><dt>{t('Precipitation')}</dt><dd>{weather.precipitation_mm.toFixed(1)} mm{#if flagged('precipitation')} <span aria-hidden="true">⚠</span>{/if}</dd></div>
          <div class:flag={flagged('visibility')}><dt>{t('Visibility')}</dt><dd>{(weather.visibility_m / 1000).toFixed(1)} km{#if flagged('visibility')} <span aria-hidden="true">⚠</span>{/if}</dd></div>
        </dl>
      {/if}

      <p class="disclaimer">{t('Advisory only. SHGM is the legal authority for Turkish airspace — verify at')} <a href="https://iha.shgm.gov.tr" target="_blank" rel="noopener noreferrer">iha.shgm.gov.tr</a>.</p>
    </div>
  {/if}

  <!-- Flight detail card — opens on clicking your own flight's start dot. Draggable, top-right area. -->
  <!-- Background-activity console: general async ops (zones/weather/flights/merge) + the archive scan's
       fine-grained progress, both in one bottom-left panel so nothing the map does off-thread is invisible. -->
  <!-- Bottom-left column: the transient background-activity console above, the always-on map key
       below. They used to be two independent `left: 12px; bottom: 12px` boxes, so for the seconds
       the console lingers after the zones land it sat directly on top of the key — which is exactly
       the moment a first-time visitor is working out what red and green mean. In a column they
       cannot collide, and with the key last it holds the same spot whether the console is showing
       or not. The wrapper deliberately sets no z-index: with `z-index: auto` it creates no stacking
       context, so each child keeps the layer it had against the rest of the pane (the console above
       the flight card, the key below it). -->
  <div class="fp-bl">
    {#if consoleOpen}
      {@const pct = scanStatus?.total ? Math.round((scanStatus.done / scanStatus.total) * 100) : 0}
      <div class="fp-scan" role="status" aria-live="polite">
        <div class="fp-scan-head">
          <span class="fp-scan-spin" class:spin={anyRunning} aria-hidden="true"></span>
          <span class="fp-scan-title">{scanStatus?.scanning ? t('Scanning archive…') : scanStatus ? t('Scan complete') : anyRunning ? t('Working…') : t('Background activity')}</span>
          {#if scanStatus}<span class="fp-scan-count">{scanStatus.done}/{scanStatus.total}</span>{/if}
          <button class="fp-scan-x" title={t('Dismiss')} aria-label={t('Dismiss')} onclick={() => { scanStatus = null; scanDismissed = true; activity = []; activityDismissed = true; }}>✕</button>
        </div>
        {#if scanStatus}<div class="fp-scan-bar"><div class="fp-scan-fill" style="width:{pct}%"></div></div>{/if}
        {#if thumbStatus && thumbStatus.total > 0 && (thumbStatus.done < thumbStatus.total || thumbStatus.active > 0)}
          {@const tpct = thumbStatus.total ? Math.round((thumbStatus.done / thumbStatus.total) * 100) : 0}
          <div class="fp-scan-sub">
            <span class="fp-scan-spin" class:spin={thumbStatus.pending > 0 || thumbStatus.active > 0} aria-hidden="true"></span>
            <span>{thumbStatus.pending > 0 || thumbStatus.active > 0 ? t('Preparing storyboards…') : t('Storyboards ready')}</span>
            <span class="fp-scan-count">{thumbStatus.done}/{thumbStatus.total}</span>
          </div>
          <div class="fp-scan-bar"><div class="fp-scan-fill" style="width:{tpct}%"></div></div>
        {/if}
        <ul class="fp-scan-log">
          {#each activity as a (a.id)}
            <li><span class="fp-act-ico {a.state}" aria-hidden="true">{a.state === 'run' ? '⋯' : a.state === 'ok' ? '✓' : '✕'}</span> {a.label}</li>
          {/each}
          {#if scanStatus}{#each scanStatus.recent.slice(-4) as line}<li class="fp-act-scan">{line}</li>{/each}{/if}
        </ul>
      </div>
    {/if}
    <!-- Always-on map key.
         The legend existed in exactly two places, both out of reach on a phone: inside the filters
         panel, which starts collapsed below 700px, and inside a dialog titled "Keyboard shortcuts"
         behind a ? button, which a device with no keyboard has no reason to open. So a first-time
         touch visitor got red, green and amber shapes and nothing saying what they mean — on a map
         whose entire purpose is that one distinction.

         Shown only while the panel is closed, because the panel carries the same swatches beside its
         checkboxes; two keys on one screen is clutter, not redundancy. Rows follow the live layer
         state, so the key never names a layer that is not drawn — NOTAM especially, which is withheld
         whenever the data is too old to trust, and a key still listing it would contradict the warning
         that withdrew it. pointer-events:none: it is text, and it sits over a map you drag. -->
    {#if dataReady && !showFilters && (layers.nofly || layers.allowed || (layers.notam && notamsOk))}
      <div class="fp-legend" role="note" aria-label={t('Map legend')}>
        {#if layers.nofly}<span><i class="zi-hatch" style="background:#ef4444"></i>{t('No-fly')}</span>{/if}
        {#if layers.allowed}<span><i style="background:#22c55e"></i>{t('Allowed')}</span>{/if}
        {#if layers.notam && notamsOk}<span><i style="background:#f59e0b"></i>{t(notamLayerLabel)}</span>{/if}
      </div>
    {/if}
  </div>

  {#if photoView}
    {@const p = photoView}
    <div class="fp-lightbox" role="dialog" aria-modal="true" aria-label={p.name}>
      <!-- Full-screen dismiss target behind the image (a real button → keyboard + screen-reader reachable). -->
      <button class="fp-lightbox-backdrop" aria-label={t('Dismiss')} onclick={() => (photoView = null)}></button>
      <figure class="fp-lightbox-fig">
        {#if p.raw && !p.thumb}
          <div class="fp-lightbox-raw">{t('RAW file — no in-browser preview')}</div>
        {:else}
          <!-- Full-res via the server; fall back to the embedded EXIF thumbnail if the endpoint is absent. -->
          <img
            class="fp-lightbox-img"
            src={`/api/photo?p=${encodeURIComponent(p.path)}`}
            alt={p.name}
            onerror={(e) => { const el = e.currentTarget as HTMLImageElement; if (p.thumb && el.src !== p.thumb) el.src = p.thumb; }}
          />
        {/if}
        <figcaption class="fp-lightbox-cap">
          <span class="fp-lightbox-name">{p.name}{#if !p.present} · {t('archived')}{/if}</span>
          <span class="fp-lightbox-meta">
            {#if p.takenAt}{p.takenAt.replace('T', ' · ').slice(0, 19)}{/if}{#if p.model} · {p.model}{/if}{#if p.focalLen} · {Math.round(p.focalLen)}mm{/if}{#if p.fnum} · ƒ{p.fnum}{/if}{#if p.shutter} · {fmtShutter(p.shutter)}{/if}{#if p.iso} · ISO {p.iso}{/if}{#if p.absAlt != null} · ⛰ {Math.round(p.absAlt)} m{/if}
          </span>
        </figcaption>
      </figure>
      <button class="fp-lightbox-x" title={t('Dismiss')} aria-label={t('Dismiss')} onclick={() => (photoView = null)}>✕</button>
    </div>
  {/if}
  {#if flightDetail}
    {@const f = flightDetail}
    <div class="fp-flight" use:draggable style="width:{flightCardW}px;{flightCardPos ? ` left:${flightCardPos.left}px; top:${flightCardPos.top}px; right:auto; bottom:auto;` : ''}">
      <div class="fp-drag" title={t('Drag to move')} aria-hidden="true">⠿⠿⠿</div>
      <button class="fp-card-x" title={t('Dismiss')} aria-label={t('Dismiss')} onclick={closeFlightDetail}>✕</button>
      <div class="fp-flight-head">
        <div class="fp-flight-name">{flightTitle(f)}</div>
        <div class="fp-flight-sub">
          {#if f.videoDesc}{f.name} · {/if}{f.clipName}{#if f.date} · {f.date}{/if}
          {#if f.startTime} · {hhmm(f.startTime)}{#if f.endTime && f.endTime !== f.startTime}–{hhmm(f.endTime)}{/if}{/if}
        </div>
      </div>
      <div class="fp-flight-body" style="max-height:{flightBodyMaxH}px">
      <div class="fp-flight-stats">
        {#if f.durationS}<span><b>{fmtDuration(f.durationS)}</b></span>{/if}
        {#if f.distanceKm}<span><b>{f.distanceKm.toFixed(f.distanceKm < 10 ? 1 : 0)}</b> km</span>{/if}
        {#if f.altMax}<span><b>{Math.round(f.altMax)}</b> m {t('max alt')}</span>{/if}
      </div>
      {#if f.meta}
        <div class="fp-flight-meta">
          {#if f.meta.width && f.meta.height}<span>{f.meta.width}×{f.meta.height}</span>{/if}
          {#if f.meta.fps}<span>{f.meta.fps} fps</span>{/if}
          {#if f.meta.codec}<span>{f.meta.codec.toUpperCase()}</span>{/if}
          {#if f.meta.bitrateMbps}<span>{f.meta.bitrateMbps} Mbps</span>{/if}
        </div>
      {/if}

      {#if f.video}
        <!-- svelte-ignore a11y_media_has_caption -->
        {#if cardGraded}
          <!-- D-Log: hidden <video> drives audio/timing; the WebGL-graded frames show on the canvas. -->
          <canvas class="fp-flight-video" bind:this={cardCanvasEl}></canvas>
          <!-- svelte-ignore a11y_media_has_caption -->
          <video bind:this={flightVideoEl} src={flightVideoUrl(f)} preload="metadata" style="display:none" ontimeupdate={onFlightTimeUpdate} onseeked={onFlightTimeUpdate} onplay={onCardPlay} onpause={onCardPause} onloadedmetadata={onCardMeta}></video>
          <div class="fp-flight-ctrls">
            <button type="button" class="fp-flight-play" onclick={toggleCardPlay} aria-label={cardPlaying ? t('Pause') : t('Play')}>{cardPlaying ? '⏸' : '▶'}</button>
            <input class="fp-flight-seek" type="range" min="0" max={cardDur || 0} step="0.1" value={cardTime} oninput={onCardSeek} aria-label={t('Seek')} />
            <span class="fp-flight-time">{fmtDuration(cardTime)} / {fmtDuration(cardDur)}</span>
          </div>
        {:else}
          <!-- svelte-ignore a11y_media_has_caption -->
          <video class="fp-flight-video" bind:this={flightVideoEl} src={flightVideoUrl(f)} controls preload="metadata" ontimeupdate={onFlightTimeUpdate} onseeked={onFlightTimeUpdate}></video>
        {/if}
        {#if flightFrames.length}
          <!-- Storyboard: a scrollable frame strip with key-moment pins (takeoff / max-alt / farthest /
               landing) overlaid at their time positions. Click a frame OR a pin to seek the video there. -->
          <div class="fp-story" role="group" aria-label={t('Storyboard')}>
            <div class="fp-story-inner">
              {#if flightEvents.length}
                <div class="fp-story-pins">
                  {#each flightEvents as ev (ev.label)}
                    <button type="button" class="fp-story-pin" style="left:{ev.frac * 100}%" title="{ev.label} · {fmtDuration(ev.t)}" aria-label="{ev.label} · {fmtDuration(ev.t)}" onclick={() => seekFlightVideo(ev.t)}>{ev.icon}</button>
                  {/each}
                </div>
              {/if}
              <div class="fp-story-frames">
                {#each flightFrames as fr (fr.t)}
                  <button type="button" class="fp-frame" title={fmtDuration(fr.t)} onclick={() => seekFlightVideo(fr.t)}>
                    <img src={fr.url} alt="" />
                  </button>
                {/each}
              </div>
            </div>
          </div>
        {:else if flightFramesLoading}
          <p class="fp-flight-fnote loading"><span class="fp-spin" aria-hidden="true"></span> {t('Preparing storyboard…')}</p>
        {:else if flightFramesError}
          <p class="fp-flight-fnote"><span aria-hidden="true">⚠</span> {flightFramesError}</p>
        {/if}
      {:else}
        <p class="fp-flight-novideo">{t('No video file found next to this clip.')}</p>
      {/if}

      <!-- All-sources panel: every fact about this clip, grouped by where it came from (folder/file name,
           SRT telemetry, video metadata, detected colour profile). Collapsed by default to keep the card
           compact; the summary line stays visible so it's one click away. -->
      <details class="fp-sources">
        <summary>{t('Sources')}</summary>
        <div class="fp-src-grid">
          <div class="fp-src-grp">
            <div class="fp-src-h">📁 {t('Folder & file')}</div>
            <div class="fp-src-r"><span>{t('Folder')}</span><b>{f.folder}</b></div>
            {#if f.video}<div class="fp-src-r"><span>{t('File')}</span><b>{videoFileName(f.video)}</b></div>{/if}
            <div class="fp-src-r"><span>{t('Clip')}</span><b>{f.clipName}</b></div>
            {#if f.videoDesc}<div class="fp-src-r"><span>{t('Description')}</span><b>{f.videoDesc}</b></div>{/if}
            <div class="fp-src-r"><span>{t('Date/time')}</span><b>{f.date}{#if f.startTime} · {hhmm(f.startTime)}{/if}</b></div>
          </div>
          <div class="fp-src-grp">
            <div class="fp-src-h">🛰 {t('SRT telemetry')}</div>
            <div class="fp-src-r"><span>{t('GPS points')}</span><b>{f.samples.length}</b></div>
            {#if f.altMin != null && f.altMax != null}<div class="fp-src-r"><span>{t('Altitude')}</span><b>{Math.round(f.altMin)}–{Math.round(f.altMax)} m</b></div>{/if}
            {#if f.distanceKm}<div class="fp-src-r"><span>{t('Distance')}</span><b>{f.distanceKm.toFixed(2)} km</b></div>{/if}
            {#if f.durationS}<div class="fp-src-r"><span>{t('Duration')}</span><b>{fmtDuration(f.durationS)}</b></div>{/if}
          </div>
          {#if f.meta}
            <div class="fp-src-grp">
              <div class="fp-src-h">📹 {t('Video metadata')}</div>
              {#if f.meta.width && f.meta.height}<div class="fp-src-r"><span>{t('Resolution')}</span><b>{f.meta.width}×{f.meta.height}</b></div>{/if}
              {#if f.meta.fps}<div class="fp-src-r"><span>FPS</span><b>{f.meta.fps}</b></div>{/if}
              {#if f.meta.codec}<div class="fp-src-r"><span>{t('Codec')}</span><b>{f.meta.codec.toUpperCase()}</b></div>{/if}
              {#if f.meta.bitrateMbps}<div class="fp-src-r"><span>{t('Bitrate')}</span><b>{f.meta.bitrateMbps} Mbps</b></div>{/if}
            </div>
          {/if}
          {#if colorProfile(f.clipName)}
            <div class="fp-src-grp">
              <div class="fp-src-h">🎨 {t('Colour profile')}</div>
              <div class="fp-src-r"><span>{t('Detected')}</span><b>{colorProfile(f.clipName)}</b></div>
            </div>
          {/if}
        </div>
      </details>

      {#if siblingClips(f).length > 1}
        <div class="fp-flight-clips">
          {#each siblingClips(f) as c (c.id)}
            <button type="button" class="fp-clip" class:on={f.id === c.id} onclick={() => selectFlight(c)}>
              <span class="fp-clip-ic" aria-hidden="true">{c.video ? '🎞' : '📄'}</span>
              <span class="fp-clip-nm">{c.clipName}</span>
              {#if c.durationS}<span class="fp-clip-dur">{fmtDuration(c.durationS)}</span>{/if}
            </button>
          {/each}
        </div>
      {/if}
      </div>
      <div class="fp-flight-grip" use:resizeFlightCard title={t('Drag to resize')} aria-hidden="true"></div>
    </div>
  {/if}

  <!-- Keyboard-shortcuts help overlay. -->
  {#if shortcutsOpen}
    <div class="fp-keys" role="dialog" aria-label={t('Keyboard shortcuts')}>
      <div class="fp-keys-head">{t('Keyboard shortcuts')} <button class="fp-facet-pop-x" title={t('Dismiss')} aria-label={t('Dismiss')} onclick={() => (shortcutsOpen = false)}>✕</button></div>
      <ul>
        <li><kbd>/</kbd> {t('Focus search')}</li>
        <li><kbd>↑</kbd><kbd>↓</kbd> {t('Move through results')}</li>
        <li><kbd>↵</kbd> {t('Go to highlighted result')}</li>
        <li><kbd>f</kbd> {t('Layers & filters')}</li>
        <li><kbd>e</kbd> {t('Effective zone (merge overlaps)')}</li>
        {#if feat.weather}<li><kbd>w</kbd> {t('Weather')}</li>{/if}
        {#if feat.archive}<li><kbd>l</kbd> {t('My flights')}</li>{/if}
        {#if feat.archive}<li><kbd>t</kbd> {t('All media table')}</li>{/if}
        <li><kbd>3</kbd> {t('3D terrain & buildings')}</li>
        <li><kbd>Ctrl</kbd>+{t('drag')} {t('Rotate / tilt (2D↔3D)')}</li>
        <li><kbd>Cmd</kbd>+{t('click')} {t('Isolate a filter pill (toggle the rest)')}</li>
        <li><kbd>Esc</kbd> {t('Close popovers')}</li>
      </ul>
      <div class="fp-keys-legend">
        <div class="fp-keys-legtitle">{t('Map legend')}</div>
        <ul>
          <li><span class="zdot zi-hatch" style="background:#ef4444"></span>{t('No-fly (red)')}</li>
          <li><span class="zdot" style="background:#22c55e"></span>{t('Allowed (green)')}</li>
          <li><span class="zdot" style="background:#f59e0b"></span>{t(notamLayerLabel)}</li>
          <li><span class="zdot zdot-o" style="background:#22d3ee"></span>{t('My flights')}</li>
          <li><span class="zdot zdot-o" style="background:#94a3b8"></span>{t('Places')}</li>
        </ul>
      </div>
    </div>
  {/if}
</div>

<style>
  .fp-pane { height: 100%; position: relative; background: var(--bg); overflow: hidden; }
  .map { position: absolute; inset: 0; z-index: 0; } /* stacking context: keep Leaflet's panes (z 200-700) below the overlay panels */

  /* z-index 9 clears the pane drag-strip (.pane-bar, z-index 8) so these stay clickable. */
  /* Cap width so the centered search never collides with the right-hand control cluster (~270px). */
  .fp-search { position: absolute; top: 10px; left: 50%; transform: translateX(-50%); z-index: 12; width: clamp(280px, calc(100vw - 520px), 480px); }
  .fp-search-ic { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); font-size: 1rem; pointer-events: none; opacity: 0.8; z-index: 1; }
  .fp-search input { width: 100%; box-sizing: border-box; border: 1px solid var(--line-strong); border-radius: 11px; background: var(--surface); color: var(--text); padding: 12px 14px 12px 38px; font-size: 1rem; box-shadow: 0 2px 12px rgba(0,0,0,0.32); }
  .fp-search.open input { border-radius: 9px 9px 0 0; border-bottom-color: transparent; }
  .fp-search input:focus { outline: 2px solid var(--accent); outline-offset: 0; }
  .fp-search-busy { position: absolute; right: 12px; top: 8px; color: var(--text-dim); z-index: 1; }
  /* Unified palette dropdown. */
  .fp-palette { position: absolute; top: 100%; left: 0; right: 0; margin-top: -1px; background: var(--surface); border: 1px solid var(--line); border-radius: 0 0 10px 10px; box-shadow: 0 6px 20px rgba(0,0,0,0.35); overflow: hidden; }
  /* Each filter section is a titled frame (fieldset+legend) so it reads as one OR'd group; the two frames
     stack, and the second (Narrow down) visibly ANDs with the first. */
  .fp-pal-fs { min-inline-size: 0; margin: 8px 9px 0; padding: 2px 9px 8px; border: 1px solid var(--line-strong); border-radius: 9px; }
  .fp-pal-fs:last-of-type { margin-bottom: 2px; }
  .fp-pal-legend { display: flex; align-items: center; gap: 8px; padding: 0 5px; font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-dim); }
  .fp-pal-cluster { display: flex; align-items: flex-start; gap: 8px; padding-top: 5px; }
  .fp-pal-clbl { flex-shrink: 0; width: 66px; padding-top: 7px; font-size: 0.66rem; color: var(--text-dim); text-align: right; }
  .fp-pal-chips { display: flex; flex-wrap: wrap; gap: 5px; flex: 1; min-width: 0; }
  .fp-pal-chip { display: inline-flex; align-items: center; gap: 5px; min-height: 28px; border: 1px solid var(--line-strong); border-radius: 999px; background: var(--surface-2); color: var(--text-soft); font-size: 0.76rem; padding: 3px 11px; cursor: pointer; }
  .fp-pal-chip.on { background: var(--accent-strong); color: #fff; border-color: var(--accent-strong); }
  .fp-chip-dot { width: 9px; height: 9px; border-radius: 3px; flex-shrink: 0; }
  /* Count badge: how many objects match this chip. Muted on idle chips, translucent-light on lit ones. */
  .fp-chip-n { font-size: 0.66rem; font-variant-numeric: tabular-nums; color: var(--text-dim); background: color-mix(in srgb, var(--text-dim) 18%, transparent); border-radius: 999px; padding: 0 5px; line-height: 1.5; margin-left: 1px; }
  .fp-pal-chip.on .fp-chip-n { color: rgba(255,255,255,0.92); background: rgba(255,255,255,0.22); }
  .fp-cat-chip { font-size: 0.72rem; }
  .fp-cat-clear { border: none; background: none; color: var(--text-dim); font-size: 0.66rem; cursor: pointer; padding: 2px 6px; min-height: 22px; text-transform: none; }
  .fp-cat-clear:hover { color: var(--text); }
  .fp-pal-body { max-height: 60vh; overflow-y: auto; padding: 2px 6px 8px; }
  .fp-index-ic { width: 20px; text-align: center; font-size: 0.95rem; line-height: 1; flex-shrink: 0; }

  .fp-tr { position: absolute; top: 10px; right: 10px; z-index: 9; display: flex; gap: 6px; align-items: center; }
  .fp-seg { display: flex; border: 1px solid var(--line-strong); border-radius: 7px; overflow: hidden; background: var(--surface); box-shadow: 0 1px 6px rgba(0,0,0,0.25); }
  .fp-seg button { background: none; border: none; border-left: 1px solid var(--line-strong); color: var(--text-soft); padding: 6px 12px; font-size: 0.78rem; cursor: pointer; }
  .fp-seg button:first-child { border-left: none; }
  .fp-seg button.on { background: var(--accent-strong); color: #fff; }
  .fp-icon { width: 30px; height: 30px; border-radius: 7px; border: 1px solid var(--line-strong); background: var(--surface); color: var(--text-soft); font-size: 0.95rem; cursor: pointer; box-shadow: 0 1px 6px rgba(0,0,0,0.25); }
  .fp-icon:hover { color: var(--text); background: var(--surface-2); }
  .fp-icon.on { background: var(--accent-strong); color: #fff; border-color: var(--accent-strong); }
  /* The exit button leaves the whole tool — set it apart so it isn't a mis-tap neighbour of the toggles. */
  .fp-icon-exit { margin-left: 12px; }
  .fp-icon-exit:hover { background: #b91c1c; color: #fff; border-color: #b91c1c; }
  .fp-3d { font-size: 0.72rem; font-weight: 700; }

  /* Height is capped against the PANE, like every other floating box here (.fp-flight, .fp-card,
     .fp-facet-pop). The body used to cap itself at 76vh, which ignores where the panel starts: at
     top:106px on a phone, under a 150px site header, 76vh of body put the panel's last 95px below
     the bottom of the screen with no way to reach it. */
  .fp-filters { position: absolute; top: 48px; left: 10px; z-index: 9; max-height: calc(100% - 58px); display: flex; flex-direction: column; background: var(--surface); border: 1px solid var(--line); border-radius: 9px; box-shadow: 0 3px 14px rgba(0,0,0,0.3); overflow: visible; cursor: grab; touch-action: pan-y; }
  /* All-flights reference table — a draggable panel on the right (opposite the filters panel). */
  .fp-table { position: absolute; top: 48px; right: 10px; z-index: 9; max-width: calc(100vw - 20px); display: flex; flex-direction: column; background-color: rgba(15, 22, 33, 0.985); border: 1px solid var(--line); border-radius: 9px; box-shadow: 0 3px 14px rgba(0,0,0,0.3); cursor: grab; touch-action: pan-y; }
  .fp-table .fp-head { background-color: rgba(24, 33, 46, 0.985); }
  .fp-table.fp-dragging { cursor: grabbing; }
  .fp-table-body { display: flex; flex-direction: column; gap: 7px; padding: 9px 10px 10px; cursor: default; }
  .fp-table-filter { width: 100%; box-sizing: border-box; padding: 5px 9px; border: 1px solid var(--line-strong); border-radius: 7px; background: var(--surface-2); color: var(--text); font-size: 0.8rem; }
  .fp-table-scroll { overflow-y: auto; overflow-x: hidden; border-radius: 6px; }
  /* Bottom-left resize grip (panel is anchored top-right, so it grows down-left, staying on screen). */
  .fp-table-resize { position: absolute; left: 0; bottom: 0; width: 16px; height: 16px; display: flex; align-items: flex-end; justify-content: flex-start; color: var(--text-dim); font-size: 0.7rem; line-height: 1; cursor: nesw-resize; touch-action: none; user-select: none; transform: scaleX(-1); padding: 1px; }
  .fp-table-resize:hover { color: var(--text); }
  /* Thumbnail column: poster + hover-scrub storyboard. */
  .fp-tbl td.thumb { padding: 3px 4px 3px 7px; width: 1%; }
  .fp-tbl-thumb { position: relative; width: 96px; aspect-ratio: 16 / 9; border-radius: 4px; overflow: hidden; background: var(--surface-2); }
  .fp-tbl-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .fp-tbl-thumb-ph { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; color: var(--text-dim); font-size: 0.9rem; }
  .fp-tbl-scrubbar { position: absolute; left: 0; bottom: 0; height: 2px; background: var(--accent-strong); box-shadow: 0 0 4px var(--accent-strong); }
  .fp-tbl { width: 100%; border-collapse: collapse; font-size: 0.74rem; }
  .fp-tbl thead th { position: sticky; top: 0; z-index: 1; background-color: rgba(24, 33, 46, 0.99); color: var(--text-dim); font-weight: 600; text-align: left; padding: 5px 7px; white-space: nowrap; }
  .fp-tbl th.s { cursor: pointer; user-select: none; }
  .fp-tbl th.s:hover { color: var(--text); }
  .fp-tbl th.num, .fp-tbl td.num { text-align: right; font-variant-numeric: tabular-nums; }
  .fp-tbl td { padding: 4px 7px; border-top: 1px solid var(--line); color: var(--text-soft); vertical-align: middle; }
  .fp-tbl tbody tr { cursor: pointer; }
  .fp-tbl tbody tr:hover { background: var(--surface-2); }
  .fp-tbl tbody tr.on { background: color-mix(in srgb, var(--accent-strong) 26%, transparent); }
  .fp-tbl td.nm { max-width: 190px; }
  .fp-tbl-dot { display: inline-block; width: 8px; height: 8px; border-radius: 2px; margin-right: 6px; vertical-align: middle; flex-shrink: 0; }
  .fp-tbl-nm { display: inline-flex; flex-direction: column; vertical-align: middle; max-width: 168px; }
  .fp-tbl-nm { overflow: hidden; }
  .fp-tbl-t, .fp-tbl-sub { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fp-tbl-t { color: var(--text); }
  .fp-tbl-sub { color: var(--text-dim); font-size: 0.66rem; }
  .fp-tbl-empty { text-align: center; color: var(--text-dim); padding: 14px 0; }
  /* Clock time + sun-phase icon. Tabular figures keep the column aligned while scanning it. */
  .fp-tbl-when { white-space: nowrap; font-variant-numeric: tabular-nums; }
  .fp-tbl-ph { font-size: 0.86rem; }
  /* Photo rows carry an icon where flight rows carry their colour dot — same column width either way, so
     the name text stays aligned down the list. */
  .fp-tbl-kind { width: 9px; flex-shrink: 0; font-size: 0.72rem; line-height: 1; text-align: center; }
  .fp-tbl-mix { font-weight: 400; font-size: 0.72rem; margin-left: 2px; }
  /* Nearly-opaque backgrounds so these overlays stand off the map instead of blending into it. */
  .fp-filters, .fp-facet-pop, .fp-palette, .fp-flight, .fp-card, .fp-search input { background-color: rgba(15, 22, 33, 0.975); }
  .fp-filters .fp-head, .fp-index-gh { background-color: rgba(24, 33, 46, 0.985); }
  .fp-filters.fp-dragging, .fp-card.fp-dragging, .fp-flight.fp-dragging { cursor: grabbing; }
  /* Full-width header = the obvious drag handle (whole panel drags too, from any empty spot). */
  .fp-head { display: flex; align-items: center; gap: 8px; padding: 7px 12px; border-bottom: 1px solid var(--line); border-radius: 9px 9px 0 0; background: var(--surface-2); user-select: none; }
  .fp-head-grip { color: var(--text-dim); font-size: 0.7rem; letter-spacing: 1px; }
  .fp-head-title { flex: 1; font-size: 0.78rem; font-weight: 600; color: var(--text-soft); }
  .fp-filters-body { display: flex; flex-direction: column; gap: 8px; padding: 9px 12px 10px; min-height: 0; overflow-y: auto; cursor: default; }
  /* The floating panels carried `touch-action: none` so a pointer-drag on their chrome moved the box
     instead of panning the page. But the behaviour allowed for a touch gesture is the INTERSECTION
     of touch-action down the whole ancestor chain: `none` on the panel disabled panning for
     everything inside it, and no value on the body could hand it back. So the scrolling bodies could
     not be scrolled by a finger — while the drag handler goes out of its way to exclude exactly
     those elements (DRAG_IGNORE) so that they can. The filter list is taller than a phone screen, so
     what this cost was the bottom of the legend.

     Mechanism, not measurement: this could not be confirmed by running it. Touch panning is decided
     by the compositor, and in a headless browser the panel's scroll hit-test data does not refresh
     until some later style change forces a recomposite — so a swipe reads as "no scroll" whatever
     the CSS says, and reads as "scrolled" after any unrelated mutation. Re-check on a real phone. */
  .fp-table-body, .fp-story, .fp-flight-clips { touch-action: pan-x pan-y; }
  /* The drag affordances keep `none` so a pointer-drag on them still moves the box: touch-action
     restrictions only ever TIGHTEN down the tree, so `none` on a child of a `pan-y` box works,
     while the reverse — the arrangement this replaced — does not. */
  .fp-filters .fp-head, .fp-table .fp-head { touch-action: none; }
  /* Searchable on-map data directory. */
  .fp-index-empty { font-size: 0.78rem; color: var(--text-dim); padding: 8px 6px; margin: 0; }
  .fp-index-credit {
    margin: 6px 10px 2px;
    font-size: 10.5px;
    color: var(--muted);
    opacity: 0.75;
  }
  .fp-index-err { font-size: 0.78rem; color: var(--warn); padding: 8px 6px; margin: 0; }
  .fp-index-group { margin-top: 4px; }
  .fp-index-gh { font-size: 0.72rem; font-weight: 700; color: var(--text-soft); letter-spacing: 0.4px; padding: 5px 6px 3px; position: sticky; top: 0; background: var(--surface); }
  .fp-index-row { display: flex; align-items: center; gap: 8px; width: 100%; text-align: left; background: none; border: none; border-radius: 6px; padding: 6px; cursor: pointer; color: var(--text-soft); }
  .fp-index-row:hover { background: var(--surface-2); color: var(--text); }
  /* Keyboard-highlighted row (↑/↓): distinct from hover so both can show at once. */
  .fp-index-row.active { background: var(--surface-2); color: var(--text); outline: 2px solid var(--accent); outline-offset: -2px; }
  .fp-index-dot { width: 10px; height: 10px; border-radius: 3px; flex-shrink: 0; }
  .fp-index-txt { display: flex; flex-direction: column; min-width: 0; }
  .fp-index-lbl { font-size: 0.8rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fp-index-sub { font-size: 0.68rem; color: var(--text-dim); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fp-index-more { font-size: 0.68rem; color: var(--text-dim); padding: 4px 6px; }
  .fp-filters label { display: flex; align-items: center; gap: 7px; min-height: 26px; font-size: 0.82rem; color: var(--text-soft); cursor: pointer; }
  .fp-filters input { cursor: pointer; }
  .fp-filters i { width: 11px; height: 11px; border-radius: 3px; display: inline-block; }
  /* The swatch repeats BOTH signals the map uses for a no-fly area — the red and the hatch — because
     a key that shows only one of them tells a colour-blind reader nothing about the texture they can
     actually see. The stripe angle and period match the map's pattern. */
  /* On a pseudo-element, not on the swatch itself: the swatch carries its colour as an INLINE
     `background:` shorthand, and the shorthand resets background-image to none at a specificity no
     stylesheet rule can beat. The first version set background-image here and rendered a plain red
     square. 135deg matches the map pattern's stroke direction (bottom-left to top-right). */
  :global(.zi-hatch) { position: relative; }
  :global(.zi-hatch)::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-image: repeating-linear-gradient(135deg, rgba(9, 14, 24, 0.72) 0 1.5px, transparent 1.5px 4px);
  }
  .fp-filters .wind-i, .fp-filters .ter-i { width: auto; height: auto; color: var(--text-soft); font-size: 0.8rem; }
  .fp-filters .dim { color: var(--text-dim); }

  .fp-filters .fp-eff { display: flex; align-items: center; gap: 8px; }
  .fp-eff-lbl { flex: 1; font-weight: 600; font-size: 0.82rem; color: var(--text); }
  .fp-eff .fp-seg { flex-shrink: 0; }
  .fp-filters .fp-parent { font-weight: 600; color: var(--text); }
  /* Section headers that group the panel into View / Zones / Overlays / Terrain. */
  .fp-sec { font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.7px; color: var(--text-dim); margin: 6px 0 1px; padding-top: 7px; border-top: 1px solid var(--line); }
  .fp-sec:first-child { margin-top: 0; padding-top: 0; border-top: none; }
  .fp-cap { font-size: 0.68rem; color: var(--text-dim); line-height: 1.35; margin: -2px 0 2px; }
  /* Full-width grab bar across the top of a card (the whole card also drags from empty areas). */
  .fp-drag { align-self: stretch; height: 16px; margin: -4px -4px 2px; display: flex; align-items: center; justify-content: center; color: var(--text-dim); font-size: 0.72rem; letter-spacing: 3px; cursor: grab; border-radius: 6px; background: var(--surface-2); touch-action: none; user-select: none; }
  .fp-drag:active { cursor: grabbing; }
  .fp-segrow { display: flex; gap: 6px; }
  .fp-segrow .fp-seg { flex: 1; }
  .fp-segrow .fp-seg button { flex: 1; }
  .fp-group { margin-left: 20px; display: flex; flex-direction: column; gap: 7px; border-left: 1px solid var(--line); padding-left: 10px; }
  .fp-foot { display: flex; align-items: center; gap: 6px; margin-top: 4px; padding-top: 8px; border-top: 1px solid var(--line); }
  .fp-fresh { flex: 1; font-size: 0.74rem; color: var(--text-dim); }
  .fp-fresh.err { color: var(--bad); }
  .fp-fresh.warn { color: var(--warn); }
  .fp-dis { opacity: 0.5; }
  .fp-stale { margin: 2px 0 4px 20px; font-size: 0.68rem; line-height: 1.35; color: var(--warn); }
  .fp-foot-btn { width: 26px; height: 26px; border-radius: 6px; border: 1px solid var(--line-strong); background: var(--surface); color: var(--text-soft); font-size: 0.9rem; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
  .fp-lang { font-size: 0.68rem; font-weight: 700; letter-spacing: 0.4px; }
  .fp-foot-btn:hover:not(:disabled) { color: var(--text); background: var(--surface-2); }
  .fp-foot-btn:disabled { opacity: 0.5; cursor: default; }
  .fp-foot-btn.spin { animation: fp-spin 0.9s linear infinite; }
  @keyframes fp-spin { to { transform: rotate(360deg); } }
  /* Zone sub-filters now open in a side popup instead of inline, so each filter row stays one line. */
  .fp-frow { display: flex; align-items: center; gap: 6px; }
  .fp-frow label { flex: 1; min-width: 0; }
  .fp-frow-sub { padding-left: 6px; }
  .fp-frow-lbl { flex: 1; font-size: 0.78rem; color: var(--text-dim); }
  .fp-facet-btn { width: 24px; height: 24px; flex-shrink: 0; border: 1px solid var(--line-strong); border-radius: 5px; background: var(--surface-2); color: var(--text-soft); font-size: 0.82rem; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
  .fp-facet-btn:hover { color: var(--text); }
  .fp-facet-btn.on { background: var(--accent-strong); color: #fff; border-color: var(--accent-strong); }
  /* Sub-popup = the same window frame as the main panel (opaque body 0.975 from the shared rule above,
     surface-2 header), so the two read as one attached unit. The header is the drag handle. */
  .fp-facet-pop { position: absolute; left: calc(100% + 8px); top: 44px; width: 248px; max-height: calc(100% - 56px); display: flex; flex-direction: column; border: 1px solid var(--line); border-radius: 9px; box-shadow: 0 4px 16px rgba(0,0,0,0.35); z-index: 2; overflow: hidden; }
  .fp-facet-pop.flip { left: auto; right: calc(100% + 8px); }
  .fp-facet-pop-head { display: flex; align-items: center; gap: 7px; padding: 7px 10px; font-size: 0.78rem; font-weight: 600; color: var(--text); background: var(--surface-2); border-bottom: 1px solid var(--line); cursor: grab; user-select: none; }
  :global(.fp-filters.fp-dragging) .fp-facet-pop-head { cursor: grabbing; }
  /* Truncate a long title instead of pushing the All/None + ✕ controls out of the fixed-width popup. */
  .fp-facet-pop-title { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fp-facet-pop-x { width: 24px; height: 24px; border: none; background: none; color: var(--text-soft); cursor: pointer; border-radius: 5px; flex-shrink: 0; font-size: 0.92rem; }
  .fp-facet-pop-x:hover { background: color-mix(in srgb, var(--bad) 26%, transparent); color: #fff; }
  .fp-facet-pop-body { display: flex; flex-direction: column; gap: 6px; padding: 8px 10px; overflow-y: auto; }
  .fp-facet-pop-body label.sub { display: flex; align-items: center; gap: 7px; min-height: 26px; font-size: 0.82rem; color: var(--text-soft); cursor: pointer; }
  .fp-facet-pop-body label.fp-sub-2l { align-items: flex-start; }
  .fp-sub-txt { display: flex; flex-direction: column; gap: 1px; }
  .fp-sub-desc { font-size: 0.68rem; color: var(--text-dim); line-height: 1.35; }
  .fp-fam + .fp-fam { margin-top: 6px; }
  .fp-fam-head { display: flex; align-items: center; gap: 5px; font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: var(--text-dim); padding: 4px 0 3px; border-top: 1px solid var(--line); }
  .fp-fam:first-child .fp-fam-head { border-top: none; padding-top: 0; }
  .fp-fam-all { margin-left: 0; border: 1px solid var(--line); border-radius: 4px; background: var(--surface-2); color: var(--text-dim); font-size: 0.64rem; padding: 3px 7px; min-height: 24px; cursor: pointer; text-transform: none; letter-spacing: 0; }
  .fp-fam-all:first-of-type { margin-left: auto; }
  .fp-fam-all:hover { color: var(--text); }
  .fp-facet-actions { display: inline-flex; gap: 4px; margin-left: auto; }
  .fp-facet-actions button { border: 1px solid var(--line); border-radius: 4px; background: var(--surface-2); color: var(--text-dim); font-size: 0.68rem; padding: 2px 6px; cursor: pointer; }
  .fp-facet-actions button:hover { color: var(--text); }
  /* Flight filters. A bordered card so the axes read as one unit that belongs to "My flights" directly
     above it, rather than as loose rows drifting into the Weather controls below. */
  .fp-ffilter { margin: 4px 0 2px 20px; padding: 6px 8px 7px; display: flex; flex-direction: column; gap: 6px; border: 1px solid var(--line); border-radius: 7px; background: color-mix(in srgb, var(--surface-2) 55%, transparent); }
  /* An active filter hides flights, so it must never be quiet — accent border + the "active" pill make
     a filter restored from a previous session obvious the moment the panel opens. */
  .fp-ffilter.on { border-color: color-mix(in srgb, var(--accent-strong) 62%, var(--line)); }
  .fp-ffilter-head { display: flex; align-items: center; gap: 6px; }
  .fp-ffilter-lbl { flex: 1; font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-dim); }
  .fp-ffilter-on { font-size: 0.64rem; font-weight: 700; color: var(--accent-strong); letter-spacing: 0.3px; }
  .fp-ffilter-reset { border: 1px solid var(--line); border-radius: 4px; background: var(--surface-2); color: var(--text-dim); font-size: 0.64rem; padding: 2px 7px; min-height: 24px; cursor: pointer; }
  .fp-ffilter-reset:hover { color: var(--text); }
  .fp-ffilter-count { font-size: 0.68rem; color: var(--text-dim); font-variant-numeric: tabular-nums; }
  .fp-ffilter-count .warn { color: var(--warn); }
  /* Per-row summary of what the popup behind it currently selects. Muted while wide open, accented once
     narrowed, so a glance down the column says which axes are doing something. */
  .fp-facet-sum { flex-shrink: 0; max-width: 118px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 0.72rem; color: var(--text-dim); }
  .fp-facet-sum.narrowed { color: var(--accent-strong); font-weight: 600; }
  /* Numeric range row: label, then two equal boxes with an en-dash between them. */
  .fp-nrow { display: flex; align-items: center; gap: 5px; padding-left: 6px; }
  .fp-num { width: 54px; flex-shrink: 0; border: 1px solid var(--line-strong); border-radius: 5px; background: var(--surface); color: var(--text); font-size: 0.74rem; padding: 4px 5px; min-height: 26px; font-variant-numeric: tabular-nums; }
  .fp-num::placeholder { color: var(--text-dim); opacity: 0.7; }
  .fp-ndash { font-size: 0.72rem; color: var(--text-dim); }
  .fp-hr-lbl { display: block; font-size: 0.72rem; color: var(--text-soft); margin-top: 2px; }
  /* Scope heading for the flight-only numeric rows, so "Duration" can't read as applying to photos. */
  .fp-nsec { font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-dim); padding: 3px 0 0 6px; }
  .fp-wx-metric { display: flex; border: 1px solid var(--line-strong); border-radius: 7px; overflow: hidden; }
  .fp-wx-metric button { flex: 1; background: none; border: none; border-left: 1px solid var(--line-strong); color: var(--text-soft); padding: 6px 10px; min-height: 28px; font-size: 0.76rem; cursor: pointer; }
  .fp-wx-metric button:first-child { border-left: none; }
  .fp-wx-metric button.on { background: var(--accent-strong); color: #fff; }
  .fp-group label.sub { font-size: 0.82rem; }
  .fp-wx-leg { margin-top: 3px; }
  .fp-wx-leg-bar { display: block; height: 8px; border-radius: 4px; background: linear-gradient(to right, #4338ca, #2563eb, #22d3ee, #22c55e, #eab308, #f97316, #ef4444); }
  .fp-wx-leg-ends { display: flex; justify-content: space-between; font-size: 0.62rem; color: var(--text-dim); margin-top: 1px; }
  .fp-wx-legrow { display: flex; align-items: center; gap: 5px; font-size: 0.66rem; color: var(--text-dim); margin-top: 3px; }
  .fp-wx-legarrow { color: var(--text-soft); font-weight: 800; }
  .fp-wx-time { font-size: 0.74rem; color: var(--text-dim); display: flex; justify-content: space-between; gap: 6px; margin-top: 2px; }
  .fp-wx-time-val { color: var(--text-soft); font-weight: 600; }
  .fp-wx-range { width: 100%; accent-color: var(--accent-strong); cursor: pointer; }
  .fp-wx-paused { font-size: 0.66rem; color: #f59e0b; margin-top: 4px; line-height: 1.3; }

  :global(.fp-wx) { display: flex; flex-direction: column; align-items: center; line-height: 1; pointer-events: auto; cursor: default; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.8)); }
  /* Pill backgrounds so the readings stay legible over ANY basemap (bright desert, dark forest, sat). */
  :global(.fp-wx-temp) { font-size: 0.74rem; font-weight: 800; color: #fff; padding: 1px 6px; border-radius: 7px; margin-bottom: 1px; text-shadow: 0 1px 2px rgba(0,0,0,0.6); box-shadow: 0 1px 3px rgba(0,0,0,0.5); }
  :global(.fp-wx-spd) { display: inline-flex; align-items: baseline; gap: 2px; font-size: 0.68rem; font-weight: 800; margin-top: -3px; padding: 1px 5px; border-radius: 6px; background: rgba(9,12,18,0.78); }
  :global(.fp-wx-gust) { font-size: 0.56rem; font-weight: 700; opacity: 0.85; }
  :global(.fp-wx-drops) { font-size: 0.64rem; margin-top: 1px; letter-spacing: -2px; filter: drop-shadow(0 1px 1px rgba(0,0,0,0.7)); }
  :global(.fp-wx-arrow) { filter: drop-shadow(0 0 2px rgba(0,0,0,0.9)); }
  /* Rich weather tooltip — symbols + colours, denser than the map. */
  :global(.fp-wxtip) { display: flex; flex-direction: column; gap: 5px; font-size: 0.8rem; }
  :global(.fp-wxtip .wxr) { display: flex; align-items: center; gap: 7px; }
  :global(.fp-wxtip .wxk) { font-weight: 800; color: #fff; padding: 1px 7px; border-radius: 6px; text-shadow: 0 1px 2px rgba(0,0,0,0.5); flex-shrink: 0; }
  :global(.fp-wxtip .wxlabel) { color: var(--text-soft); }
  :global(.fp-wxtip .wxarrow) { display: inline-block; font-size: 1.1rem; font-weight: 900; line-height: 1; flex-shrink: 0; }
  :global(.fp-wxtip .wxwind) { display: flex; flex-direction: column; line-height: 1.35; }
  :global(.fp-wxtip .wxsev) { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
  :global(.fp-wxtip .wxfrom) { font-size: 0.7rem; color: var(--text-dim); }
  :global(.fp-wxtip .wxdrops) { font-size: 0.85rem; letter-spacing: -2px; flex-shrink: 0; }

  .fp-flight { position: absolute; left: 10px; bottom: 12px; z-index: 7; width: 300px; max-height: calc(100% - 90px); overflow: hidden; background: var(--surface); border: 1px solid var(--line); border-radius: 11px; padding: 12px; box-shadow: 0 4px 18px rgba(0,0,0,0.35); display: flex; flex-direction: column; gap: 9px; cursor: grab; touch-action: pan-y; }
  /* Header stays pinned; only the body scrolls (so the drag handle + title never leave with the content). */
  .fp-flight > .fp-drag, .fp-flight-head { flex-shrink: 0; }
  .fp-flight-head { padding-right: 26px; }
  .fp-flight-body { flex: 1 1 auto; min-height: 0; overflow-y: auto; display: flex; flex-direction: column; gap: 9px; }
  /* Bottom-right resize grip (rightward = wider, downward = taller body). */
  .fp-flight-grip { position: absolute; right: 1px; bottom: 1px; width: 16px; height: 16px; cursor: nwse-resize; touch-action: none; z-index: 2; }
  .fp-flight-grip::after { content: ''; position: absolute; right: 3px; bottom: 3px; width: 7px; height: 7px; border-right: 2px solid var(--text-dim); border-bottom: 2px solid var(--text-dim); }
  .fp-flight-grip:hover::after { border-color: var(--accent); }
  .fp-flight-name { font-weight: 700; font-size: 0.9rem; color: var(--text); }
  .fp-flight-sub { font-size: 0.78rem; color: var(--text-dim); margin-top: 1px; }
  .fp-flight-stats { display: flex; flex-wrap: wrap; gap: 4px 12px; font-size: 0.78rem; color: var(--text-soft); }
  .fp-flight-stats b { color: var(--text); font-variant-numeric: tabular-nums; }
  .fp-flight-meta { display: flex; flex-wrap: wrap; gap: 4px 6px; }
  .fp-flight-meta span { font-size: 0.68rem; color: var(--text-dim); background: var(--surface-2); border-radius: 4px; padding: 1px 6px; }
  .fp-flight-video { width: 100%; border-radius: 8px; background: #000; aspect-ratio: 16 / 9; display: block; }
  .fp-flight-ctrls { display: flex; align-items: center; gap: 8px; margin-top: 5px; }
  .fp-flight-play { width: 30px; height: 26px; border: none; background: var(--surface-2); color: var(--text); border-radius: 5px; cursor: pointer; font-size: 0.78rem; flex-shrink: 0; }
  .fp-flight-play:hover { background: var(--line-strong); }
  .fp-flight-seek { flex: 1; min-width: 0; accent-color: var(--accent); }
  .fp-flight-time { font-size: 0.68rem; color: var(--text-dim); font-variant-numeric: tabular-nums; flex-shrink: 0; }
  /* Storyboard: horizontally scrollable; pins row + frame row share one inner track so they scroll together
     and a pin at left:frac% aligns with the same time position in the (evenly-spaced) frame strip. */
  .fp-story { overflow-x: auto; overflow-y: hidden; }
  .fp-story-inner { display: inline-flex; flex-direction: column; gap: 3px; position: relative; }
  .fp-story-pins { position: relative; height: 17px; }
  .fp-story-pin { position: absolute; top: 0; transform: translateX(-50%); font-size: 0.78rem; line-height: 1; padding: 1px 2px; border: none; background: none; cursor: pointer; filter: drop-shadow(0 1px 1px rgba(0,0,0,0.6)); }
  .fp-story-pin::after { content: ''; position: absolute; left: 50%; top: 100%; width: 1px; height: 4px; background: var(--text-dim); transform: translateX(-50%); }
  .fp-story-pin:hover { transform: translateX(-50%) scale(1.25); }
  .fp-story-frames { display: flex; gap: 3px; }
  .fp-frame { flex: 0 0 auto; padding: 0; border: 1px solid var(--line); border-radius: 5px; overflow: hidden; cursor: pointer; background: none; line-height: 0; }
  .fp-frame:hover { border-color: var(--accent); }
  .fp-frame img { width: 92px; height: auto; display: block; }
  .fp-flight-novideo { font-size: 0.72rem; color: var(--text-dim); margin: 0; }
  .fp-flight-fnote { font-size: 0.72rem; color: var(--warn); margin: 0; display: flex; align-items: baseline; gap: 5px; line-height: 1.4; }
  .fp-flight-fnote span { flex-shrink: 0; }
  .fp-flight-fnote.loading { color: var(--text-dim); align-items: center; }
  .fp-spin { width: 11px; height: 11px; border: 2px solid var(--line-strong); border-top-color: var(--accent); border-radius: 50%; display: inline-block; animation: fp-spin 0.7s linear infinite; }
  @keyframes fp-spin { to { transform: rotate(360deg); } }
  /* All-sources panel: grouped key/value rows, collapsed under a summary line. */
  .fp-sources { font-size: 0.72rem; }
  .fp-sources > summary { cursor: pointer; color: var(--text-soft); font-weight: 600; list-style-position: inside; padding: 2px 0; user-select: none; }
  .fp-sources > summary:hover { color: var(--text); }
  .fp-src-grid { display: flex; flex-direction: column; gap: 7px; margin-top: 5px; }
  .fp-src-h { color: var(--text-soft); font-weight: 600; margin-bottom: 2px; }
  .fp-src-r { display: flex; justify-content: space-between; gap: 10px; line-height: 1.5; }
  .fp-src-r > span { color: var(--text-dim); flex: 0 0 auto; }
  .fp-src-r > b { color: var(--text); font-weight: 500; text-align: right; word-break: break-word; }
  .fp-flight-clips { display: flex; flex-direction: column; gap: 3px; }
  .fp-clip { display: flex; align-items: center; gap: 7px; background: var(--surface-2); border: 1px solid transparent; border-radius: 6px; padding: 6px 8px; font-size: 0.78rem; color: var(--text-soft); cursor: pointer; text-align: left; }
  .fp-clip.on { border-color: var(--accent); color: var(--text); }
  .fp-clip:hover { color: var(--text); }
  .fp-clip-nm { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fp-clip-dur { color: var(--text-dim); font-variant-numeric: tabular-nums; }

  .fp-card { position: absolute; right: 10px; bottom: 12px; z-index: 6; width: 264px; max-height: calc(100% - 120px); overflow-y: auto; background: var(--surface); border: 1px solid var(--line); border-radius: 11px; padding: 12px; box-shadow: 0 4px 18px rgba(0,0,0,0.3); display: flex; flex-direction: column; gap: 10px; cursor: grab; touch-action: pan-y; }
  .fp-card-x { position: absolute; top: 8px; right: 8px; width: 26px; height: 26px; border: none; background: none; color: var(--text-dim); cursor: pointer; border-radius: 5px; }
  .fp-card-x:hover { background: var(--surface-2); color: var(--text); }
  .verdict { display: flex; align-items: center; gap: 9px; font-size: 0.95rem; font-weight: 700; padding: 9px 34px 9px 11px; border-radius: 9px; }
  .verdict .vicon { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; font-size: 0.8rem; color: #fff; flex-shrink: 0; }
  .verdict.good { background: color-mix(in srgb, #22c55e 22%, transparent); color: var(--good); }
  .verdict.good .vicon { background: #22c55e; }
  .verdict.marginal { background: color-mix(in srgb, #f59e0b 24%, transparent); color: var(--warn); }
  .verdict.marginal .vicon { background: #f59e0b; }
  .verdict.nofly, .verdict.err { background: color-mix(in srgb, #ef4444 22%, transparent); color: var(--bad); }
  .verdict.nofly .vicon, .verdict.err .vicon { background: #ef4444; }
  .verdict.pending { background: var(--surface-2); color: var(--text-dim); }
  .verdict.pending .vicon { background: var(--line-strong); }
  .coord { font-size: 0.78rem; color: var(--text-dim); font-variant-numeric: tabular-nums; }
  .block h3 { font-size: 0.8rem; font-weight: 700; margin: 0 0 4px; }
  .block ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }
  .block li { font-size: 0.75rem; color: var(--text-soft); }
  .zlist li { display: flex; align-items: baseline; gap: 6px; }
  /* Rounded square, matching the Layers-panel legend swatch (.fp-filters i) — same zone = same shape. */
  .zdot { width: 11px; height: 11px; border-radius: 3px; flex-shrink: 0; display: inline-block; align-self: center; }
  .zn { flex: 1; min-width: 0; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .zc { font-size: 0.66rem; color: var(--text-dim); flex-shrink: 0; }
  .ok-note { display: flex; align-items: center; gap: 7px; font-size: 0.75rem; color: var(--text-soft); margin: 0; }
  .wx { margin: 0; display: grid; grid-template-columns: 1fr auto; gap: 3px 10px; }
  .wx div { display: contents; }
  .wx dt { font-size: 0.74rem; color: var(--text-dim); }
  .wx dd { margin: 0; font-size: 0.74rem; color: var(--text-soft); font-variant-numeric: tabular-nums; text-align: right; }
  .wx div.flag dt, .wx div.flag dd { color: var(--warn); font-weight: 700; }
  .errmsg { font-size: 0.76rem; color: var(--text-soft); }
  .disclaimer { font-size: 0.66rem; color: var(--text-dim); line-height: 1.5; margin: 0; }
  .disclaimer a { color: var(--accent); }

  /* Themed popups (default MapLibre is bare white). Tip triangle hidden — reads as a floating card. */
  /* Map popups get the SOLID surface, not the translucent `--surface` the panels use. A panel at 82%
     opacity sits over the app's own dark chrome and reads fine; a popup sits over whatever the map
     is showing, and over bright satellite imagery an 18%-transparent dark card with light text is
     hard to read. Opaque here, plus a stronger border so the card still separates from a busy
     photographic background. */
  :global(.fp-tip .maplibregl-popup-content),
  :global(.fp-pop .maplibregl-popup-content) {
    background: var(--surface-solid);
    color: var(--text);
    border: 1px solid var(--line-strong);
    border-radius: 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5), 0 8px 28px rgba(0, 0, 0, 0.45);
  }
  :global(.fp-tip .maplibregl-popup-content) { padding: 8px 11px; font-size: 0.78rem; }
  /* The tap-to-identify popup. Same card, but the close button is load-bearing here: a finger opens
     it and there is no pointer to move away. 44px target, and the text goes up a step because
     0.78rem is ~12.5px — fine under a cursor, a squint on a phone. */
  :global(.fp-tip-tap .maplibregl-popup-content) { padding: 10px 46px 10px 12px; font-size: 0.86rem; }
  :global(.fp-tip-tap .maplibregl-popup-close-button) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 0;
    font-size: 20px;
    line-height: 1;
    color: var(--text-soft);
    background: transparent;
    border-radius: 8px;
  }
  :global(.fp-tip-tap .maplibregl-popup-close-button:hover) { background: var(--surface-2); color: var(--text); }
  /* A message, not an identity readout: it wraps, and it is wide enough to read as a sentence. */
  :global(.fp-tip-notice) { max-width: 240px; line-height: 1.45; color: var(--text); }
  :global(.fp-tip-list) { display: flex; flex-direction: column; gap: 6px; }
  :global(.fp-tip-row) { display: flex; align-items: flex-start; gap: 7px; }
  :global(.fp-tip-dot) { width: 9px; height: 9px; border-radius: 3px; flex-shrink: 0; margin-top: 3px; }
  :global(.fp-tip-sym) { font-size: 0.95rem; line-height: 1; flex-shrink: 0; }
  :global(.fp-tip-frame) { display: block; width: 232px; max-width: 100%; aspect-ratio: 16 / 9; object-fit: cover; border-radius: 6px; }
  :global(.fp-tip-txt) { display: flex; flex-direction: column; line-height: 1.3; }
  :global(.fp-tip-name) { color: var(--text); font-weight: 600; }
  /* Enriched flight hover tooltip: name + clip/date + a live frame preview + telemetry chips. */
  :global(.fp-tip-flight) { display: flex; flex-direction: column; gap: 6px; width: 232px; }
  :global(.fp-tip-head) { display: flex; align-items: center; gap: 7px; }
  :global(.fp-tip-sub) { color: var(--text-dim); font-size: 0.72rem; margin-top: -3px; }
  :global(.fp-tip-frameph) { display: flex; align-items: center; justify-content: center; width: 232px; aspect-ratio: 16 / 9; border-radius: 6px; background: var(--surface-2); color: var(--text-dim); }
  :global(.fp-tip-stats) { display: flex; flex-wrap: wrap; gap: 4px 6px; }
  :global(.fp-tip-stat) { display: inline-flex; align-items: center; gap: 3px; font-size: 0.72rem; color: var(--text-soft); background: var(--surface-2); border-radius: 999px; padding: 1px 8px; font-variant-numeric: tabular-nums; }
  :global(.fp-tip-hint) { opacity: 0.65; font-style: italic; }
  /* Photo thumbnail: contain (not 16/9 cover) so a portrait shot shows whole + upright, not cropped sideways. */
  :global(.fp-tip-photoframe) { display: block; width: 232px; max-width: 100%; height: 150px; object-fit: contain; border-radius: 6px; background: #0b1220; }

  /* Photo lightbox (click a photo marker → full-size). */
  .fp-lightbox { position: fixed; inset: 0; z-index: 60; display: flex; align-items: center; justify-content: center; padding: 3vmin; }
  .fp-lightbox-backdrop { position: absolute; inset: 0; border: 0; padding: 0; background: rgba(6, 10, 18, 0.82); cursor: zoom-out; }
  .fp-lightbox-fig { position: relative; margin: 0; display: flex; flex-direction: column; gap: 8px; align-items: center; max-width: 92vw; max-height: 92vh; }
  .fp-lightbox-img { max-width: 92vw; max-height: 82vh; object-fit: contain; border-radius: 8px; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5); background: #0b1220; }
  .fp-lightbox-raw { padding: 48px 40px; border-radius: 8px; background: var(--surface-2); color: var(--text-dim); }
  .fp-lightbox-cap { display: flex; flex-direction: column; gap: 2px; color: var(--text); text-align: center; }
  .fp-lightbox-name { font-weight: 600; }
  .fp-lightbox-meta { color: var(--text-dim); font-size: 0.8rem; font-variant-numeric: tabular-nums; }
  .fp-lightbox-x { position: absolute; top: 16px; right: 16px; width: 36px; height: 36px; border-radius: 999px; border: 0; background: rgba(11, 18, 32, 0.85); color: #fff; font-size: 1.1rem; cursor: pointer; display: flex; align-items: center; justify-content: center; }
  .fp-lightbox-x:hover { background: rgba(30, 41, 59, 0.95); }

  /* Background-scan console + progress bar (bottom-left of the map pane). */
  .fp-bl { position: absolute; left: 12px; bottom: 12px; display: flex; flex-direction: column; align-items: flex-start; gap: 8px; max-width: calc(100% - 24px); pointer-events: none; }
  /* Below a 640px MAP width MapLibre stops right-anchoring the attribution and lets it run the full
     width of the map: measured at 360/390/430px it is 44px tall with its top 54px above the map's
     bottom edge, so a column sitting at bottom:12px lands on top of it. Above 640px the attribution
     is 411px wide on the right and the two never share space. 62px clears the measured 54 with room
     for the shadow; re-measure if the attribution string gains a source. */
  @media (max-width: 640px) { .fp-bl { bottom: 62px; } }
  .fp-scan { position: relative; z-index: 40; pointer-events: auto; width: 300px; max-width: 100%; background: rgba(11, 18, 32, 0.92); color: var(--text); border: 1px solid var(--line); border-radius: 10px; padding: 10px 11px; box-shadow: 0 6px 24px rgba(0, 0, 0, 0.4); font-size: 0.78rem; }
  .fp-scan-head { display: flex; align-items: center; gap: 7px; }
  .fp-scan-title { font-weight: 600; }
  .fp-scan-count { margin-left: auto; font-variant-numeric: tabular-nums; color: var(--text-dim); }
  .fp-scan-sub { display: flex; align-items: center; gap: 7px; margin-top: 7px; font-size: 0.74rem; color: var(--text-soft); }
  .fp-scan-x { border: 0; background: transparent; color: var(--text-dim); cursor: pointer; font-size: 0.9rem; line-height: 1; padding: 0 2px; }
  .fp-scan-x:hover { color: var(--text); }
  .fp-scan-bar { height: 5px; border-radius: 999px; background: var(--surface-2); overflow: hidden; margin: 8px 0; }
  .fp-scan-fill { height: 100%; background: linear-gradient(90deg, #22d3ee, #34d399); border-radius: 999px; transition: width 0.3s ease; }
  .fp-scan-log { list-style: none; margin: 0; padding: 0; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.68rem; color: var(--text-dim); line-height: 1.5; max-height: 92px; overflow: hidden; }
  .fp-scan-log li { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  /* Per-op status glyph: running (dim), done (green ✓), failed (red ✕) — colour is paired with the glyph
     shape so it reads without relying on colour alone. */
  .fp-act-ico { display: inline-block; width: 1em; text-align: center; }
  .fp-act-ico.run { color: var(--text-dim); }
  .fp-act-ico.ok { color: #34d399; }
  .fp-act-ico.err { color: #f87171; }
  .fp-act-scan { opacity: 0.7; padding-left: 1.2em; }
  .fp-scan-spin { width: 11px; height: 11px; border-radius: 50%; border: 2px solid var(--line); border-top-color: #22d3ee; flex-shrink: 0; }
  .fp-scan-spin.spin { animation: fp-scan-rot 0.8s linear infinite; }
  @keyframes fp-scan-rot { to { transform: rotate(360deg); } }
  :global(.fp-tip-row + .fp-tip-row) { border-top: 1px solid var(--line); padding-top: 6px; }
  :global(.fp-pop .maplibregl-popup-content) { padding: 10px 12px 11px; font-size: 0.75rem; }
  :global(.fp-tip .maplibregl-popup-tip),
  :global(.fp-pop .maplibregl-popup-tip) { display: none; }
  :global(.fp-pop .maplibregl-popup-close-button) { color: var(--text-dim); font-size: 1.1rem; padding: 0 7px; }
  :global(.fp-pop .maplibregl-popup-close-button:hover) { background: none; color: var(--text); }
  :global(.fp-tip-kind) { color: var(--text-dim); }
  :global(.fp-tip-more) { margin-top: 3px; color: var(--accent); font-size: 0.66rem; }
  /* Structured zone/NOTAM tooltip: a colour-accented card per overlapping zone — status badge + name in
     the head, then a 2-column key→value table (label left, value right). --bx is the class colour. */
  :global(.fp-tip-card) { display: flex; flex-direction: column; gap: 4px; padding-left: 8px; border-left: 3px solid var(--bx, var(--line-strong)); }
  :global(.fp-tip-card + .fp-tip-card) { border-top: 1px solid var(--line); padding-top: 6px; }
  :global(.fp-tip-chead) { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
  :global(.fp-tip-badge) { flex-shrink: 0; font-size: 0.66rem; font-weight: 700; padding: 1px 6px; border-radius: 4px; color: var(--bx); background: color-mix(in srgb, var(--bx) 16%, transparent); border: 1px solid color-mix(in srgb, var(--bx) 34%, transparent); }
  :global(.fp-tip-meta) { display: grid; grid-template-columns: auto 1fr; gap: 2px 10px; margin: 0; }
  :global(.fp-tip-mrow) { display: contents; }
  :global(.fp-tip-mk) { color: var(--text-dim); font-size: 0.7rem; white-space: nowrap; }
  :global(.fp-tip-mv) { color: var(--text-soft); font-size: 0.72rem; }
  :global(.fp-pop-body) { display: flex; flex-direction: column; gap: 7px; padding-right: 8px; color: var(--text-soft); max-height: 320px; overflow-y: auto; }
  :global(.fp-pop-row) { border-left: 2px solid var(--line-strong); padding-left: 8px; }
  :global(.fp-pop-name) { font-weight: 700; color: var(--text); font-size: 0.76rem; }
  :global(.fp-pop-code) { font-size: 0.7rem; color: var(--text-dim); }
  :global(.fp-pop-desc) { font-size: 0.66rem; color: var(--text-dim); margin-top: 2px; line-height: 1.4; }
  :global(.fp-plan-here) { margin-top: 4px; background: var(--accent-strong, #2563eb); color: #fff; border: none; border-radius: 6px; padding: 5px 10px; font-size: 0.74rem; cursor: pointer; }

  .fp-reduced { position: absolute; bottom: 12px; left: 50%; transform: translateX(-50%); z-index: 11; background: #92400e; color: #fff; font-size: 0.72rem; text-align: center; padding: 5px 14px; border-radius: 999px; box-shadow: 0 2px 10px rgba(0,0,0,0.45); max-width: min(560px, calc(100vw - 40px)); }
  .fp-databanner { position: absolute; top: 0; left: 0; right: 0; z-index: 14; background: #b91c1c; color: #fff; font-size: 0.82rem; font-weight: 700; text-align: center; padding: 8px 14px; box-shadow: 0 2px 10px rgba(0,0,0,0.4); }
  /* z-index 5 keeps the key under the flight card (7) and the activity console (40): a panel the
     user opened on purpose outranks an ambient key. */
  .fp-legend {
    position: relative; z-index: 5;
    display: flex; flex-wrap: wrap; align-items: center; gap: 4px 12px;
    max-width: 100%;
    padding: 5px 10px; border-radius: 9px;
    background: rgba(11, 18, 32, 0.86); border: 1px solid var(--line);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
    font-size: 0.74rem; color: var(--text-soft);
    pointer-events: none;
  }
  .fp-legend span { display: flex; align-items: center; gap: 6px; white-space: nowrap; }
  .fp-legend i { width: 11px; height: 11px; border-radius: 3px; flex-shrink: 0; }
  .fp-keys { position: absolute; left: 50%; top: 64px; transform: translateX(-50%); z-index: 13; width: 300px; background: rgba(15, 22, 33, 0.98); border: 1px solid var(--line); border-radius: 11px; padding: 12px 14px; box-shadow: 0 8px 26px rgba(0,0,0,0.45); }
  .fp-keys-head { display: flex; align-items: center; font-size: 0.82rem; font-weight: 700; color: var(--text); margin-bottom: 8px; }
  .fp-keys-head .fp-facet-pop-x { margin-left: auto; }
  .fp-keys ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
  .fp-keys li { display: flex; align-items: center; gap: 9px; font-size: 0.8rem; color: var(--text-soft); }
  .fp-keys kbd { flex-shrink: 0; min-width: 20px; text-align: center; font-family: inherit; font-size: 0.72rem; background: var(--surface-2); border: 1px solid var(--line-strong); border-bottom-width: 2px; border-radius: 5px; padding: 1px 6px; color: var(--text); }
  .fp-keys-legend { margin-top: 10px; padding-top: 9px; border-top: 1px solid var(--line); }
  .fp-keys-legtitle { font-size: 0.72rem; font-weight: 700; color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 6px; }
  /* Point-type markers (flight track, place pin) are round; zone areas are the default rounded square. */
  .fp-keys .zdot-o { border-radius: 50%; }

  /* ---- touch and narrow screens -----------------------------------------------------------------
     This component had no media query at all: one layout, designed once for a cursor in a wide
     window. Two things follow from that which only show up on a phone.

     1. A pointer that cannot hover is also about 10 mm wide. Measured on a 390×844 viewport, 7 of
        the 8 controls over the map were under 44 px — MapLibre's own zoom/locate/compass stack is
        29 px and the dismiss button on the scan banner was 15×14, under even the 24 px WCAG 2.2
        floor. Scoped to `pointer: coarse` so the desktop app keeps its compact chrome.
     2. The search field is centred at a fixed width, and at 390 px it ended at x=335 while the
        icon rail started at x=314 — the primary control on the site, with a button sitting on it.
        Anchoring both edges instead of centring a fixed width keeps the gap at any width, and
        makes the field wider on exactly the screens where it was worst. */
  @media (pointer: coarse) {
    .fp-icon { width: 40px; height: 40px; font-size: 1.05rem; }
    .fp-foot-btn { width: 36px; height: 36px; font-size: 1rem; }
    .fp-facet-btn { width: 32px; height: 32px; font-size: 0.95rem; }
    .fp-scan-x { padding: 8px 11px; font-size: 1rem; }
    .fp-drag { height: 22px; }
    /* MapLibre injects its controls into the map container, so they are outside this component's
       style scope; :global is scoped to .fp-pane so it cannot reach another map on the page. */
    :global(.fp-pane .maplibregl-ctrl-group button) { width: 40px; height: 40px; }
    :global(.fp-pane .maplibregl-ctrl-group .maplibregl-ctrl-icon) { background-size: 21px 21px; }
  }

  @media (max-width: 700px) {
    /* The search box and the icon rail were two separately-anchored boxes competing for the same
       strip: centred at a fixed width, the box ended at x=335 while the rail began at x=314, so a
       button sat on top of the site's primary control. Reserving a fixed right margin only moves
       the problem — the rail holds two icons here and four in the full app, so its width is not a
       number this stylesheet can know.

       Stacking them removes the contention instead of negotiating it, and the field goes
       full-width, which is the better shape for a thumb anyway. The panels move below the search
       so the three rows do not overlap. */
    .fp-search { top: 58px; left: 10px; right: 10px; width: auto; transform: none; }
    .fp-filters, .fp-table { top: 106px; }
    .fp-filters { right: 10px; max-height: calc(100% - 116px); }
  }
</style>
